import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaUserMd } from 'react-icons/fa';
// Use CSS modules for isolated styling
import pdlSearchStyles from '../css-isolated/pdl_health_record_search.module.css';
import modalStyles from '../css-isolated/pdl_health_record_modal.module.css';
import bodyMarkingService from '../services/bodyMarkingService';
import API_BASE_URL from '../config';
import NotificationBell from './notification';
import ChangePasswordModal from './change_pass';
import authFetch from '../utils/authFetch';
import { lettersOnly } from '../utils/inputValidation';

function PdlHealthRecordSearch() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchResults, setSearchResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [bodyMarkings, setBodyMarkings] = useState({ front: [], back: [] });
  const [isModalLoading, setIsModalLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [logoFailed, setLogoFailed] = useState(false);
  const [userData, setUserData] = useState(null);
  const [remarksFilter, setRemarksFilter] = useState('not_archived');
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc'); // 'asc' or 'desc'
  
  // Helper function to format date from YYYY-MM-DD to MM/DD/YYYY
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString; // Return original if invalid
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };
  
  // Theme State
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    return savedTheme !== 'light';
  });
  const [showThemeModal, setShowThemeModal] = useState(false);
  const [pendingTheme, setPendingTheme] = useState(null);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [searchData, setSearchData] = useState({
    lastName: '',
    firstName: '',
    middleName: '',
    akaNickname: ''
  });

  // Helper function to calculate age from date of birth
  const calculateAge = React.useCallback((dateOfBirth) => {
    if (!dateOfBirth) return 'N/A';
    
    const birthDate = new Date(dateOfBirth);
    const today = new Date();
    
    if (isNaN(birthDate.getTime())) return 'N/A';
    
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    return age >= 0 ? age : 'N/A';
  }, []);

  // Helper function to capitalize names
  const capitalizeName = React.useCallback((name) => {
    if (!name) return 'N/A';
    return name
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }, []);

  // Filtering & Pagination
  const ITEMS_PER_PAGE = 10;
  const filteredResults = React.useMemo(() => {
    switch (remarksFilter) {
      case 'all':
        return searchResults;
      case 'Release':
      case 'Transfer':
      case 'Death':
        return searchResults.filter(r => (r.final_remarks || '').toLowerCase() === remarksFilter.toLowerCase());
      case 'not_archived':
      default:
        return searchResults.filter(r => !r.final_remarks);
    }
  }, [searchResults, remarksFilter]);

  // Sorting logic
  const sortedResults = React.useMemo(() => {
    if (!sortColumn) return filteredResults;

    const sorted = [...filteredResults].sort((a, b) => {
      let aValue, bValue;

      switch (sortColumn) {
        case 'lastName':
          aValue = a.last_name || '';
          bValue = b.last_name || '';
          break;
        case 'firstName':
          aValue = a.first_name || '';
          bValue = b.first_name || '';
          break;
        case 'middleName':
          aValue = a.middle_name || '';
          bValue = b.middle_name || '';
          break;
        case 'aka':
          aValue = a.alias_nickname || '';
          bValue = b.alias_nickname || '';
          break;
        case 'age':
          aValue = calculateAge(a.date_of_birth);
          bValue = calculateAge(b.date_of_birth);
          // Handle 'N/A' values - treat as very high number so they appear at the end
          aValue = aValue === 'N/A' ? 999999 : Number(aValue);
          bValue = bValue === 'N/A' ? 999999 : Number(bValue);
          // Direct numeric comparison for age
          return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
        default:
          return 0;
      }

      // String comparison
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [filteredResults, sortColumn, sortDirection, calculateAge]);

  const totalPages = Math.ceil(sortedResults.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const currentPageData = sortedResults.slice(startIndex, endIndex);

  // Reset to page 1 when search results or filter change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchResults, remarksFilter]);

  // Handle column header click for sorting
  const handleSort = (column) => {
    if (sortColumn === column) {
      // Toggle sort direction if same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new column and default to ascending
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Get tooltip text for column sorting
  const getSortTooltip = (column) => {
    if (sortColumn === column && sortDirection === 'asc') {
      return 'Sort Z to A';
    }
    return 'Sort A to Z';
  };

  // Render sort indicator
  const renderSortIndicator = (column) => {
    if (sortColumn !== column) {
      return (
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ opacity: 0.3, marginLeft: '4px' }}>
          <path d="M7 10L12 5L17 10M7 14L12 19L17 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      );
    }
    return sortDirection === 'asc' ? (
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginLeft: '4px' }}>
        <path d="M7 14L12 9L17 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ) : (
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginLeft: '4px' }}>
        <path d="M7 10L12 15L17 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    );
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSearchData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Load user data from localStorage
  React.useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setUserData(parsedUser);
      } catch (error) {
        console.error('Error parsing user data:', error);
      }
    }
  }, []);

  // Auto-load all records only when navigated from the health record KPI card
  React.useEffect(() => {
    if (!location.state?.autoLoad) return;
    const fetchAllRecords = async () => {
      setIsLoading(true);
      try {
        const response = await authFetch(`${API_BASE_URL}/api/hr-info/search?lastName=&firstName=&middleName=&akaNickname=`);
        if (!response.ok) throw new Error('Network response was not ok');
        const data = await response.json();
        setSearchResults(data);
      } catch (error) {
        console.error('Error fetching all health records:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchAllRecords();
  }, [location.state]);

  // Update time every second
  React.useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleLogout = () => {
    setShowLogoutModal(true);
  };

  const confirmLogout = () => {
    localStorage.clear();
    sessionStorage.clear();
    setShowLogoutModal(false);
    navigate('/login', { replace: true });
  };

  const cancelLogout = () => {
    setShowLogoutModal(false);
  };

  const toggleDarkMode = () => {
    setPendingTheme(!isDarkMode);
    setShowThemeModal(true);
  };

  const confirmThemeChange = async () => {
    // Close modal immediately
    setShowThemeModal(false);
    
    try {
      // Update local state and storage
      setIsDarkMode(pendingTheme);
      localStorage.setItem('theme', pendingTheme ? 'dark' : 'light');

      // Update database in background
      if (userData && userData.id) {
        const response = await authFetch(`${API_BASE_URL}/api/update-theme`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            user_id: userData.id,
            is_darkmode: pendingTheme ? 1 : 0
          })
        });
        
        if (!response.ok) {
          console.error('Failed to update theme in database');
        }
      }
    } catch (error) {
      console.error('Failed to update theme:', error);
    } finally {
      setPendingTheme(null);
    }
  };

  const cancelThemeChange = () => {
    setShowThemeModal(false);
    setPendingTheme(null);
  };

  const formatDateTime = (date) => {
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    return date.toLocaleDateString('en-US', options);
  };

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (isDropdownOpen && !event.target.closest(`.${pdlSearchStyles.profileSection}`)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  const handleSearch = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const queryParams = new URLSearchParams(searchData).toString();
      const response = await authFetch(`${API_BASE_URL}/api/hr-info/search?${queryParams}`);
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      const data = await response.json();
      setSearchResults(data);
    } catch (error) {
      console.error('Error fetching search results:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleView = async (record) => {
    setCurrentPage(4);
    setShowViewModal(true);
    setIsModalLoading(true);
    try {
      const response = await authFetch(`${API_BASE_URL}/api/hr-info/${record.id}`);
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      const data = await response.json();
      setSelectedRecord(data);
      
      // Fetch body markings
      try {
        const markings = await bodyMarkingService.getByHrInfoId(record.id);
        setBodyMarkings(markings);
      } catch (markingError) {
        console.error('Error fetching body markings:', markingError);
        setBodyMarkings({ front: [], back: [] });
      }
    } catch (error) {
      console.error('Error fetching record details:', error);
    } finally {
      setIsModalLoading(false);
    }
  };

  const closeModal = () => {
    setShowViewModal(false);
    setSelectedRecord(null);
    setBodyMarkings({ front: [], back: [] });
    setCurrentPage(1);
  };

  const handleUpdate = async (record) => {
    setIsModalLoading(true);
    try {
      // Fetch complete record data including all related information
      const response = await authFetch(`${API_BASE_URL}/api/hr-info/${record.id}`);
      if (!response.ok) {
        throw new Error('Failed to fetch record data');
      }
      const fullRecord = await response.json();
      // Navigate to HR wizard with the record data and edit mode
      navigate('/pdl-health-wizard', { 
        state: { 
          mode: 'edit', 
          recordId: record.id,
          initialData: fullRecord 
        } 
      });
    } catch (error) {
      console.error('Error fetching HR record for update:', error);
      alert('Failed to load record data for editing. Please try again.');
    } finally {
      setIsModalLoading(false);
    }
  };

  const handleCopyRecord = async () => {
    if (!selectedRecord) return;

    setIsModalLoading(true);
    try {
      // Fetch complete record data including all related information
      const response = await authFetch(`${API_BASE_URL}/api/hr-info/${selectedRecord.id}`);
      if (!response.ok) {
        throw new Error('Failed to fetch record data');
      }
      const fullRecord = await response.json();
      // Navigate to HR wizard with the record data in copy mode
      navigate('/pdl-health-wizard', { 
        state: { 
          mode: 'copy', 
          recordId: selectedRecord.id,
          initialData: fullRecord 
        } 
      });
    } catch (error) {
      console.error('Error fetching HR record for copying:', error);
      alert('Failed to load record data for copying. Please try again.');
    } finally {
      setIsModalLoading(false);
    }
  };


  const handleExportToPDF = () => {
    if (!selectedRecord) return;
    
    try {
      // Create a new window for printing
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        alert('Please allow pop-ups for this site to export PDF');
        return;
      }

      // Create the filename with the specified format
      const lastName = selectedRecord.last_name || '';
      const firstName = selectedRecord.first_name || '';
      const middleName = selectedRecord.middle_name || '';
      const aka = selectedRecord.alias_nickname || '';
      
      let filename = `PDL Health Record - ${lastName}, ${firstName}`;
      if (middleName) {
        filename += ` ${middleName}`;
      }
      if (aka) {
        filename += ` aka "${aka}"`;
      }

      // Generate HTML content for the PDF
      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>${filename}</title>
          <style>
            @page {
              size: A4;
              margin: 8mm;
            }
            * {
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
              box-sizing: border-box;
              margin: 0;
              padding: 0;
            }
            body { 
              font-family: Arial, sans-serif; 
              margin: 0;
              padding: 8px;
              color: #000;
              font-size: 9px;
              line-height: 1.2;
            }
            input[type="checkbox"] {
              width: 12px;
              height: 12px;
              cursor: default;
              appearance: none;
              -webkit-appearance: none;
              border: 1.5px solid #000;
              border-radius: 1px;
              position: relative;
              background-color: #fff;
              display: inline-block;
              vertical-align: middle;
            }
            input[type="checkbox"]:checked {
              background-color: #000;
              border-color: #000;
            }
            input[type="checkbox"]:checked::after {
              content: '';
              position: absolute;
              width: 3px;
              height: 7px;
              border: solid #fff;
              border-width: 0 2px 2px 0;
              top: 45%;
              left: 50%;
              transform: translate(-50%, -50%) rotate(45deg);
            }
            table {
              width: 100%;
              border-collapse: collapse;
              border: 1px solid #000;
              font-size: 9px;
            }
            td {
              border: 1px solid #000;
              padding: 4px 6px;
              vertical-align: top;
            }
            @media print {
              body { margin: 0; padding: 8px; }
              .page-break { page-break-before: always; }
            }
          </style>
        </head>
        <body>
          <!-- Header Section -->
          <div style="display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: 8px; gap: 8px;">
            <div style="display: flex; gap: 8px; align-items: center; flex: 1;">
              <div style="width: 60px; height: 60px; border: 1.5px solid #000; border-radius: 50%; display: flex; align-items: center; justify-content: center; overflow: hidden; background: #fff; flex-shrink: 0;">
                <img src="/BJMPRO-CAR.png" alt="BJMP Logo" style="width: 56px; height: 56px; object-fit: contain;" />
              </div>
              <div style="padding-top: 4px;">
                <div style="font-size: 9px; margin-bottom: 1px; letter-spacing: 0.3px;">REPUBLIC OF THE PHILIPPINES</div>
                <div style="font-size: 9px; margin-bottom: 1px; letter-spacing: 0.3px;">DEPARTMENT OF INTERIOR AND LOCAL GOVERNMENT</div>
                <div style="font-size: 10px; font-weight: bold; letter-spacing: 0.3px;">BUREAU OF JAIL MANAGEMENT AND PENOLOGY</div>
              </div>
            </div>
            <div style="text-align: right; padding-top: 2px; min-width: 220px; flex-shrink: 0;">
              <div style="font-size: 10px; margin-bottom: 12px;">Form No. <span style="border-bottom: 1px solid #000; display: inline-block; width: 70px; margin-left: 3px;"></span></div>
              <div style="display: flex; gap: 6px; justify-content: flex-end; font-size: 9px;">
                <span>Final Remarks:</span>
                <label style="display: flex; align-items: center; gap: 3px;">
                  <input type="checkbox" ${selectedRecord?.final_remarks === 'Release' ? 'checked' : ''} disabled />
                  <span>Release</span>
                </label>
                <label style="display: flex; align-items: center; gap: 3px;">
                  <input type="checkbox" ${selectedRecord?.final_remarks === 'Transfer' ? 'checked' : ''} disabled />
                  <span>Transfer</span>
                </label>
                <label style="display: flex; align-items: center; gap: 3px;">
                  <input type="checkbox" ${selectedRecord?.final_remarks === 'Death' ? 'checked' : ''} disabled />
                  <span>Death</span>
                </label>
              </div>
            </div>
          </div>

          <!-- Black Header Bar -->
          <div style="background: #000; color: #fff; padding: 4px 8px; margin-bottom: 0; display: flex; justify-content: space-between; align-items: center;">
            <div style="font-size: 10px; font-weight: bold; letter-spacing: 0.3px;">PDL HEALTH RECORD</div>
            <div style="font-size: 8px; font-style: italic; letter-spacing: 0.2px;">CONFIDENTIAL - Authorized Health Personnel only.</div>
          </div>

          <!-- Form Table -->
          <table>
            <tbody>
              <!-- Name Row -->
              <tr>
                <td colspan="7">
                  <div style="display: flex; gap: 6px; align-items: flex-start;">
                    <strong style="min-width: 35px; font-size: 8px;">Name:</strong>
                    <div style="flex: 1; display: grid; grid-template-columns: 1fr 1fr 1fr 0.7fr; gap: 8px;">
                      <div style="text-align: center;">
                        <div style="border-bottom: 1px solid #000; min-height: 14px; margin-bottom: 1px; font-size: 8px;">${selectedRecord?.last_name || ''}</div>
                        <div style="font-size: 7px; color: #666;">Surname</div>
                      </div>
                      <div style="text-align: center;">
                        <div style="border-bottom: 1px solid #000; min-height: 14px; margin-bottom: 1px; font-size: 8px;">${selectedRecord?.first_name || ''}</div>
                        <div style="font-size: 7px; color: #666;">First Name</div>
                      </div>
                      <div style="text-align: center;">
                        <div style="border-bottom: 1px solid #000; min-height: 14px; margin-bottom: 1px; font-size: 8px;">${selectedRecord?.middle_name || ''}</div>
                        <div style="font-size: 7px; color: #666;">Middle Name</div>
                      </div>
                      <div style="text-align: center;">
                        <div style="border-bottom: 1px solid #000; min-height: 14px; margin-bottom: 1px; font-size: 8px;">${selectedRecord?.alias_nickname || ''}</div>
                        <div style="font-size: 7px; color: #666;">A.K.A.</div>
                      </div>
                    </div>
                  </div>
                </td>
                <td rowspan="5" style="padding: 4px; vertical-align: middle; text-align: center; width: 100px; background: #f9f9f9;">
                  <div style="font-size: 7px; font-style: italic; color: #666; margin-bottom: 3px;">(photo of PDL)</div>
                  <div style="width: 90px; height: 90px; border: 1px solid #ccc; margin: 0 auto; display: flex; align-items: center; justify-content: center; background: #fff; overflow: hidden;">
                    ${selectedRecord?.photo_of_pdl ? 
                      `<img src="${API_BASE_URL}/api/storage/${selectedRecord.photo_of_pdl}" alt="PDL" style="width: 100%; height: 100%; object-fit: cover;" onerror="this.onerror=null;this.src='/default-image.png';" />`
                      : '<img src="/default-image.png" alt="PDL" style="width: 100%; height: 100%; object-fit: cover;" />'}
                  </div>
                </td>
              </tr>

              <!-- Sex, Age, Civil Status, Educational Attainment Row -->
              <tr>
                <td style="width: 15%;">
                  <div style="display: flex; align-items: center; gap: 4px; font-size: 8px;">
                    <strong>Sex:</strong>
                    <label style="display: flex; align-items: center; gap: 2px;">
                      <input type="checkbox" ${selectedRecord?.sex === 'M' ? 'checked' : ''} disabled />
                      <span>M</span>
                    </label>
                    <label style="display: flex; align-items: center; gap: 2px; margin-left: 3px;">
                      <input type="checkbox" ${selectedRecord?.sex === 'F' ? 'checked' : ''} disabled />
                      <span>F</span>
                    </label>
                  </div>
                </td>
                <td style="width: 10%;">
                  <div style="font-size: 8px;"><strong>Age:</strong> ${calculateAge(selectedRecord.date_of_birth)}</div>
                </td>
                <td style="width: 25%;">
                  <div style="font-size: 8px;"><strong>Civil status:</strong> ${selectedRecord?.civil_status || ''}</div>
                  <div style="font-size: 7px; color: #666;">(single/married/widow/others)</div>
                </td>
                <td colspan="4">
                  <div style="font-size: 8px;"><strong>Educational attainment:</strong> ${selectedRecord?.educ_attainment || ''}</div>
                  <div style="font-size: 7px; color: #666;">(grade/year/degree)</div>
                </td>
              </tr>

              <!-- Date of Birth, Place of Birth Row -->
              <tr>
                <td colspan="2" style="vertical-align: middle;">
                  <div style="display: flex; gap: 4px; align-items: center;">
                    <strong style="font-size: 8px; white-space: nowrap;">Date of birth:</strong>
                    <div style="display: flex; gap: 2px; align-items: center;">
                      <div style="text-align: center;">
                        <div style="border-bottom: 1px solid #000; min-height: 12px; min-width: 18px; font-size: 8px; margin-bottom: 1px;">
                          ${selectedRecord?.date_of_birth ? String(new Date(selectedRecord.date_of_birth).getMonth() + 1).padStart(2, '0') : ''}
                        </div>
                        <div style="font-size: 6px; color: #666;">MM</div>
                      </div>
                      <span style="font-size: 8px; padding-bottom: 8px;">/</span>
                      <div style="text-align: center;">
                        <div style="border-bottom: 1px solid #000; min-height: 12px; min-width: 18px; font-size: 8px; margin-bottom: 1px;">
                          ${selectedRecord?.date_of_birth ? String(new Date(selectedRecord.date_of_birth).getDate()).padStart(2, '0') : ''}
                        </div>
                        <div style="font-size: 6px; color: #666;">DD</div>
                      </div>
                      <span style="font-size: 8px; padding-bottom: 8px;">/</span>
                      <div style="text-align: center;">
                        <div style="border-bottom: 1px solid #000; min-height: 12px; min-width: 28px; font-size: 8px; margin-bottom: 1px;">
                          ${selectedRecord?.date_of_birth ? new Date(selectedRecord.date_of_birth).getFullYear() : ''}
                        </div>
                        <div style="font-size: 6px; color: #666;">YY</div>
                      </div>
                    </div>
                  </div>
                </td>
                <td colspan="5" style="vertical-align: middle;">
                  <div style="display: flex; gap: 6px; align-items: center;">
                    <strong style="white-space: nowrap; font-size: 8px;">Place of birth:</strong>
                    <div style="display: grid; grid-template-columns: 1fr 1fr 1fr 1fr; gap: 4px; flex: 1;">
                      <div style="text-align: center;">
                        <div style="border-bottom: 1px solid #000; min-height: 12px; margin-bottom: 1px; font-size: 8px;">${selectedRecord?.pob_municipality || ''}</div>
                        <div style="font-size: 6px; color: #666;">Municipality/City</div>
                      </div>
                      <div style="text-align: center;">
                        <div style="border-bottom: 1px solid #000; min-height: 12px; margin-bottom: 1px; font-size: 8px;">${selectedRecord?.pob_province || ''}</div>
                        <div style="font-size: 6px; color: #666;">Province</div>
                      </div>
                      <div style="text-align: center;">
                        <div style="border-bottom: 1px solid #000; min-height: 12px; margin-bottom: 1px; font-size: 8px;">${selectedRecord?.pob_region || ''}</div>
                        <div style="font-size: 8px; color: #666;">Region</div>
                      </div>
                      <div style="text-align: center;">
                        <div style="border-bottom: 1px solid #000; min-height: 18px; margin-bottom: 2px; font-size: 10px;">${selectedRecord?.pob_country || ''}</div>
                        <div style="font-size: 8px; color: #666;">Country</div>
                      </div>
                    </div>
                  </div>
                </td>
              </tr>

              <!-- Date of Commitment, Name of Jail Row -->
              <tr>
                <td colspan="2" style="vertical-align: middle;">
                  <div style="display: flex; gap: 8px; align-items: center;">
                    <strong style="font-size: 10px; white-space: nowrap;">Date of commitment:</strong>
                    <div style="display: flex; gap: 4px; align-items: center;">
                      <div style="text-align: center;">
                        <div style="border-bottom: 1px solid #000; min-height: 18px; min-width: 25px; font-size: 10px; margin-bottom: 2px;">
                          ${selectedRecord?.date_of_commitment ? String(new Date(selectedRecord.date_of_commitment).getMonth() + 1).padStart(2, '0') : ''}
                        </div>
                        <div style="font-size: 8px; color: #666;">MM</div>
                      </div>
                      <span style="font-size: 10px; padding-bottom: 12px;">/</span>
                      <div style="text-align: center;">
                        <div style="border-bottom: 1px solid #000; min-height: 18px; min-width: 25px; font-size: 10px; margin-bottom: 2px;">
                          ${selectedRecord?.date_of_commitment ? String(new Date(selectedRecord.date_of_commitment).getDate()).padStart(2, '0') : ''}
                        </div>
                        <div style="font-size: 8px; color: #666;">DD</div>
                      </div>
                      <span style="font-size: 10px; padding-bottom: 12px;">/</span>
                      <div style="text-align: center;">
                        <div style="border-bottom: 1px solid #000; min-height: 18px; min-width: 35px; font-size: 10px; margin-bottom: 2px;">
                          ${selectedRecord?.date_of_commitment ? new Date(selectedRecord.date_of_commitment).getFullYear() : ''}
                        </div>
                        <div style="font-size: 8px; color: #666;">YY</div>
                      </div>
                    </div>
                  </div>
                </td>
                <td colspan="5">
                  <div><strong>Name of jail:</strong> ${selectedRecord?.name_of_jail || ''}</div>
                </td>
              </tr>

              <!-- Origin Lock-up, PhilHealth No Row -->
              <tr>
                <td colspan="2" style="font-size: 8px;">
                  <div><strong>Origin lock-up:</strong> ${selectedRecord?.origin_lock_up || ''}</div>
                </td>
                <td colspan="6" style="font-size: 8px;">
                  <div><strong>PhilHealth No:</strong> ${selectedRecord?.philhealth_no || ''}</div>
                </td>
              </tr>

              <!-- Case, Case No Row -->
              <tr>
                <td colspan="4" style="font-size: 8px;">
                  <div><strong>Case:</strong> ${selectedRecord?.case_ || ''}</div>
                </td>
                <td colspan="4" style="font-size: 8px;">
                  <div><strong>Case No:</strong> ${selectedRecord?.case_no || ''}</div>
                </td>
              </tr>

              <!-- Name of contact person, Relationship Row -->
              <tr>
                <td colspan="4" style="font-size: 8px;">
                  <div><strong>Name of contact person:</strong> ${selectedRecord?.name_of_contact_person || ''}</div>
                </td>
                <td colspan="4" style="font-size: 8px;">
                  <div><strong>Relationship:</strong> ${selectedRecord?.relationship || ''}</div>
                </td>
              </tr>

              <!-- Address of contact person, Phone/Email Row -->
              <tr>
                <td colspan="4" style="font-size: 8px;">
                  <div><strong>Address of contact person:</strong> ${selectedRecord?.add_of_contact_person || ''}</div>
                </td>
                <td colspan="4" style="font-size: 8px;">
                  <div><strong>Phone / Email:</strong> ${selectedRecord?.phone_email || ''}</div>
                </td>
              </tr>

              <!-- Section Header: A. INITIAL HEALTH ASSESSMENT -->
              <tr>
                <td colspan="8" style="padding: 6px 8px; background: #fff; border-top: 3px solid #000;">
                  <div style="font-size: 10px; font-weight: bold;">A. INITIAL HEALTH ASSESSMENT</div>
                </td>
              </tr>

              <!-- Present Health Condition & Blood type Row -->
              <tr>
                <td colspan="7" style="font-size: 8px;">
                  <div style="margin-bottom: 6px;"><strong>Present Health Condition:</strong> ${(selectedRecord?.hr_present_health_conditions || []).map(c => c.present_health_condition).join(', ') || ''}</div>
                  <div><strong>PAST MEDICAL HISTORY</strong> <span style="font-size: 7px; font-style: italic;">(Please enumerate/document as applicable):</span></div>
                </td>
                <td style="font-size: 8px;">
                  <div><strong>Blood type:</strong> ${selectedRecord?.hr_a_medical_history?.blood_type || ''}</div>
                </td>
              </tr>

              <!-- Pediatric History Row -->
              <tr>
                <td colspan="7" style="min-height: 30px; font-size: 8px;">
                  <div>Pediatric History / Significant Childhood Illnesses: ${selectedRecord?.hr_a_medical_history?.pediatric_history || ''}</div>
                </td>
                <td rowspan="2" style="font-size: 8px;">
                  <div><strong>Allergies:</strong> ${selectedRecord?.hr_a_medical_history?.allergies || ''}</div>
                </td>
              </tr>

              <!-- Major Adult Illnesses Row -->
              <tr>
                <td colspan="7" style="min-height: 30px; font-size: 8px;">
                  <div>Major Adult Illnesses: ${selectedRecord?.hr_a_medical_history?.major_adult_illnesses || ''}</div>
                </td>
              </tr>

              <!-- Major Surgeries Row -->
              <tr>
                <td colspan="7" style="min-height: 30px; font-size: 8px;">
                  <div>Major Surgeries / Previous Hospitalization: ${selectedRecord?.hr_a_medical_history?.major_surgeries || ''}</div>
                </td>
                <td rowspan="6" style="font-size: 8px;">
                  <div style="margin-bottom: 4px;"><strong>Family History</strong> <span style="font-size: 7px; font-style: italic;">(up to 1st degree):</span></div>
                  <div style="display: flex; flex-direction: column; gap: 3px;">
                    <label style="display: flex; align-items: center; gap: 4px;">
                      <input type="checkbox" ${selectedRecord?.hr_a_medical_history?.fm_hypertension === 1 ? 'checked' : ''} disabled />
                      <span style="font-size: 8px;">Hypertension</span>
                    </label>
                    <label style="display: flex; align-items: center; gap: 4px;">
                      <input type="checkbox" ${selectedRecord?.hr_a_medical_history?.fm_stroke === 1 ? 'checked' : ''} disabled />
                      <span style="font-size: 8px;">Stroke</span>
                    </label>
                    <label style="display: flex; align-items: center; gap: 4px;">
                      <input type="checkbox" ${selectedRecord?.hr_a_medical_history?.fm_heartAttack === 1 ? 'checked' : ''} disabled />
                      <span style="font-size: 8px;">Heart attack</span>
                    </label>
                    <label style="display: flex; align-items: center; gap: 4px;">
                      <input type="checkbox" ${selectedRecord?.hr_a_medical_history?.fm_diabetes === 1 ? 'checked' : ''} disabled />
                      <span style="font-size: 8px;">Diabetes</span>
                    </label>
                    <label style="display: flex; align-items: center; gap: 4px;">
                      <input type="checkbox" ${selectedRecord?.hr_a_medical_history?.fm_asthma === 1 ? 'checked' : ''} disabled />
                      <span style="font-size: 8px;">Asthma</span>
                    </label>
                    <label style="display: flex; align-items: center; gap: 4px;">
                      <input type="checkbox" ${selectedRecord?.hr_a_medical_history?.fm_kidneyDisease === 1 ? 'checked' : ''} disabled />
                      <span style="font-size: 8px;">Kidney disease</span>
                    </label>
                    <label style="display: flex; align-items: center; gap: 4px;">
                      <input type="checkbox" ${selectedRecord?.hr_a_medical_history?.fm_cancer === 1 ? 'checked' : ''} disabled />
                      <span style="font-size: 8px;">Cancer</span>
                    </label>
                  </div>
                  <div style="margin-top: 4px; font-size: 8px;"><strong>Others:</strong> ${selectedRecord?.hr_a_medical_history?.fm_others || ''}</div>
                </td>
              </tr>

              <!-- Serious Physical Injuries Row -->
              <tr>
                <td colspan="7" style="min-height: 30px; font-size: 8px;">
                  <div>Serious Physical Injuries: ${selectedRecord?.hr_a_medical_history?.serious_physical_injuries || ''}</div>
                </td>
              </tr>

              <!-- Limitations in Range of Motion Row -->
              <tr>
                <td colspan="7" style="min-height: 30px; font-size: 8px;">
                  <div>Limitations in Range of Motion & Activities: ${selectedRecord?.hr_a_medical_history?.limit_in_range_of_motion || ''}</div>
                </td>
              </tr>

              <!-- Medications History Row -->
              <tr>
                <td colspan="7" style="min-height: 30px; font-size: 8px;">
                  <div>Medications History: ${selectedRecord?.hr_a_medical_history?.medications_history || ''}</div>
                </td>
              </tr>

              <!-- History of Transfusions Row -->
              <tr>
                <td colspan="7" style="min-height: 30px; font-size: 8px;">
                  <div>History of Transfusions / BT reactions: ${selectedRecord?.hr_a_medical_history?.history_of_transfusions || ''}</div>
                </td>
              </tr>

              <!-- Mental & Emotional Problems Row -->
              <tr>
                <td colspan="7" style="min-height: 30px; font-size: 8px;">
                  <div>Mental & Emotional Problems: ${selectedRecord?.hr_a_medical_history?.mental_emotional_problems || ''}</div>
                </td>
              </tr>

              <!-- PSYCHIATRIC HISTORY SECTION -->
              <tr>
                <td colspan="8" style="padding: 4px 6px; font-size: 8px;">
                  <div style="display: flex; align-items: center; gap: 8px;">
                    <strong>Past psychiatric history:</strong>
                    <span>Consultation with Psychiatrist</span>
                    <label style="display: inline-flex; align-items: center; gap: 3px; margin-left: 8px;">
                      <input type="checkbox" ${selectedRecord?.hr_a_psychiatric_history?.consulted_psychiatrist === 1 ? 'checked' : ''} disabled style="width: 12px; height: 12px;" />
                      <span>Yes</span>
                    </label>
                    <label style="display: inline-flex; align-items: center; gap: 3px;">
                      <input type="checkbox" ${selectedRecord?.hr_a_psychiatric_history?.consulted_psychiatrist === 0 ? 'checked' : ''} disabled style="width: 12px; height: 12px;" />
                      <span>No</span>
                    </label>
                    <span style="margin-left: 12px;">If yes, when? ${selectedRecord?.hr_a_psychiatric_history?.consultation_date || '_____________'}</span>
                  </div>
                </td>
              </tr>

              <tr>
                <td colspan="8" style="padding: 4px 6px; font-size: 8px;">
                  <div style="display: flex; align-items: center; gap: 8px;">
                    <strong>Confinement in a psychiatric facility:</strong>
                    <label style="display: inline-flex; align-items: center; gap: 3px; margin-left: 8px;">
                      <input type="checkbox" ${selectedRecord?.hr_a_psychiatric_history?.confined === 1 ? 'checked' : ''} disabled style="width: 12px; height: 12px;" />
                      <span>Yes</span>
                    </label>
                    <label style="display: inline-flex; align-items: center; gap: 3px;">
                      <input type="checkbox" ${selectedRecord?.hr_a_psychiatric_history?.confined === 0 ? 'checked' : ''} disabled style="width: 12px; height: 12px;" />
                      <span>No</span>
                    </label>
                    <span style="margin-left: 12px;">If yes, when? ${selectedRecord?.hr_a_psychiatric_history?.confinement_date || '_____________'}</span>
                    <span style="margin-left: 12px;">Where? ${selectedRecord?.hr_a_psychiatric_history?.confinement_place || '_____________'}</span>
                  </div>
                </td>
              </tr>

              <tr>
                <td colspan="4" style="padding: 4px 6px; font-size: 8px;">
                  <strong>Diagnosis:</strong> ${selectedRecord?.hr_a_psychiatric_history?.diagnosis || '_______________________'}
                </td>
                <td colspan="4" style="padding: 4px 6px; font-size: 8px;">
                  <strong>Medication taken:</strong> ${selectedRecord?.hr_a_psychiatric_history?.medication || '_______________________'}
                </td>
              </tr>

              <tr>
                <td colspan="8" style="padding: 4px 6px; font-size: 8px;">
                  <div style="display: flex; align-items: center; gap: 8px;">
                    <strong>ARV treatment:</strong>
                    <label style="display: inline-flex; align-items: center; gap: 3px; margin-left: 8px;">
                      <input type="checkbox" ${selectedRecord?.hr_a_psychiatric_history?.arv_treatment === 1 ? 'checked' : ''} disabled style="width: 12px; height: 12px;" />
                      <span>Yes</span>
                    </label>
                    <label style="display: inline-flex; align-items: center; gap: 3px;">
                      <input type="checkbox" ${selectedRecord?.hr_a_psychiatric_history?.arv_treatment === 0 ? 'checked' : ''} disabled style="width: 12px; height: 12px;" />
                      <span>No</span>
                    </label>
                    <span style="margin-left: 12px;">If yes, from (MM/DD/YY) ${selectedRecord?.hr_a_psychiatric_history?.arv_start || '____/____/____'}</span>
                    <span style="margin-left: 12px;">Place: ${selectedRecord?.hr_a_psychiatric_history?.arv_place || '_______________________'}</span>
                  </div>
                </td>
              </tr>

              <tr>
                <td colspan="8" style="padding: 4px 6px; font-size: 8px;">
                  <div style="display: flex; align-items: center; gap: 12px;">
                    <div style="display: flex; align-items: center; gap: 8px;">
                      <strong>OB/GYNE history:</strong>
                      <label style="display: inline-flex; align-items: center; gap: 3px;">
                        <input type="checkbox" disabled style="width: 12px; height: 12px;" />
                        <span>Yes</span>
                      </label>
                      <label style="display: inline-flex; align-items: center; gap: 3px;">
                        <input type="checkbox" disabled style="width: 12px; height: 12px;" />
                        <span>No</span>
                      </label>
                    </div>
                    <span>LMP (MM/DD/YY) ${selectedRecord?.hr_a_psychiatric_history?.lmp || '____________________'}</span>
                    <span>G ${selectedRecord?.hr_a_psychiatric_history?.g_gravida || '________'}</span>
                    <span>P ${selectedRecord?.hr_a_psychiatric_history?.p_para || '________'}</span>
                  </div>
                </td>
              </tr>

              <tr>
                <td colspan="8" style="padding: 4px 6px; font-size: 8px;">
                  <strong>Vaccines:</strong> <span style="font-style: italic; font-size: 7px;">(if known)</span> ${selectedRecord?.hr_a_psychiatric_history?.vaccines || '_______________________________________________'}
                </td>
              </tr>

              <!-- ALCOHOL HISTORY SECTION -->
              <tr>
                <td colspan="8" style="padding: 6px 8px; background: #f0f0f0; border-top: 2px solid #000;">
                  <div style="font-size: 9px; font-weight: bold;">ALCOHOL HISTORY</div>
                </td>
              </tr>

              <tr>
                <td colspan="8" style="padding: 4px 6px; font-size: 8px; border-bottom: none;">
                  <div style="display: flex; align-items: center; gap: 12px;">
                    <div style="display: flex; align-items: center; gap: 8px;">
                      <strong>Alcohol drinker:</strong>
                      <label style="display: inline-flex; align-items: center; gap: 3px;">
                        <input type="checkbox" ${selectedRecord?.hr_a_drugs_history?.alcohol_drinker === 1 ? 'checked' : ''} disabled style="width: 12px; height: 12px;" />
                        <span>Yes</span>
                      </label>
                      <label style="display: inline-flex; align-items: center; gap: 3px;">
                        <input type="checkbox" ${selectedRecord?.hr_a_drugs_history?.alcohol_drinker === 0 ? 'checked' : ''} disabled style="width: 12px; height: 12px;" />
                        <span>No</span>
                      </label>
                    </div>
                    <span>Age at onset: ${selectedRecord?.hr_a_drugs_history?.alcohol_age_at_onset || '________'}</span>
                    <span>Frequency of use per week: ${selectedRecord?.hr_a_drugs_history?.use_per_week || '_______________'}</span>
                    <span>Type of drinks containing alcohol: ${selectedRecord?.hr_a_drugs_history?.type_of_drinks || '_______________'}</span>
                  </div>
                </td>
              </tr>

              <tr>
                <td colspan="8" style="padding: 4px 6px; font-size: 8px; border-top: none;">
                  <strong>Approximate volume of alcohol consumed:</strong> ${selectedRecord?.hr_a_drugs_history?.volume_of_alcohol || '_______________'} <span style="font-size: 7px; font-style: italic;">(bottle, glass, shots, etc.)</span>
                </td>
              </tr>

              <!-- SMOKING SECTION -->
              <tr>
                <td colspan="8" style="padding: 6px 8px; background: #f0f0f0; border-top: 2px solid #000;">
                  <div style="font-size: 9px; font-weight: bold;">SMOKING (Tobacco/Cigarette/Vape)</div>
                </td>
              </tr>

              <tr>
                <td colspan="8" style="padding: 4px 6px; font-size: 8px; border-bottom: none;">
                  <div style="display: flex; align-items: center; gap: 12px;">
                    <label style="display: inline-flex; align-items: center; gap: 3px;">
                      <input type="checkbox" ${selectedRecord?.hr_a_drugs_history?.smoking === 'Never smoked' ? 'checked' : ''} disabled style="width: 12px; height: 12px;" />
                      <span>Never smoked</span>
                    </label>
                    <label style="display: inline-flex; align-items: center; gap: 3px;">
                      <input type="checkbox" ${selectedRecord?.hr_a_drugs_history?.smoking === 'Current smoker' ? 'checked' : ''} disabled style="width: 12px; height: 12px;" />
                      <span>Current smoker</span>
                    </label>
                    <label style="display: inline-flex; align-items: center; gap: 3px;">
                      <input type="checkbox" ${selectedRecord?.hr_a_drugs_history?.smoking === 'Passive smoker' ? 'checked' : ''} disabled style="width: 12px; height: 12px;" />
                      <span>Passive smoker</span>
                    </label>
                    <label style="display: inline-flex; align-items: center; gap: 3px;">
                      <input type="checkbox" ${selectedRecord?.hr_a_drugs_history?.smoking === 'Stopped > a year' ? 'checked' : ''} disabled style="width: 12px; height: 12px;" />
                      <span>Stopped > a year</span>
                    </label>
                    <label style="display: inline-flex; align-items: center; gap: 3px;">
                      <input type="checkbox" ${selectedRecord?.hr_a_drugs_history?.smoking === 'Stopped < a year' ? 'checked' : ''} disabled style="width: 12px; height: 12px;" />
                      <span>Stopped < a year</span>
                    </label>
                  </div>
                </td>
              </tr>

              <tr>
                <td colspan="8" style="padding: 4px 6px; font-size: 8px; border-top: none;">
                  <div style="display: flex; align-items: center; gap: 12px;">
                    <span><strong>Age at onset:</strong> ${selectedRecord?.hr_a_drugs_history?.smoking_age_at_onset || '_______________________'}</span>
                    <span><strong>Number of cigarette sticks consumed in a day:</strong> ${selectedRecord?.hr_a_drugs_history?.cigarette_a_day || '_______________________'}</span>
                  </div>
                </td>
              </tr>

              <!-- DRUGS SECTION -->
              <tr>
                <td colspan="8" style="padding: 6px 8px; background: #f0f0f0; border-top: 2px solid #000;">
                  <div style="font-size: 9px; font-weight: bold;">DRUGS</div>
                </td>
              </tr>

              <tr>
                <td colspan="8" style="padding: 4px 6px; font-size: 8px;">
                  <div style="display: flex; align-items: center; gap: 12px;">
                    <div style="display: flex; align-items: center; gap: 8px;">
                      <strong>Use of illicit drugs:</strong>
                      <label style="display: inline-flex; align-items: center; gap: 3px;">
                        <input type="checkbox" ${selectedRecord?.hr_a_drugs_history?.use_drugs === 1 ? 'checked' : ''} disabled style="width: 12px; height: 12px;" />
                        <span>Yes</span>
                      </label>
                      <label style="display: inline-flex; align-items: center; gap: 3px;">
                        <input type="checkbox" ${selectedRecord?.hr_a_drugs_history?.use_drugs === 0 ? 'checked' : ''} disabled style="width: 12px; height: 12px;" />
                        <span>No</span>
                      </label>
                    </div>
                    <span>Age of onset: ${selectedRecord?.hr_a_drugs_history?.drugs_age_at_onset || '______________'}</span>
                    <div style="display: flex; align-items: center; gap: 8px; margin-left: 12px;">
                      <strong>Injecting drugs for non-medical use:</strong>
                      <label style="display: inline-flex; align-items: center; gap: 3px;">
                        <input type="checkbox" ${selectedRecord?.hr_a_drugs_history?.injecting_drugs === 1 ? 'checked' : ''} disabled style="width: 12px; height: 12px;" />
                        <span>Yes</span>
                      </label>
                      <label style="display: inline-flex; align-items: center; gap: 3px;">
                        <input type="checkbox" ${selectedRecord?.hr_a_drugs_history?.injecting_drugs === 0 ? 'checked' : ''} disabled style="width: 12px; height: 12px;" />
                        <span>No</span>
                      </label>
                    </div>
                  </div>
                </td>
              </tr>

              <tr>
                <td colspan="8" style="padding: 4px 6px; font-size: 8px;">
                  <div style="display: flex; align-items: center; gap: 8px;">
                    <strong>Uses more than one drug at a time:</strong>
                    <label style="display: inline-flex; align-items: center; gap: 3px;">
                      <input type="checkbox" ${selectedRecord?.hr_a_drugs_history?.use_more_than_one_drugs === 1 ? 'checked' : ''} disabled style="width: 12px; height: 12px;" />
                      <span>Yes</span>
                    </label>
                    <label style="display: inline-flex; align-items: center; gap: 3px;">
                      <input type="checkbox" ${selectedRecord?.hr_a_drugs_history?.use_more_than_one_drugs === 0 ? 'checked' : ''} disabled style="width: 12px; height: 12px;" />
                      <span>No</span>
                    </label>
                  </div>
                </td>
              </tr>

              <!-- Drug Usage Table -->
              <tr>
                <td style="padding: 4px 6px; font-size: 8px; font-weight: bold; border: 1px solid #000; text-align: center; width: 12.5%;">Primary use</td>
                <td colspan="2" style="padding: 4px 6px; font-size: 8px; border: 1px solid #000; width: 25%;">${selectedRecord?.hr_a_drugs_history?.primary_use || ''}</td>
                <td style="padding: 4px 6px; font-size: 8px; font-weight: bold; border: 1px solid #000; text-align: center; width: 12.5%;">Frequency</td>
                <td colspan="2" style="padding: 4px 6px; font-size: 8px; border: 1px solid #000; width: 12.5%;">${selectedRecord?.hr_a_drugs_history?.pri_frequency || ''}</td>
                <td style="padding: 4px 6px; font-size: 8px; font-weight: bold; border: 1px solid #000; text-align: center; width: 12.5%;">Last intake</td>
                <td colspan="2" style="padding: 4px 6px; font-size: 8px; border: 1px solid #000; width: 25%;">${selectedRecord?.hr_a_drugs_history?.pri_last_intake || ''}</td>
              </tr>

              <tr>
                <td style="padding: 4px 6px; font-size: 8px; font-weight: bold; border: 1px solid #000; text-align: center;">Secondary use</td>
                <td colspan="2" style="padding: 4px 6px; font-size: 8px; border: 1px solid #000;">${selectedRecord?.hr_a_drugs_history?.secondary_use || ''}</td>
                <td style="padding: 4px 6px; font-size: 8px; font-weight: bold; border: 1px solid #000; text-align: center;">Frequency</td>
                <td colspan="2" style="padding: 4px 6px; font-size: 8px; border: 1px solid #000;">${selectedRecord?.hr_a_drugs_history?.sec_frequency || ''}</td>
                <td style="padding: 4px 6px; font-size: 8px; font-weight: bold; border: 1px solid #000; text-align: center;">Last intake</td>
                <td colspan="2" style="padding: 4px 6px; font-size: 8px; border: 1px solid #000;">${selectedRecord?.hr_a_drugs_history?.sec_last_intake || ''}</td>
              </tr>

              <tr>
                <td style="padding: 4px 6px; font-size: 8px; font-weight: bold; border: 1px solid #000; text-align: center;">Other</td>
                <td colspan="2" style="padding: 4px 6px; font-size: 8px; border: 1px solid #000;">${selectedRecord?.hr_a_drugs_history?.other_use || ''}</td>
                <td style="padding: 4px 6px; font-size: 8px; font-weight: bold; border: 1px solid #000; text-align: center;">Frequency</td>
                <td colspan="2" style="padding: 4px 6px; font-size: 8px; border: 1px solid #000;">${selectedRecord?.hr_a_drugs_history?.oth_frequency || ''}</td>
                <td style="padding: 4px 6px; font-size: 8px; font-weight: bold; border: 1px solid #000; text-align: center;">Last intake</td>
                <td colspan="2" style="padding: 4px 6px; font-size: 8px; border: 1px solid #000;">${selectedRecord?.hr_a_drugs_history?.oth_last_intake || ''}</td>
              </tr>

              <!-- B. PHYSICAL EXAMINATION ON ARRIVAL -->
              <tr style="page-break-before: always;">
                <td colspan="8" style="padding: 6px; font-size: 10px; font-weight: bold; border: 1px solid #000; background-color: #fff; text-align: left;">
                  B. PHYSICAL EXAMINATION ON ARRIVAL
                </td>
              </tr>

              <!-- Vital Signs -->
              <tr>
                <td colspan="8" style="padding: 6px; font-size: 8px; border: 1px solid #000;">
                  <div style="display: flex; gap: 15px; flex-wrap: wrap;">
                    <div>T: <span style="border-bottom: 1px solid #000; display: inline-block; min-width: 60px; text-align: center;">${selectedRecord?.hr_b_physical_examination?.temp || ''}</span></div>
                    <div>BP: <span style="border-bottom: 1px solid #000; display: inline-block; min-width: 60px; text-align: center;">${selectedRecord?.hr_b_physical_examination?.bp || ''}</span></div>
                    <div>HR: <span style="border-bottom: 1px solid #000; display: inline-block; min-width: 60px; text-align: center;">${selectedRecord?.hr_b_physical_examination?.hr || ''}</span></div>
                    <div>RR: <span style="border-bottom: 1px solid #000; display: inline-block; min-width: 60px; text-align: center;">${selectedRecord?.hr_b_physical_examination?.rr || ''}</span></div>
                    <div>Height: <span style="border-bottom: 1px solid #000; display: inline-block; min-width: 60px; text-align: center;">${selectedRecord?.hr_b_physical_examination?.height || ''}</span></div>
                    <div>Weight: <span style="border-bottom: 1px solid #000; display: inline-block; min-width: 60px; text-align: center;">${selectedRecord?.hr_b_physical_examination?.weight || ''}</span></div>
                    <div>BMI: <span style="border-bottom: 1px solid #000; display: inline-block; min-width: 60px; text-align: center;">${selectedRecord?.hr_b_physical_examination?.bmi || ''}</span></div>
                  </div>
                </td>
              </tr>

              <!-- Physical Examination Items and Detailed Findings -->
              <tr>
                <!-- Left Column - Examination Items -->
                <td colspan="5" style="padding: 6px; font-size: 8px; border: 1px solid #000; vertical-align: top;">
                  <!-- Yes/No Header -->
                  <div style="display: flex; margin-bottom: 6px;">
                    <div style="flex: 1;"></div>
                    <div style="display: flex; gap: 8px; min-width: 60px; justify-content: center;">
                      <div style="width: 12px; text-align: center; font-size: 8px; font-weight: bold;">Yes</div>
                      <div style="width: 12px; text-align: center; font-size: 8px; font-weight: bold;">No</div>
                    </div>
                  </div>

                  <!-- Item 1 -->
                  <div style="display: flex; margin-bottom: 5px;">
                    <div style="flex: 1;">
                      <strong>1. General appearance</strong> <em style="font-size: 7px;">(conscious, coherent, ambulatory, not in cardio-respiratory distress)</em>
                    </div>
                    <div style="display: flex; gap: 8px; min-width: 60px; justify-content: center;">
                      <input type="checkbox" ${selectedRecord?.hr_b_physical_examination?.general_appearance === 'Yes' || selectedRecord?.hr_b_physical_examination?.general_appearance === 1 || selectedRecord?.hr_b_physical_examination?.general_appearance === '1' ? 'checked' : ''} disabled style="width: 12px; height: 12px;" />
                      <input type="checkbox" ${selectedRecord?.hr_b_physical_examination?.general_appearance === 'No' || selectedRecord?.hr_b_physical_examination?.general_appearance === 0 || selectedRecord?.hr_b_physical_examination?.general_appearance === '0' ? 'checked' : ''} disabled style="width: 12px; height: 12px;" />
                    </div>
                  </div>

                  <!-- Item 2 -->
                  <div style="display: flex; margin-bottom: 5px;">
                    <div style="flex: 1;">
                      <strong>2. Head, eyes, ears, nose, throat</strong> <em style="font-size: 7px;">(pink palpebral conjunctivae, anicteric sclerae, no nasal or aural discharges, tonsils not enlarged, no palpable neck mass)</em>
                    </div>
                    <div style="display: flex; gap: 8px; min-width: 60px; justify-content: center;">
                      <input type="checkbox" ${selectedRecord?.hr_b_physical_examination?.head === 'Yes' || selectedRecord?.hr_b_physical_examination?.head === 1 || selectedRecord?.hr_b_physical_examination?.head === '1' ? 'checked' : ''} disabled style="width: 12px; height: 12px;" />
                      <input type="checkbox" ${selectedRecord?.hr_b_physical_examination?.head === 'No' || selectedRecord?.hr_b_physical_examination?.head === 0 || selectedRecord?.hr_b_physical_examination?.head === '0' ? 'checked' : ''} disabled style="width: 12px; height: 12px;" />
                    </div>
                  </div>

                  <!-- Item 3 -->
                  <div style="display: flex; margin-bottom: 5px;">
                    <div style="flex: 1;">
                      <strong>3. Neck</strong> <em style="font-size: 7px;">(supple, no palpable mass)</em>
                    </div>
                    <div style="display: flex; gap: 8px; min-width: 60px; justify-content: center;">
                      <input type="checkbox" ${selectedRecord?.hr_b_physical_examination?.neck === 'Yes' || selectedRecord?.hr_b_physical_examination?.neck === 1 || selectedRecord?.hr_b_physical_examination?.neck === '1' ? 'checked' : ''} disabled style="width: 12px; height: 12px;" />
                      <input type="checkbox" ${selectedRecord?.hr_b_physical_examination?.neck === 'No' || selectedRecord?.hr_b_physical_examination?.neck === 0 || selectedRecord?.hr_b_physical_examination?.neck === '0' ? 'checked' : ''} disabled style="width: 12px; height: 12px;" />
                    </div>
                  </div>

                  <!-- Item 4 -->
                  <div style="display: flex; margin-bottom: 5px;">
                    <div style="flex: 1;">
                      <strong>4. Chest/Lungs</strong> <em style="font-size: 7px;">(symmetrical chest expansion, clear breath sounds)</em>
                    </div>
                    <div style="display: flex; gap: 8px; min-width: 60px; justify-content: center;">
                      <input type="checkbox" ${selectedRecord?.hr_b_physical_examination?.chest === 'Yes' || selectedRecord?.hr_b_physical_examination?.chest === 1 || selectedRecord?.hr_b_physical_examination?.chest === '1' ? 'checked' : ''} disabled style="width: 12px; height: 12px;" />
                      <input type="checkbox" ${selectedRecord?.hr_b_physical_examination?.chest === 'No' || selectedRecord?.hr_b_physical_examination?.chest === 0 || selectedRecord?.hr_b_physical_examination?.chest === '0' ? 'checked' : ''} disabled style="width: 12px; height: 12px;" />
                    </div>
                  </div>

                  <!-- Item 5 -->
                  <div style="display: flex; margin-bottom: 5px;">
                    <div style="flex: 1;">
                      <strong>5. Heart</strong> <em style="font-size: 7px;">(adynamic precordium, regular rate, normal heart sounds, no murmur)</em>
                    </div>
                    <div style="display: flex; gap: 8px; min-width: 60px; justify-content: center;">
                      <input type="checkbox" ${selectedRecord?.hr_b_physical_examination?.heart === 'Yes' || selectedRecord?.hr_b_physical_examination?.heart === 1 || selectedRecord?.hr_b_physical_examination?.heart === '1' ? 'checked' : ''} disabled style="width: 12px; height: 12px;" />
                      <input type="checkbox" ${selectedRecord?.hr_b_physical_examination?.heart === 'No' || selectedRecord?.hr_b_physical_examination?.heart === 0 || selectedRecord?.hr_b_physical_examination?.heart === '0' ? 'checked' : ''} disabled style="width: 12px; height: 12px;" />
                    </div>
                  </div>

                  <!-- Item 6 -->
                  <div style="display: flex; margin-bottom: 5px;">
                    <div style="flex: 1;">
                      <strong>6. Abdomen</strong> <em style="font-size: 7px;">(flat, soft, no masses, normal bowel sounds)</em>
                    </div>
                    <div style="display: flex; gap: 8px; min-width: 60px; justify-content: center;">
                      <input type="checkbox" ${selectedRecord?.hr_b_physical_examination?.abdomen === 'Yes' || selectedRecord?.hr_b_physical_examination?.abdomen === 1 || selectedRecord?.hr_b_physical_examination?.abdomen === '1' ? 'checked' : ''} disabled style="width: 12px; height: 12px;" />
                      <input type="checkbox" ${selectedRecord?.hr_b_physical_examination?.abdomen === 'No' || selectedRecord?.hr_b_physical_examination?.abdomen === 0 || selectedRecord?.hr_b_physical_examination?.abdomen === '0' ? 'checked' : ''} disabled style="width: 12px; height: 12px;" />
                    </div>
                  </div>

                  <!-- Item 7 -->
                  <div style="display: flex; margin-bottom: 5px;">
                    <div style="flex: 1;">
                      <strong>7. Genito-urinary</strong> <em style="font-size: 7px;">(no lesions, no urethral discharges, no palpable hernia)</em>
                    </div>
                    <div style="display: flex; gap: 8px; min-width: 60px; justify-content: center;">
                      <input type="checkbox" ${selectedRecord?.hr_b_physical_examination?.genito_urinary === 'Yes' || selectedRecord?.hr_b_physical_examination?.genito_urinary === 1 || selectedRecord?.hr_b_physical_examination?.genito_urinary === '1' ? 'checked' : ''} disabled style="width: 12px; height: 12px;" />
                      <input type="checkbox" ${selectedRecord?.hr_b_physical_examination?.genito_urinary === 'No' || selectedRecord?.hr_b_physical_examination?.genito_urinary === 0 || selectedRecord?.hr_b_physical_examination?.genito_urinary === '0' ? 'checked' : ''} disabled style="width: 12px; height: 12px;" />
                    </div>
                  </div>

                  <!-- Item 8 -->
                  <div style="display: flex; margin-bottom: 5px;">
                    <div style="flex: 1;">
                      <strong>8. Musculo-skeletal</strong> <em style="font-size: 7px;">(no gross deformities, no limitation of movement)</em>
                    </div>
                    <div style="display: flex; gap: 8px; min-width: 60px; justify-content: center;">
                      <input type="checkbox" ${selectedRecord?.hr_b_physical_examination?.musculo_skeletal === 'Yes' || selectedRecord?.hr_b_physical_examination?.musculo_skeletal === 1 || selectedRecord?.hr_b_physical_examination?.musculo_skeletal === '1' ? 'checked' : ''} disabled style="width: 12px; height: 12px;" />
                      <input type="checkbox" ${selectedRecord?.hr_b_physical_examination?.musculo_skeletal === 'No' || selectedRecord?.hr_b_physical_examination?.musculo_skeletal === 0 || selectedRecord?.hr_b_physical_examination?.musculo_skeletal === '0' ? 'checked' : ''} disabled style="width: 12px; height: 12px;" />
                    </div>
                  </div>

                  <!-- Item 9 -->
                  <div style="display: flex; margin-bottom: 5px;">
                    <div style="flex: 1;">
                      <strong>9. Extremities</strong> <em style="font-size: 7px;">(no swelling/deformity, no weakness, good capillary refill, strong pulses, normal flexes)</em>
                    </div>
                    <div style="display: flex; gap: 8px; min-width: 60px; justify-content: center;">
                      <input type="checkbox" ${selectedRecord?.hr_b_physical_examination?.extremities === 'Yes' || selectedRecord?.hr_b_physical_examination?.extremities === 1 || selectedRecord?.hr_b_physical_examination?.extremities === '1' ? 'checked' : ''} disabled style="width: 12px; height: 12px;" />
                      <input type="checkbox" ${selectedRecord?.hr_b_physical_examination?.extremities === 'No' || selectedRecord?.hr_b_physical_examination?.extremities === 0 || selectedRecord?.hr_b_physical_examination?.extremities === '0' ? 'checked' : ''} disabled style="width: 12px; height: 12px;" />
                    </div>
                  </div>

                  <!-- Item 10 -->
                  <div style="display: flex; margin-bottom: 0;">
                    <div style="flex: 1;">
                      <strong>10. Other significant findings</strong> <em style="font-size: 7px;">(specify)</em>
                      <span style="border-bottom: 1px solid #000; display: inline-block; min-width: 150px;">${selectedRecord?.hr_b_physical_examination?.other_findings || ''}</span>
                    </div>
                  </div>
                </td>

                <!-- Right Column - Detailed Findings -->
                <td colspan="3" style="padding: 6px; font-size: 8px; border: 1px solid #000; vertical-align: top;">
                  <strong style="display: block; margin-bottom: 6px;">Detailed findings</strong>
                  <div style="white-space: pre-wrap; word-wrap: break-word; font-size: 8px;">
                    ${selectedRecord?.hr_b_physical_examination?.detailed_findings || ''}
                  </div>
                </td>
              </tr>

              <!-- C. TB ENTRY SCREENING CHECKLIST -->
              <tr>
                <td colspan="8" style="padding: 6px; font-size: 10px; font-weight: bold; border: 1px solid #000; background-color: #fff; text-align: left;">
                  C. TB ENTRY SCREENING CHECKLIST
                </td>
              </tr>

              <tr>
                <!-- Left Column - Checklist Items -->
                <td colspan="4" style="padding: 6px; font-size: 8px; border: 1px solid #000; vertical-align: top;">
                  <!-- Yes/No Header -->
                  <div style="display: flex; margin-bottom: 6px;">
                    <div style="flex: 1;"></div>
                    <div style="display: flex; gap: 8px; min-width: 60px; justify-content: center;">
                      <div style="width: 12px; text-align: center; font-size: 8px; font-weight: bold;">Yes</div>
                      <div style="width: 12px; text-align: center; font-size: 8px; font-weight: bold;">No</div>
                    </div>
                  </div>

                  <!-- Item A -->
                  <div style="display: flex; margin-bottom: 5px;">
                    <div style="flex: 1;">
                      <strong>A.</strong> Unexplained cough, any duration
                    </div>
                    <div style="display: flex; gap: 8px; min-width: 60px; justify-content: center;">
                      <input type="checkbox" ${selectedRecord?.hr_c_tb_screening?.unexplained_cough === 'Yes' || selectedRecord?.hr_c_tb_screening?.unexplained_cough === 1 || selectedRecord?.hr_c_tb_screening?.unexplained_cough === '1' ? 'checked' : ''} disabled style="width: 12px; height: 12px;" />
                      <input type="checkbox" ${selectedRecord?.hr_c_tb_screening?.unexplained_cough === 'No' || selectedRecord?.hr_c_tb_screening?.unexplained_cough === 0 || selectedRecord?.hr_c_tb_screening?.unexplained_cough === '0' ? 'checked' : ''} disabled style="width: 12px; height: 12px;" />
                    </div>
                  </div>

                  <!-- Item B -->
                  <div style="display: flex; margin-bottom: 5px;">
                    <div style="flex: 1;">
                      <strong>B.</strong> BMI &lt; 18.5
                    </div>
                    <div style="display: flex; gap: 8px; min-width: 60px; justify-content: center;">
                      <input type="checkbox" ${selectedRecord?.hr_c_tb_screening?.bmi_low === 'Yes' || selectedRecord?.hr_c_tb_screening?.bmi_low === 1 || selectedRecord?.hr_c_tb_screening?.bmi_low === '1' ? 'checked' : ''} disabled style="width: 12px; height: 12px;" />
                      <input type="checkbox" ${selectedRecord?.hr_c_tb_screening?.bmi_low === 'No' || selectedRecord?.hr_c_tb_screening?.bmi_low === 0 || selectedRecord?.hr_c_tb_screening?.bmi_low === '0' ? 'checked' : ''} disabled style="width: 12px; height: 12px;" />
                    </div>
                  </div>

                  <!-- Known medical conditions -->
                  <div style="display: flex; margin-bottom: 5px;">
                    <div style="flex: 1;">
                      <div style="margin-bottom: 2px;">Known medical conditions</div>
                      <div style="font-size: 7px; font-style: italic; padding-left: 8px;">
                        (Clinical High-risk Groups: HIV/AIDS, diabetes, end-stage renal disease, cancer, connective tissue diseases, autoimmune diseases, silicosis, gastrectomy, solid organ transplantation, prolonged systemic steroids)
                      </div>
                    </div>
                    <div style="display: flex; gap: 8px; min-width: 60px; justify-content: center;">
                      <input type="checkbox" ${selectedRecord?.hr_c_tb_screening?.known_medical_condition === 'Yes' || selectedRecord?.hr_c_tb_screening?.known_medical_condition === 1 || selectedRecord?.hr_c_tb_screening?.known_medical_condition === '1' ? 'checked' : ''} disabled style="width: 12px; height: 12px;" />
                      <input type="checkbox" ${selectedRecord?.hr_c_tb_screening?.known_medical_condition === 'No' || selectedRecord?.hr_c_tb_screening?.known_medical_condition === 0 || selectedRecord?.hr_c_tb_screening?.known_medical_condition === '0' ? 'checked' : ''} disabled style="width: 12px; height: 12px;" />
                    </div>
                  </div>

                  <!-- Item C -->
                  <div style="display: flex; margin-bottom: 5px;">
                    <div style="flex: 1;">
                      <strong>C.</strong> Blood-streaked sputum
                    </div>
                    <div style="display: flex; gap: 8px; min-width: 60px; justify-content: center;">
                      <input type="checkbox" ${selectedRecord?.hr_c_tb_screening?.blood_streaked_sputum === 'Yes' || selectedRecord?.hr_c_tb_screening?.blood_streaked_sputum === 1 || selectedRecord?.hr_c_tb_screening?.blood_streaked_sputum === '1' ? 'checked' : ''} disabled style="width: 12px; height: 12px;" />
                      <input type="checkbox" ${selectedRecord?.hr_c_tb_screening?.blood_streaked_sputum === 'No' || selectedRecord?.hr_c_tb_screening?.blood_streaked_sputum === 0 || selectedRecord?.hr_c_tb_screening?.blood_streaked_sputum === '0' ? 'checked' : ''} disabled style="width: 12px; height: 12px;" />
                    </div>
                  </div>

                  <!-- Weight loss -->
                  <div style="display: flex; margin-bottom: 5px;">
                    <div style="flex: 1;">
                      Weight loss or BMI &lt; 18.5
                    </div>
                    <div style="display: flex; gap: 8px; min-width: 60px; justify-content: center;">
                      <input type="checkbox" ${selectedRecord?.hr_c_tb_screening?.weight_loss === 'Yes' || selectedRecord?.hr_c_tb_screening?.weight_loss === 1 || selectedRecord?.hr_c_tb_screening?.weight_loss === '1' ? 'checked' : ''} disabled style="width: 12px; height: 12px;" />
                      <input type="checkbox" ${selectedRecord?.hr_c_tb_screening?.weight_loss === 'No' || selectedRecord?.hr_c_tb_screening?.weight_loss === 0 || selectedRecord?.hr_c_tb_screening?.weight_loss === '0' ? 'checked' : ''} disabled style="width: 12px; height: 12px;" />
                    </div>
                  </div>

                  <!-- Persistent fever -->
                  <div style="display: flex; margin-bottom: 5px;">
                    <div style="flex: 1;">
                      Persistent fever
                    </div>
                    <div style="display: flex; gap: 8px; min-width: 60px; justify-content: center;">
                      <input type="checkbox" ${selectedRecord?.hr_c_tb_screening?.persistent_fever === 'Yes' || selectedRecord?.hr_c_tb_screening?.persistent_fever === 1 || selectedRecord?.hr_c_tb_screening?.persistent_fever === '1' ? 'checked' : ''} disabled style="width: 12px; height: 12px;" />
                      <input type="checkbox" ${selectedRecord?.hr_c_tb_screening?.persistent_fever === 'No' || selectedRecord?.hr_c_tb_screening?.persistent_fever === 0 || selectedRecord?.hr_c_tb_screening?.persistent_fever === '0' ? 'checked' : ''} disabled style="width: 12px; height: 12px;" />
                    </div>
                  </div>

                  <!-- Item D -->
                  <div style="display: flex; margin-bottom: 5px;">
                    <div style="flex: 1;">
                      <strong>D.</strong> Chest x-ray suggestive of TB
                    </div>
                    <div style="display: flex; gap: 8px; min-width: 60px; justify-content: center;">
                      <input type="checkbox" ${selectedRecord?.hr_c_tb_screening?.xray_suggestion === 'Yes' || selectedRecord?.hr_c_tb_screening?.xray_suggestion === 1 || selectedRecord?.hr_c_tb_screening?.xray_suggestion === '1' ? 'checked' : ''} disabled style="width: 12px; height: 12px;" />
                      <input type="checkbox" ${selectedRecord?.hr_c_tb_screening?.xray_suggestion === 'No' || selectedRecord?.hr_c_tb_screening?.xray_suggestion === 0 || selectedRecord?.hr_c_tb_screening?.xray_suggestion === '0' ? 'checked' : ''} disabled style="width: 12px; height: 12px;" />
                    </div>
                  </div>

                  <!-- Item E -->
                  <div style="display: flex; margin-bottom: 5px;">
                    <div style="flex: 1;">
                      <strong>E.</strong> History of previous TB treatment
                    </div>
                    <div style="display: flex; gap: 8px; min-width: 60px; justify-content: center;">
                      <input type="checkbox" ${selectedRecord?.hr_c_tb_screening?.tb_treatment_history === 'Yes' || selectedRecord?.hr_c_tb_screening?.tb_treatment_history === 1 || selectedRecord?.hr_c_tb_screening?.tb_treatment_history === '1' ? 'checked' : ''} disabled style="width: 12px; height: 12px;" />
                      <input type="checkbox" ${selectedRecord?.hr_c_tb_screening?.tb_treatment_history === 'No' || selectedRecord?.hr_c_tb_screening?.tb_treatment_history === 0 || selectedRecord?.hr_c_tb_screening?.tb_treatment_history === '0' ? 'checked' : ''} disabled style="width: 12px; height: 12px;" />
                    </div>
                  </div>

                  <!-- If outcome known, specify -->
                  <div style="margin-bottom: 5px; padding-left: 8px;">
                    If outcome known, specify:
                    <div style="border-bottom: 1px solid #000; display: block; min-width: 100%; margin-top: 3px;">${selectedRecord?.hr_c_tb_screening?.tb_tratment_specify || ''}</div>
                  </div>

                  <!-- Item F -->
                  <div style="display: flex; margin-bottom: 5px;">
                    <div style="flex: 1;">
                      <strong>F.</strong> Exposure to TB case
                    </div>
                    <div style="display: flex; gap: 8px; min-width: 60px; justify-content: center;">
                      <input type="checkbox" ${selectedRecord?.hr_c_tb_screening?.exposure_to_tb === 'Yes' || selectedRecord?.hr_c_tb_screening?.exposure_to_tb === 1 || selectedRecord?.hr_c_tb_screening?.exposure_to_tb === '1' ? 'checked' : ''} disabled style="width: 12px; height: 12px;" />
                      <input type="checkbox" ${selectedRecord?.hr_c_tb_screening?.exposure_to_tb === 'No' || selectedRecord?.hr_c_tb_screening?.exposure_to_tb === 0 || selectedRecord?.hr_c_tb_screening?.exposure_to_tb === '0' ? 'checked' : ''} disabled style="width: 12px; height: 12px;" />
                    </div>
                  </div>

                  <!-- If known, specify -->
                  <div style="margin-bottom: 5px; padding-left: 8px;">
                    If known, specify:
                    <div style="border-bottom: 1px solid #000; display: block; min-width: 100%; margin-top: 3px;">${selectedRecord?.hr_c_tb_screening?.exposure_specify || ''}</div>
                  </div>

                  <!-- Complete name and signature -->
                  <div style="margin-top: 10px;">
                    Complete name and signature of health staff:
                    <div style="border-bottom: 1px solid #000; display: block; min-width: 100%; margin-top: 3px;">
                      ${(() => {
                        const nurseData = selectedRecord?.nurse_created || selectedRecord?.nurseCreated;
                        if (nurseData) {
                          const firstName = nurseData.first_name || '';
                          const middleName = nurseData.middle_name || '';
                          const lastName = nurseData.last_name || '';
                          return `${firstName} ${middleName} ${lastName}`.trim();
                        }
                        return '';
                      })()}
                    </div>
                  </div>
                </td>

                <!-- Right Column - Remarks and Final Result -->
                <td colspan="4" style="padding: 6px; font-size: 8px; border: 1px solid #000; vertical-align: top;">
                  <!-- Remarks Section -->
                  <div style="margin-bottom: 10px;">
                    <div style="display: flex; align-items: center; margin-bottom: 3px;">
                      <strong style="width: 60px; flex-shrink: 0;">Remarks:</strong>
                      <input type="checkbox" ${selectedRecord?.hr_c_tb_screening?.remarks?.includes('Presumptive TB') ? 'checked' : ''} disabled style="width: 12px; height: 12px; margin-right: 5px;" />
                      <span>Presumptive TB</span>
                    </div>
                    <div style="display: flex; align-items: center; margin-bottom: 3px; margin-left: 60px;">
                      <input type="checkbox" ${selectedRecord?.hr_c_tb_screening?.remarks?.includes('Presumptive DR-TB') ? 'checked' : ''} disabled style="width: 12px; height: 12px; margin-right: 5px;" />
                      <span>Presumptive DR-TB</span>
                    </div>
                    <div style="display: flex; align-items: center; margin-bottom: 3px; margin-left: 60px;">
                      <input type="checkbox" ${selectedRecord?.hr_c_tb_screening?.remarks?.includes('Not TB') ? 'checked' : ''} disabled style="width: 12px; height: 12px; margin-right: 5px;" />
                      <span>Not TB</span>
                    </div>
                    <div style="display: flex; align-items: center; margin-bottom: 3px; margin-left: 60px;">
                      <input type="checkbox" ${selectedRecord?.hr_c_tb_screening?.remarks?.includes('On-going TB treatment') ? 'checked' : ''} disabled style="width: 12px; height: 12px; margin-right: 5px;" />
                      <span>On-going TB treatment <em style="font-size: 7px;">(Ask for NTP Referral Form)</em></span>
                    </div>
                    <div style="display: flex; align-items: flex-start; margin-bottom: 3px; margin-left: 60px;">
                      <input type="checkbox" ${selectedRecord?.hr_c_tb_screening?.remarks?.includes('Other follow-up') ? 'checked' : ''} disabled style="width: 12px; height: 12px; margin-right: 5px; margin-top: 1px;" />
                      <div style="flex: 1;">
                        Other follow-up, specify:
                        <div style="border-bottom: 1px solid #000; display: block; min-width: 100%; margin-top: 2px;">${(selectedRecord?.hr_c_tb_screening?.remarks?.includes('Other follow-up') && selectedRecord?.hr_c_tb_screening?.remarks?.split(':')[1]?.trim()) || ''}</div>
                      </div>
                    </div>
                  </div>

                  <!-- Final Result Section -->
                  <div style="margin-bottom: 10px;">
                    <div style="display: flex; flex-wrap: wrap; gap: 8px; align-items: center;">
                      <strong style="margin-right: 8px;">Final Result:</strong>
                      <div style="display: flex; align-items: center;">
                        <input type="checkbox" ${selectedRecord?.hr_c_tb_screening?.final_result?.includes('New Case') ? 'checked' : ''} disabled style="width: 12px; height: 12px; margin-right: 3px;" />
                        <span>New Case</span>
                      </div>
                      <div style="display: flex; align-items: center;">
                        <input type="checkbox" ${selectedRecord?.hr_c_tb_screening?.final_result?.includes('Retreatment') ? 'checked' : ''} disabled style="width: 12px; height: 12px; margin-right: 3px;" />
                        <span>Retreatment</span>
                      </div>
                      <div style="display: flex; align-items: center;">
                        <input type="checkbox" ${selectedRecord?.hr_c_tb_screening?.final_result?.includes('Negative') ? 'checked' : ''} disabled style="width: 12px; height: 12px; margin-right: 3px;" />
                        <span>Negative</span>
                      </div>
                    </div>
                    <div style="margin-top: 5px; display: flex; gap: 8px; margin-left: 80px;">
                      <div style="display: flex; align-items: center;">
                        <input type="checkbox" ${selectedRecord?.hr_c_tb_screening?.final_result?.includes('BC') ? 'checked' : ''} disabled style="width: 12px; height: 12px; margin-right: 3px;" />
                        <span>BC</span>
                      </div>
                      <div style="display: flex; align-items: center;">
                        <input type="checkbox" ${selectedRecord?.hr_c_tb_screening?.final_result?.includes('CD') ? 'checked' : ''} disabled style="width: 12px; height: 12px; margin-right: 3px;" />
                        <span>CD</span>
                      </div>
                    </div>
                  </div>

                  <!-- Presumptive TB Info Box -->
                  <div style="border: 1.5px solid #000; border-radius: 10px; padding: 8px; margin-top: 6px;">
                    <div style="margin-bottom: 6px;">
                      <strong>Presumptive TB if:</strong>
                      <div style="margin-top: 3px; font-size: 7px; padding-left: 6px;">
                        <div>Unexplained cough of any duration (A) with/without other signs/symptoms (C) or risk factors (B).</div>
                        <div>X-ray suggestive of TB (D)</div>
                        <div>If with any other signs/symptoms (C) or risk factors (B), refer to physician.</div>
                        <div style="padding-left: 10px; margin-top: 2px;">In absence of physician, you may consider presumptive TB.</div>
                      </div>
                    </div>
                    <div>
                      <strong>Presumptive DR-TB if:</strong>
                      <div style="margin-top: 3px; font-size: 7px; padding-left: 6px;">
                        <div>Presumptive TB with previous treatment (E)</div>
                        <div>Presumptive TB with exposure to known active DR-TB case (F)</div>
                      </div>
                    </div>
                  </div>
                </td>
              </tr>

              <!-- Body Diagrams and Conclusion Section -->
              <tr>
                <!-- Left Column - Body Diagrams -->
                <td colspan="4" style="padding: 6px; font-size: 8px; border: 1px solid #000; vertical-align: top;">
                  <!-- Body Diagrams with Markings -->
                  ${(() => {
                    const markColors = { tattoo: '#FF0000', piercing: '#0000FF', scar: '#800080', burn: '#FFA500', bruise: '#4B0082', wound: '#8B0000' };
                    const getColor = (type) => markColors[type] || '#FF0000';

                    const buildDots = (marks) => marks.map((mark, index) => {
                      const count = marks.slice(0, index).filter(m => m.mark_type === mark.mark_type).length + 1;
                      return `<div style="position:absolute;left:${mark.position_x}%;top:${mark.position_y}%;width:12px;height:12px;border-radius:50%;background:${getColor(mark.mark_type)};border:1.5px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,0.4);transform:translate(-50%,-50%);display:flex;align-items:center;justify-content:center;"><span style="color:#fff;font-weight:bold;font-size:7px;line-height:1;">${count}</span></div>`;
                    }).join('');

                    const buildList = (marks) => {
                      if (marks.length === 0) return '<div style="text-align:center;color:#666;font-style:italic;font-size:7px;">No markings</div>';
                      return marks.map((mark, index) => {
                        const count = marks.slice(0, index).filter(m => m.mark_type === mark.mark_type).length + 1;
                        const label = mark.mark_type.charAt(0).toUpperCase() + mark.mark_type.slice(1);
                        return `<div style="display:flex;align-items:flex-start;gap:4px;margin-bottom:4px;padding:3px 4px;border:1px solid #e2e8f0;border-radius:4px;background:#fff;"><span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${getColor(mark.mark_type)};flex-shrink:0;margin-top:1px;"></span><div><div style="font-weight:bold;font-size:7px;text-transform:capitalize;">${label} ${count}</div><div style="color:#555;font-size:7px;">${mark.description || 'No description'}</div></div></div>`;
                      }).join('');
                    };

                    return `
                  <div style="display:flex;gap:12px;margin-bottom:10px;">
                    <!-- Front Body Column -->
                    <div style="flex:1;">
                      <div style="text-align:center;font-size:8px;font-weight:bold;margin-bottom:4px;">Front</div>
                      <div style="display:flex;gap:6px;align-items:flex-start;">
                        <div style="position:relative;border:1.5px solid #ccc;border-radius:6px;background:#fff;display:inline-block;line-height:0;flex-shrink:0;box-shadow:0 2px 6px rgba(0,0,0,0.1);">
                          <img src="/front_body.png" alt="Front View" style="width:95px;height:auto;display:block;border-radius:5px;" />
                          ${buildDots(bodyMarkings.front)}
                        </div>
                        <div style="flex:1;min-width:0;">${buildList(bodyMarkings.front)}</div>
                      </div>
                    </div>
                    <!-- Back Body Column -->
                    <div style="flex:1;">
                      <div style="text-align:center;font-size:8px;font-weight:bold;margin-bottom:4px;">Back</div>
                      <div style="display:flex;gap:6px;align-items:flex-start;">
                        <div style="position:relative;border:1.5px solid #ccc;border-radius:6px;background:#fff;display:inline-block;line-height:0;flex-shrink:0;box-shadow:0 2px 6px rgba(0,0,0,0.1);">
                          <img src="/back_body.png" alt="Back View" style="width:95px;height:auto;display:block;border-radius:5px;" />
                          ${buildDots(bodyMarkings.back)}
                        </div>
                        <div style="flex:1;min-width:0;">${buildList(bodyMarkings.back)}</div>
                      </div>
                    </div>
                  </div>`;
                  })()}

                  <!-- Checkboxes below diagrams -->
                  <div style="margin-bottom: 6px;">
                    <div style="display: flex; align-items: center; margin-bottom: 3px;">
                      <input type="checkbox" ${selectedRecord?.hr_c_tb_conclusion?.no_physical_injury === 1 || selectedRecord?.hr_c_tb_conclusion?.no_physical_injury === '1' || selectedRecord?.hr_c_tb_conclusion?.no_physical_injury === 'Yes' ? 'checked' : ''} disabled style="width: 12px; height: 12px; margin-right: 5px;" />
                      <span>No signs of external physical injury</span>
                    </div>
                    <div style="display: flex; align-items: center; margin-bottom: 3px;">
                      <input type="checkbox" ${selectedRecord?.hr_c_tb_conclusion?.no_skin_markings === 1 || selectedRecord?.hr_c_tb_conclusion?.no_skin_markings === '1' || selectedRecord?.hr_c_tb_conclusion?.no_skin_markings === 'Yes' ? 'checked' : ''} disabled style="width: 12px; height: 12px; margin-right: 5px;" />
                      <span>No skin markings or unusual piercings</span>
                    </div>
                  </div>

                  <!-- Signature -->
                  <div style="margin-top: 8px;">
                    Signature of PDL:
                    <div style="border-bottom: 1px solid #000; display: inline-block; min-width: 140px; margin-left: 5px;"></div>
                  </div>
                </td>

                <!-- Right Column - Conclusion and Recommendation -->
                <td colspan="4" style="padding: 6px; font-size: 8px; border: 1px solid #000; vertical-align: top;">
                  <!-- Conclusion Section -->
                  <div style="margin-bottom: 10px;">
                    <div style="display: flex; align-items: center; margin-bottom: 6px;">
                      <strong style="margin-right: 10px;">Conclusion:</strong>
                      <input type="checkbox" ${selectedRecord?.hr_c_tb_conclusion?.practically_healthy === 1 || selectedRecord?.hr_c_tb_conclusion?.practically_healthy === '1' || selectedRecord?.hr_c_tb_conclusion?.practically_healthy === 'Yes' ? 'checked' : ''} disabled style="width: 12px; height: 12px; margin-right: 5px;" />
                      <span>Practically healthy</span>
                    </div>

                    <div style="margin-bottom: 6px;">
                      <em>For further examination/evaluation:</em>
                    </div>

                    <div style="padding-left: 15px;">
                      <div style="display: flex; align-items: center; margin-bottom: 3px;">
                        <input type="checkbox" ${selectedRecord?.hr_c_tb_conclusion?.require_sbirt === 1 || selectedRecord?.hr_c_tb_conclusion?.require_sbirt === '1' || selectedRecord?.hr_c_tb_conclusion?.require_sbirt === 'Yes' ? 'checked' : ''} disabled style="width: 12px; height: 12px; margin-right: 5px;" />
                        <span>SBIRT</span>
                      </div>
                      <div style="display: flex; align-items: center; margin-bottom: 3px;">
                        <input type="checkbox" ${selectedRecord?.hr_c_tb_conclusion?.require_philpen === 1 || selectedRecord?.hr_c_tb_conclusion?.require_philpen === '1' || selectedRecord?.hr_c_tb_conclusion?.require_philpen === 'Yes' ? 'checked' : ''} disabled style="width: 12px; height: 12px; margin-right: 5px;" />
                        <span>PhilPEN</span>
                      </div>
                      <div style="display: flex; align-items: center; margin-bottom: 3px;">
                        <input type="checkbox" ${selectedRecord?.hr_c_tb_conclusion?.require_neuro_eval === 1 || selectedRecord?.hr_c_tb_conclusion?.require_neuro_eval === '1' || selectedRecord?.hr_c_tb_conclusion?.require_neuro_eval === 'Yes' ? 'checked' : ''} disabled style="width: 12px; height: 12px; margin-right: 5px;" />
                        <span>Neuro-Psychiatric Evaluation</span>
                      </div>
                      <div style="display: flex; align-items: center; margin-bottom: 3px;">
                        <input type="checkbox" ${selectedRecord?.hr_c_tb_conclusion?.require_ill_treatment_doc === 1 || selectedRecord?.hr_c_tb_conclusion?.require_ill_treatment_doc === '1' || selectedRecord?.hr_c_tb_conclusion?.require_ill_treatment_doc === 'Yes' ? 'checked' : ''} disabled style="width: 12px; height: 12px; margin-right: 5px;" />
                        <span>Further documentation of Allegation of Ill-Treatment</span>
                      </div>
                    </div>
                  </div>

                  <!-- Other impressions -->
                  <div style="margin-bottom: 10px;">
                    <div style="margin-bottom: 3px;">Other impressions if not practically healthy:</div>
                    <div style="border-bottom: 1px solid #000; margin-bottom: 3px; min-height: 15px; white-space: pre-wrap;">${selectedRecord?.hr_c_tb_conclusion?.other_impressions || ''}</div>
                  </div>

                  <!-- Recommendation Section -->
                  <div>
                    <div style="display: flex; align-items: center; margin-bottom: 6px;">
                      <strong style="margin-right: 10px;">Recommendation:</strong>
                      <div style="display: flex; flex-direction: column;">
                        <div style="display: flex; align-items: center; margin-bottom: 3px;">
                          <input type="checkbox" ${selectedRecord?.hr_c_tb_conclusion?.recommendation?.includes('dorm') ? 'checked' : ''} disabled style="width: 12px; height: 12px; margin-right: 5px;" />
                          <span>to be accommodated in general dormitory</span>
                        </div>
                        <div style="display: flex; align-items: center; margin-bottom: 3px;">
                          <input type="checkbox" ${selectedRecord?.hr_c_tb_conclusion?.recommendation?.includes('isolated') ? 'checked' : ''} disabled style="width: 12px; height: 12px; margin-right: 5px;" />
                          <span>to be isolated</span>
                        </div>
                        <div style="display: flex; align-items: center; margin-bottom: 3px;">
                          <input type="checkbox" ${selectedRecord?.hr_c_tb_conclusion?.recommendation?.includes('hospitalized') ? 'checked' : ''} disabled style="width: 12px; height: 12px; margin-right: 5px;" />
                          <span>to be hospitalized</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </td>
              </tr>

            </tbody>
          </table>
        </body>
        </html>
      `;

      // Write content to the new window
      printWindow.document.write(htmlContent);
      printWindow.document.close();

      // Wait for content to load then trigger print
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 500);

    } catch (error) {
      console.error('Error exporting to PDF:', error);
      alert('Failed to export PDF. Please try again.');
    }
  };

  return (
    <div className={`${pdlSearchStyles.systemApp} ${isDarkMode ? '' : pdlSearchStyles.lightMode}`}>
      {/* System Header */}
      <header className={pdlSearchStyles.systemHeader}>
        <div className={pdlSearchStyles.headerLeft}>
          <div className={pdlSearchStyles.systemLogo}>
            <img src="/BJMPRO-CAR.png" alt="BJMP CAR Logo" className={pdlSearchStyles.systemLogoImg} />
            <div className={pdlSearchStyles.systemTitle}>
              <h1>MediCast: BJMP Health Monitoring System</h1>
              <p>Bureau of Jail Management and Penology</p>
            </div>
          </div>
        </div>
        <div className={pdlSearchStyles.headerRight}>
          <div className={pdlSearchStyles.systemInfo}>
            <div className={pdlSearchStyles.systemTime}>{currentTime.toLocaleTimeString()}</div>
            <div className={pdlSearchStyles.systemUser}>{formatDateTime(currentTime)}</div>
          </div>
          <NotificationBell />
          <div className={pdlSearchStyles.profileSection}>
            <div className={pdlSearchStyles.profileIcon} onClick={toggleDropdown}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 12C14.7614 12 17 9.76142 17 7C17 4.23858 14.7614 2 12 2C9.23858 2 7 4.23858 7 7C7 9.76142 9.23858 12 12 12Z" fill="currentColor"/>
                <path d="M12 14C7.58172 14 4 17.5817 4 22H20C20 17.5817 16 14 12 14Z" fill="currentColor"/>
              </svg>
            </div>
            {isDropdownOpen && (
              <div className={pdlSearchStyles.profileDropdown}>
                <div className={pdlSearchStyles.dropdownHeader}>
                  <div className={pdlSearchStyles.userInfo}>
                    <div className={pdlSearchStyles.userRole}>
                      {userData?.user_type === 'unit_nurse' ? 'Unit Nurse' : 
                       userData?.user_type === 'chief_nurse' ? 'Chief Nurse' : 
                       userData?.user_type === 'warden' ? 'Warden' : 
                       userData?.user_type === 'admin' ? 'System Admin' : 
                       userData?.user_type === 'jail_officer' ? 'Jail Officer' : 'User'}
                    </div>
                    <div className={pdlSearchStyles.userName}>
                      {userData ? 
                        `${userData.last_name ? userData.last_name.charAt(0).toUpperCase() + userData.last_name.slice(1).toLowerCase() : ''}, ${userData.first_name ? userData.first_name.charAt(0).toUpperCase() + userData.first_name.slice(1).toLowerCase() : ''} ${userData.middle_name ? userData.middle_name.charAt(0).toUpperCase() + userData.middle_name.slice(1).toLowerCase() : ''}`.trim() 
                        : 'User Name'}
                    </div>
                  </div>
                </div>
                <div className={pdlSearchStyles.dropdownDivider}></div>
                <button 
                  className={pdlSearchStyles.dropdownItem}
                  onClick={toggleDarkMode}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    {isDarkMode ? (
                      <path d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    ) : (
                      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    )}
                  </svg>
                  {isDarkMode ? 'Light Mode' : 'Dark Mode'}
                </button>
                <button className={pdlSearchStyles.dropdownItem} onClick={() => { setShowChangePasswordModal(true); setIsDropdownOpen(false); }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M7 11V7C7 5.67392 7.52678 4.40215 8.46447 3.46447C9.40215 2.52678 10.6739 2 12 2C13.3261 2 14.5979 2.52678 15.5355 3.46447C16.4732 4.40215 17 5.67392 17 7V11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Change Password
                </button>
                <button className={pdlSearchStyles.dropdownItem} onClick={handleLogout}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <polyline points="16,17 21,12 16,7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <line x1="21" y1="12" x2="9" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <div className={pdlSearchStyles.jbrSearchPage}>
        {/* Header with home button */}
        <div className={pdlSearchStyles.jbrHeader}>
          <button onClick={() => navigate('/')} className={pdlSearchStyles.jbrBackBtn}>← Back to Dashboard</button>
          <h1 className={pdlSearchStyles.jbrTitle}>Search PDL Health Records</h1>
        </div>

      {/* Search Form */}
      <div className={pdlSearchStyles.searchCard}>
        <div className={pdlSearchStyles.searchHeader}>
          <div className={pdlSearchStyles.searchHeaderIcon}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M21 21L16.514 16.506L21 21ZM19 10.5C19 15.194 15.194 19 10.5 19C5.806 19 2 15.194 2 10.5C2 5.806 5.806 2 10.5 2C15.194 2 19 5.806 19 10.5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div className={pdlSearchStyles.searchHeaderText}>
            <h2>Search PDL Health Records</h2>
            <p>Enter patient information to find health records</p>
          </div>
        </div>
          
        
        <form onSubmit={handleSearch} className={pdlSearchStyles.searchForm}>
          <div className={pdlSearchStyles.searchFormGrid}>
            <div className={pdlSearchStyles.inputGroup}>
              <div className={pdlSearchStyles.inputIcon}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div className={pdlSearchStyles.inputWrapper}>
                <label className={pdlSearchStyles.inputLabel}>Last Name</label>
                <input
                  type="text"
                  name="lastName"
                  value={searchData.lastName}
                  onChange={handleInputChange}
                  onKeyDown={lettersOnly}
                  className={pdlSearchStyles.searchInput}
                  placeholder="Enter last name"
                  autoComplete="off"
                />
              </div>
            </div>

            <div className={pdlSearchStyles.inputGroup}>
              <div className={pdlSearchStyles.inputIcon}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div className={pdlSearchStyles.inputWrapper}>
                <label className={pdlSearchStyles.inputLabel}>First Name</label>
                <input
                  type="text"
                  name="firstName"
                  value={searchData.firstName}
                  onChange={handleInputChange}
                  onKeyDown={lettersOnly}
                  className={pdlSearchStyles.searchInput}
                  placeholder="Enter first name"
                  autoComplete="off"
                />
              </div>
            </div>

            <div className={pdlSearchStyles.inputGroup}>
              <div className={pdlSearchStyles.inputIcon}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div className={pdlSearchStyles.inputWrapper}>
                <label className={pdlSearchStyles.inputLabel}>Middle Name</label>
                <input
                  type="text"
                  name="middleName"
                  value={searchData.middleName}
                  onChange={handleInputChange}
                  onKeyDown={lettersOnly}
                  className={pdlSearchStyles.searchInput}
                  placeholder="Enter middle name"
                  autoComplete="off"
                />
              </div>
            </div>

            <div className={pdlSearchStyles.inputGroup}>
              <div className={pdlSearchStyles.inputIcon}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M16 7C16 9.20914 14.2091 11 12 11C9.79086 11 8 9.20914 8 7C8 4.79086 9.79086 3 12 3C14.2091 3 16 4.79086 16 7Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M12 14C8.13401 14 5 17.134 5 21H19C19 17.134 15.866 14 12 14Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div className={pdlSearchStyles.inputWrapper}>
                <label className={pdlSearchStyles.inputLabel}>AKA/Nickname</label>
                <input
                  type="text"
                  name="akaNickname"
                  value={searchData.akaNickname}
                  onChange={handleInputChange}
                  onKeyDown={lettersOnly}
                  className={pdlSearchStyles.searchInput}
                  placeholder="Enter AKA/nickname"
                  autoComplete="off"
                />
              </div>
            </div>
          </div>

          <div className={pdlSearchStyles.searchActions}>
            <button type="submit" className={pdlSearchStyles.searchButton} disabled={isLoading}>
              {isLoading ? (
                <>
                  <div className={pdlSearchStyles.searchSpinner}></div>
                  Searching...
                </>
              ) : (
                <>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M21 21L16.514 16.506L21 21ZM19 10.5C19 15.194 15.194 19 10.5 19C5.806 19 2 15.194 2 10.5C2 5.806 5.806 2 10.5 2C15.194 2 19 5.806 19 10.5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Search Records
                </>
              )}
            </button>
            
            <button 
              type="button" 
              className={pdlSearchStyles.clearButton}
              onClick={() => setSearchData({ lastName: '', firstName: '', middleName: '', akaNickname: '' })}
            >
              Clear
            </button>
          </div>
        </form>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className={pdlSearchStyles.resultsSection}>
          <div className={pdlSearchStyles.resultsHeader}>
            <div className={pdlSearchStyles.resultsHeaderIcon}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 11H15M9 15H15M17 21H7C5.89543 21 5 20.1046 5 19V5C5 3.89543 5.89543 3 7 3H12.5858C12.851 3 13.1054 3.10536 13.2929 3.29289L19.7071 9.70711C19.8946 9.89464 20 10.149 20 10.4142V19C20 20.1046 19.1046 21 18 21H17Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div className={pdlSearchStyles.resultsHeaderText}>
              <h3>Searching...</h3>
              <p>Please wait while we search for health records</p>
            </div>
          </div>
          <div className={pdlSearchStyles.resultsContainer}>
            <div className={pdlSearchStyles.loadingState}>
              <div className={pdlSearchStyles.loadingSpinner}></div>
              <p>Searching health records...</p>
            </div>
          </div>
        </div>
      )}

      {/* Results Section */}
      {searchResults.length > 0 && (
        <div className={pdlSearchStyles.resultsSection}>
          <div className={pdlSearchStyles.resultsHeader}>
            <div className={pdlSearchStyles.resultsHeaderIcon}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 11H15M9 15H15M17 21H7C5.89543 21 5 20.1046 5 19V5C5 3.89543 5.89543 3 7 3H12.5858C12.851 3 13.1054 3.10536 13.2929 3.29289L19.7071 9.70711C19.8946 9.89464 20 10.149 20 10.4142V19C20 20.1046 19.1046 21 18 21H17Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div className={pdlSearchStyles.resultsHeaderText}>
              <h3>Health Records Found</h3>
              <p>Showing {filteredResults.length} of {searchResults.length} record(s)</p>
            </div>
            <div style={{ marginLeft: 'auto', minWidth: '220px' }}>
              <select
                className={pdlSearchStyles.searchInput}
                value={remarksFilter}
                onChange={(e) => setRemarksFilter(e.target.value)}
                style={{ marginTop: 0 }}
              >
                <option value="not_archived">Not Archived (No Remarks)</option>
                <option value="all">All (Archived + Not)</option>
                <option value="Release">Release</option>
                <option value="Transfer">Transfer</option>
                <option value="Death">Death</option>
              </select>
            </div>
          </div>

          <div className={pdlSearchStyles.resultsContainer}>
            <div className={pdlSearchStyles.resultsTableContainer}>
              <table className={pdlSearchStyles.resultsTable}>
                <thead>
                  <tr>
                    <th className={pdlSearchStyles.tableHeader}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        Current Cell
                      </div>
                    </th>
                    <th className={pdlSearchStyles.tableHeader} onClick={() => handleSort('lastName')} style={{ cursor: 'pointer', userSelect: 'none' }} title={getSortTooltip('lastName')}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        Last Name
                        {renderSortIndicator('lastName')}
                      </div>
                    </th>
                    <th className={pdlSearchStyles.tableHeader} onClick={() => handleSort('firstName')} style={{ cursor: 'pointer', userSelect: 'none' }} title={getSortTooltip('firstName')}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        First Name
                        {renderSortIndicator('firstName')}
                      </div>
                    </th>
                    <th className={pdlSearchStyles.tableHeader} onClick={() => handleSort('middleName')} style={{ cursor: 'pointer', userSelect: 'none' }} title={getSortTooltip('middleName')}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        Middle Name
                        {renderSortIndicator('middleName')}
                      </div>
                    </th>
                    <th className={pdlSearchStyles.tableHeader} onClick={() => handleSort('aka')} style={{ cursor: 'pointer', userSelect: 'none' }} title={getSortTooltip('aka')}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        AKA
                        {renderSortIndicator('aka')}
                      </div>
                    </th>
                    <th className={pdlSearchStyles.tableHeader} onClick={() => handleSort('age')} style={{ cursor: 'pointer', userSelect: 'none' }} title={getSortTooltip('age')}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        Age
                        {renderSortIndicator('age')}
                      </div>
                    </th>
                    <th className={pdlSearchStyles.tableHeader}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentPageData.map((record, index) => (
                    <tr key={record.id} className={pdlSearchStyles.tableRow}>
                      <td className={pdlSearchStyles.tableCell}>
                        <span className={`${pdlSearchStyles.cellBadge} ${record.cell ? pdlSearchStyles.cellBadgeAssigned : pdlSearchStyles.cellBadgeUnassigned}`}>
                          {record.cell?.cell_no || 'Unassigned'}
                        </span>
                      </td>
                      <td className={pdlSearchStyles.tableCell}>
                        <span className={pdlSearchStyles.cellText}>{capitalizeName(record.last_name)}</span>
                      </td>
                      <td className={pdlSearchStyles.tableCell}>
                        <span className={pdlSearchStyles.cellText}>{capitalizeName(record.first_name)}</span>
                      </td>
                      <td className={pdlSearchStyles.tableCell}>
                        <span className={pdlSearchStyles.cellText}>{capitalizeName(record.middle_name)}</span>
                      </td>
                      <td className={pdlSearchStyles.tableCell}>
                        <span className={pdlSearchStyles.cellText}>{capitalizeName(record.alias_nickname)}</span>
                      </td>
                      <td className={pdlSearchStyles.tableCell}>
                        <span className={pdlSearchStyles.cellText}>{calculateAge(record.date_of_birth)}</span>
                      </td>
                      <td className={pdlSearchStyles.tableCell}>
                        <div className={pdlSearchStyles.actionButtons}>
                          <button
                            className={`${pdlSearchStyles.actionButton} ${pdlSearchStyles.viewButton}`}
                            onClick={() => handleView(record)}
                            title="View Details"
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M1 12S5 4 12 4S23 12 23 12S19 20 12 20S1 12 1 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            View
                          </button>
                          <button
                            className={`${pdlSearchStyles.actionButton} ${pdlSearchStyles.updateButton}`}
                            onClick={() => handleUpdate(record)}
                            title="Update Record"
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 5.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              <path d="M18.5 2.50023C18.8978 2.1024 19.4374 1.87891 20 1.87891C20.5626 1.87891 21.1022 2.1024 21.5 2.50023C21.8978 2.89805 22.1213 3.43762 22.1213 4.00023C22.1213 4.56284 21.8978 5.1024 21.5 5.50023L12 15.0002L8 16.0002L9 12.0002L18.5 2.50023Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            Update
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className={pdlSearchStyles.paginationContainer}>
                <button
                  className={pdlSearchStyles.paginationButton}
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Previous
                </button>

                <div className={pdlSearchStyles.paginationInfo}>
                  <span className={pdlSearchStyles.paginationText}>
                    Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
                  </span>
                  <span className={pdlSearchStyles.paginationDivider}>•</span>
                  <span className={pdlSearchStyles.paginationText}>
                    Showing <strong>{startIndex + 1}</strong> to <strong>{Math.min(endIndex, filteredResults.length)}</strong> of <strong>{filteredResults.length}</strong> records
                  </span>
                </div>

                <button
                  className={pdlSearchStyles.paginationButton}
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                >
                  Next
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* No records after filter */}
      {!isLoading && searchResults.length > 0 && filteredResults.length === 0 && (
        <div className={pdlSearchStyles.emptyState}>
          <svg className={pdlSearchStyles.emptyStateIcon} width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M10 10L14 14M14 10L10 14M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <h4>No Records For Filter</h4>
          <p>No records match the selected final remarks filter option.</p>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && searchResults.length === 0 && (
        <div className={pdlSearchStyles.emptyState}>
          <div className={pdlSearchStyles.emptyStateIcon}>
            <FaUserMd size={48} />
          </div>
          <h4>No health records found</h4>
          <p>Try adjusting your search criteria or check if the patient information is correct.</p>
        </div>
      )}

      {/* View Modal */}
      {showViewModal && (
        <div className={`${modalStyles.jbrModalOverlay} ${isDarkMode ? '' : modalStyles.lightMode}`} onClick={closeModal}>
          <div className={`${modalStyles.jbrModalContent} ${isDarkMode ? '' : modalStyles.lightMode}`} onClick={(e) => e.stopPropagation()}>
            <div className={modalStyles.jbrModalHeader}>
              <div className={modalStyles.jbrHeaderLeft}>
                <div className={modalStyles.jbrLogo}>🏛️</div>
                <div className={modalStyles.jbrHeaderText}>
                  <h4 className={modalStyles.jbrGradientText}>Republic of the Philippines</h4>
                  <h5 className={modalStyles.jbrGradientText}>BUREAU OF JAIL MANAGEMENT AND PENOLOGY</h5>
                </div>
              </div>
              <div className={modalStyles.jbrHeaderActions}>
                {selectedRecord?.final_remarks && (
                  <button className={modalStyles.jbrCopyBtn} onClick={handleCopyRecord} title="Copy Record as New PDL">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M5 15H4C3.46957 15 2.96086 14.7893 2.58579 14.4142C2.21071 14.0391 2 13.5304 2 13V4C2 3.46957 2.21071 2.96086 2.58579 2.58579C2.96086 2.21071 3.46957 2 4 2H13C13.5304 2 14.0391 2.21071 14.4142 2.58579C14.7893 2.96086 15 3.46957 15 4V5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Copy
                  </button>
                )}
                <button className={modalStyles.jbrPdfBtn} onClick={handleExportToPDF} title="Save as PDF">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <polyline points="14,2 14,8 20,8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <line x1="16" y1="13" x2="8" y2="13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <line x1="16" y1="17" x2="8" y2="17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <polyline points="10,9 9,9 8,9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  PDF
                </button>
                <button className={modalStyles.jbrCloseBtn} onClick={closeModal}>&times;</button>
              </div>
            </div>

            <div className={modalStyles.jbrModalBody}>
              {isModalLoading ? (
                <div className={modalStyles.jbrLoadingContainer}>
                  <div className={modalStyles.jbrLoadingSpinner}></div>
                  <span className={modalStyles.jbrGradientText}>Loading...</span>
                </div>
              ) : selectedRecord && (
                <>
                  <h2 className={modalStyles.jbrFormTitle}>
                    <span className={modalStyles.jbrInteractiveIcon}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M9 11H15M9 15H15M17 21H7C5.89543 21 5 20.1046 5 19V5C5 3.89543 5.89543 3 7 3H12.5858C12.851 3 13.1054 3.10536 13.2929 3.29289L19.7071 9.70711C19.8946 9.89464 20 10.149 20 10.4142V19C20 20.1046 19.1046 21 18 21H17ZM17 21V10L13 6H7V19H17Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </span>
                    PDL Health Record
                  </h2>
                  
                  {/* Page Navigation */}
                  <div className={modalStyles.jbrPageNavigation}>
                    <button 
                      className={`${modalStyles.jbrPageBtn} ${currentPage === 4 ? modalStyles.active : ''}`}
                      onClick={() => setCurrentPage(4)}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <polyline points="14,2 14,8 20,8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      Front Form
                    </button>
                    <button 
                      className={`${modalStyles.jbrPageBtn} ${currentPage === 5 ? modalStyles.active : ''}`}
                      onClick={() => setCurrentPage(5)}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <polyline points="14,2 14,8 20,8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      Back Form
                    </button>
                  </div>

                  {/* Page 4 Content - Front Form */}
                  {currentPage === 4 && (
                    <>
                      <style>
                        {`
                          #front-form-container input[type="checkbox"] {
                            width: 16px;
                            height: 16px;
                            cursor: default;
                            appearance: none;
                            -webkit-appearance: none;
                            border: 2px solid #000;
                            border-radius: 2px;
                            position: relative;
                            background-color: #fff;
                          }
                          #front-form-container input[type="checkbox"]:checked {
                            background-color: #000;
                            border-color: #000;
                          }
                          #front-form-container input[type="checkbox"]:checked::after {
                            content: '';
                            position: absolute;
                            width: 4px;
                            height: 9px;
                            border: solid #fff;
                            border-width: 0 2px 2px 0;
                            top: 45%;
                            left: 50%;
                            transform: translate(-50%, -50%) rotate(45deg);
                          }
                        `}
                      </style>
                      <div id="front-form-container" style={{ 
                        maxWidth: '1100px', 
                        margin: '0 auto 1.5rem auto', 
                        background: '#ffffff', 
                        padding: '20px', 
                        fontFamily: 'Arial, sans-serif',
                        color: '#000000',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        border: '1px solid #ddd'
                      }}>
                        
                        {/* Header Section */}
                        <div style={{ 
                          display: 'flex',
                          alignItems: 'flex-start',
                          justifyContent: 'space-between',
                          marginBottom: '20px',
                          gap: '15px'
                        }}>
                          
                          {/* Logo and Header Text */}
                          <div style={{ display: 'flex', gap: '15px', alignItems: 'center', flex: 1 }}>
                            {/* Logo */}
                            <div style={{ 
                              width: '100px',
                              height: '100px',
                              border: '2px solid #000',
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              overflow: 'hidden',
                              background: '#fff',
                              flexShrink: 0
                            }}>
                              {!logoFailed ? (
                                <img 
                                  src="/BJMPRO-CAR.png" 
                                  alt="BJMP Logo" 
                                  style={{ width: '95px', height: '95px', objectFit: 'contain' }}
                                  onError={() => setLogoFailed(true)}
                                />
                              ) : (
                                <div style={{ fontSize: '10px', textAlign: 'center', fontWeight: 'bold', lineHeight: 1.2 }}>BJMP<br/>LOGO</div>
                              )}
                            </div>
                            
                            {/* Header Text */}
                            <div style={{ paddingTop: '15px' }}>
                              <div style={{ fontSize: '12px', fontWeight: 'normal', marginBottom: '2px', letterSpacing: '0.5px' }}>
                                REPUBLIC OF THE PHILIPPINES
                              </div>
                              <div style={{ fontSize: '12px', fontWeight: 'normal', marginBottom: '2px', letterSpacing: '0.5px' }}>
                                DEPARTMENT OF INTERIOR AND LOCAL GOVERNMENT
                              </div>
                              <div style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '0', letterSpacing: '0.5px' }}>
                                BUREAU OF JAIL MANAGEMENT AND PENOLOGY
                              </div>
                            </div>
                          </div>
                          
                          {/* Right Side - Form No and Final Remarks */}
                          <div style={{ textAlign: 'right', paddingTop: '10px', minWidth: '300px', flexShrink: 0 }}>
                            <div style={{ fontSize: '14px', fontWeight: 'normal', marginBottom: '25px' }}>
                              Form No. <span style={{ borderBottom: '1px solid #000', display: 'inline-block', width: '100px', marginLeft: '5px' }}></span>
                            </div>
                            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', alignItems: 'center', fontSize: '12px' }}>
                              <span style={{ marginRight: '5px' }}>Final Remarks:</span>
                              <label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                <input 
                                  type="checkbox" 
                                  checked={selectedRecord?.final_remarks === 'Release'} 
                                  disabled 
                                  readOnly
                                  style={{ width: '15px', height: '15px', cursor: 'default' }} 
                                />
                                <span>Release</span>
                              </label>
                              <label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                <input 
                                  type="checkbox" 
                                  checked={selectedRecord?.final_remarks === 'Transfer'} 
                                  disabled 
                                  readOnly
                                  style={{ width: '15px', height: '15px', cursor: 'default' }} 
                                />
                                <span>Transfer</span>
                              </label>
                              <label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                <input 
                                  type="checkbox" 
                                  checked={selectedRecord?.final_remarks === 'Death'} 
                                  disabled 
                                  readOnly
                                  style={{ width: '15px', height: '15px', cursor: 'default' }} 
                                />
                                <span>Death</span>
                              </label>
                            </div>
                          </div>
                        </div>

                        {/* Black Header Bar */}
                        <div style={{ 
                          background: '#000', 
                          color: '#fff', 
                          padding: '6px 12px',
                          marginBottom: '0',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}>
                          <div style={{ fontSize: '14px', fontWeight: 'bold', letterSpacing: '0.5px' }}>
                            PDL HEALTH RECORD
                          </div>
                          <div style={{ fontSize: '11px', fontStyle: 'italic', letterSpacing: '0.3px' }}>
                            CONFIDENTIAL - Authorized Health Personnel only.
                          </div>
                        </div>

                        {/* Form Table */}
                        <table style={{ 
                          width: '100%', 
                          borderCollapse: 'collapse',
                          border: '1px solid #000',
                          fontSize: '11px'
                        }}>
                          <tbody>
                            {/* Name Row */}
                            <tr>
                              <td colSpan="7" style={{ 
                                border: '1px solid #000', 
                                padding: '8px 10px',
                                position: 'relative'
                              }}>
                                <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                                  <strong style={{ minWidth: '45px' }}>Name:</strong>
                                  <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 0.7fr', gap: '15px' }}>
                                    <div style={{ textAlign: 'center' }}>
                                      <div style={{ borderBottom: '1px solid #000', minHeight: '20px', marginBottom: '3px' }}>
                                        {selectedRecord?.last_name || ''}
                                      </div>
                                      <div style={{ fontSize: '9px', color: '#666' }}>Surname</div>
                                    </div>
                                    <div style={{ textAlign: 'center' }}>
                                      <div style={{ borderBottom: '1px solid #000', minHeight: '20px', marginBottom: '3px' }}>
                                        {selectedRecord?.first_name || ''}
                                      </div>
                                      <div style={{ fontSize: '9px', color: '#666' }}>First Name</div>
                                    </div>
                                    <div style={{ textAlign: 'center' }}>
                                      <div style={{ borderBottom: '1px solid #000', minHeight: '20px', marginBottom: '3px' }}>
                                        {selectedRecord?.middle_name || ''}
                                      </div>
                                      <div style={{ fontSize: '9px', color: '#666' }}>Middle Name</div>
                                    </div>
                                    <div style={{ textAlign: 'center' }}>
                                      <div style={{ borderBottom: '1px solid #000', minHeight: '20px', marginBottom: '3px' }}>
                                        {selectedRecord?.alias_nickname || ''}
                                      </div>
                                      <div style={{ fontSize: '9px', color: '#666' }}>A.K.A.</div>
                                    </div>
                                  </div>
                                </div>
                              </td>
                              <td rowSpan="5" style={{ 
                                border: '1px solid #000', 
                                padding: '10px',
                                verticalAlign: 'middle',
                                textAlign: 'center',
                                width: '170px',
                                background: '#f9f9f9'
                              }}>
                                <div style={{ fontSize: '9px', fontStyle: 'italic', color: '#666', marginBottom: '8px' }}>
                                  (photo of PDL)
                                </div>
                                <div style={{ 
                                  width: '140px', 
                                  height: '140px', 
                                  border: '1px solid #ccc',
                                  margin: '0 auto',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  background: '#fff',
                                  overflow: 'hidden'
                                }}>
                                  {selectedRecord?.photo_of_pdl ? (
                                    <img 
                                      src={`${API_BASE_URL}/api/storage/${selectedRecord.photo_of_pdl}`}
                                      alt="PDL"
                                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                      onError={(e) => { e.target.onerror = null; e.target.src = '/default-image.png'; }}
                                    />
                                  ) : (
                                    <img 
                                      src="/default-image.png"
                                      alt="No PDL available"
                                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                    />
                                  )}
                                </div>
                              </td>
                            </tr>
                            
                            {/* Sex, Age, Civil Status, Educational Attainment Row */}
                            <tr>
                              <td style={{ 
                                border: '1px solid #000', 
                                padding: '8px 10px',
                                width: '15%'
                              }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <strong>Sex:</strong>
                                  <label style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                                    <input type="checkbox" checked={selectedRecord?.sex === 'M'} disabled readOnly style={{ width: '14px', height: '14px', margin: '0' }} />
                                    <span>M</span>
                                  </label>
                                  <label style={{ display: 'flex', alignItems: 'center', gap: '3px', marginLeft: '5px' }}>
                                    <input type="checkbox" checked={selectedRecord?.sex === 'F'} disabled readOnly style={{ width: '14px', height: '14px', margin: '0' }} />
                                    <span>F</span>
                                  </label>
                                </div>
                              </td>
                              <td style={{ 
                                border: '1px solid #000', 
                                padding: '8px 10px',
                                width: '10%'
                              }}>
                                <div>
                                  <div style={{ marginBottom: '3px' }}>
                                    <strong>Age:</strong> {selectedRecord ? calculateAge(selectedRecord.date_of_birth) : ''}
                                  </div>
                                </div>
                              </td>
                              <td style={{ 
                                border: '1px solid #000', 
                                padding: '8px 10px',
                                width: '25%'
                              }}>
                                <div>
                                  <div style={{ marginBottom: '3px' }}>
                                    <strong>Civil status:</strong> {selectedRecord?.civil_status || ''}
                                  </div>
                                  <div style={{ fontSize: '9px', color: '#666' }}>(single/married/widow/others, specify)</div>
                                </div>
                              </td>
                              <td colSpan="4" style={{ 
                                border: '1px solid #000', 
                                padding: '8px 10px'
                              }}>
                                <div>
                                  <div style={{ marginBottom: '3px' }}>
                                    <strong>Educational attainment:</strong> {selectedRecord?.educ_attainment || ''}
                                  </div>
                                  <div style={{ fontSize: '9px', color: '#666' }}>(grade/year/degree)</div>
                                </div>
                              </td>
                            </tr>

                            {/* Date of Birth, Place of Birth Row */}
                            <tr>
                              <td colSpan="2" style={{ 
                                border: '1px solid #000', 
                                padding: '8px 10px',
                                verticalAlign: 'middle'
                              }}>
                                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                  <strong style={{ fontSize: '10px', whiteSpace: 'nowrap' }}>Date of birth:</strong>
                                  <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                                    <div style={{ textAlign: 'center' }}>
                                      <div style={{ borderBottom: '1px solid #000', minHeight: '18px', minWidth: '25px', fontSize: '10px', marginBottom: '2px' }}>
                                        {selectedRecord?.date_of_birth ? String(new Date(selectedRecord.date_of_birth).getMonth() + 1).padStart(2, '0') : ''}
                                      </div>
                                      <div style={{ fontSize: '8px', color: '#666' }}>MM</div>
                                    </div>
                                    <span style={{ fontSize: '10px', paddingBottom: '12px' }}>/</span>
                                    <div style={{ textAlign: 'center' }}>
                                      <div style={{ borderBottom: '1px solid #000', minHeight: '18px', minWidth: '25px', fontSize: '10px', marginBottom: '2px' }}>
                                        {selectedRecord?.date_of_birth ? String(new Date(selectedRecord.date_of_birth).getDate()).padStart(2, '0') : ''}
                                      </div>
                                      <div style={{ fontSize: '8px', color: '#666' }}>DD</div>
                                    </div>
                                    <span style={{ fontSize: '10px', paddingBottom: '12px' }}>/</span>
                                    <div style={{ textAlign: 'center' }}>
                                      <div style={{ borderBottom: '1px solid #000', minHeight: '18px', minWidth: '35px', fontSize: '10px', marginBottom: '2px' }}>
                                        {selectedRecord?.date_of_birth ? new Date(selectedRecord.date_of_birth).getFullYear() : ''}
                                      </div>
                                      <div style={{ fontSize: '8px', color: '#666' }}>YY</div>
                                    </div>
                                  </div>
                                </div>
                              </td>
                              <td colSpan="5" style={{ 
                                border: '1px solid #000', 
                                padding: '8px 10px',
                                verticalAlign: 'middle'
                              }}>
                                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                  <strong style={{ whiteSpace: 'nowrap', fontSize: '11px' }}>Place of birth:</strong>
                                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '8px', flex: 1 }}>
                                    <div style={{ textAlign: 'center' }}>
                                      <div style={{ borderBottom: '1px solid #000', minHeight: '18px', marginBottom: '2px', fontSize: '10px' }}>
                                        {selectedRecord?.pob_municipality || ''}
                                      </div>
                                      <div style={{ fontSize: '8px', color: '#666' }}>Municipality/City</div>
                                    </div>
                                    <div style={{ textAlign: 'center' }}>
                                      <div style={{ borderBottom: '1px solid #000', minHeight: '18px', marginBottom: '2px', fontSize: '10px' }}>
                                        {selectedRecord?.pob_province || ''}
                                      </div>
                                      <div style={{ fontSize: '8px', color: '#666' }}>Province</div>
                                    </div>
                                    <div style={{ textAlign: 'center' }}>
                                      <div style={{ borderBottom: '1px solid #000', minHeight: '18px', marginBottom: '2px', fontSize: '10px' }}>
                                        {selectedRecord?.pob_region || ''}
                                      </div>
                                      <div style={{ fontSize: '8px', color: '#666' }}>Region</div>
                                    </div>
                                    <div style={{ textAlign: 'center' }}>
                                      <div style={{ borderBottom: '1px solid #000', minHeight: '18px', marginBottom: '2px', fontSize: '10px' }}>
                                        {selectedRecord?.pob_country || ''}
                                      </div>
                                      <div style={{ fontSize: '8px', color: '#666' }}>Country</div>
                                    </div>
                                  </div>
                                </div>
                              </td>
                            </tr>

                            {/* Date of Commitment, Name of Jail Row */}
                            <tr>
                              <td colSpan="2" style={{ 
                                border: '1px solid #000', 
                                padding: '8px 10px',
                                verticalAlign: 'middle'
                              }}>
                                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                  <strong style={{ fontSize: '10px', whiteSpace: 'nowrap' }}>Date of commitment:</strong>
                                  <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                                    <div style={{ textAlign: 'center' }}>
                                      <div style={{ borderBottom: '1px solid #000', minHeight: '18px', minWidth: '25px', fontSize: '10px', marginBottom: '2px' }}>
                                        {selectedRecord?.date_of_commitment ? String(new Date(selectedRecord.date_of_commitment).getMonth() + 1).padStart(2, '0') : ''}
                                      </div>
                                      <div style={{ fontSize: '8px', color: '#666' }}>MM</div>
                                    </div>
                                    <span style={{ fontSize: '10px', paddingBottom: '12px' }}>/</span>
                                    <div style={{ textAlign: 'center' }}>
                                      <div style={{ borderBottom: '1px solid #000', minHeight: '18px', minWidth: '25px', fontSize: '10px', marginBottom: '2px' }}>
                                        {selectedRecord?.date_of_commitment ? String(new Date(selectedRecord.date_of_commitment).getDate()).padStart(2, '0') : ''}
                                      </div>
                                      <div style={{ fontSize: '8px', color: '#666' }}>DD</div>
                                    </div>
                                    <span style={{ fontSize: '10px', paddingBottom: '12px' }}>/</span>
                                    <div style={{ textAlign: 'center' }}>
                                      <div style={{ borderBottom: '1px solid #000', minHeight: '18px', minWidth: '35px', fontSize: '10px', marginBottom: '2px' }}>
                                        {selectedRecord?.date_of_commitment ? new Date(selectedRecord.date_of_commitment).getFullYear() : ''}
                                      </div>
                                      <div style={{ fontSize: '8px', color: '#666' }}>YY</div>
                                    </div>
                                  </div>
                                </div>
                              </td>
                              <td colSpan="5" style={{ 
                                border: '1px solid #000', 
                                padding: '8px 10px'
                              }}>
                                <div>
                                  <strong>Name of jail:</strong> {selectedRecord?.name_of_jail || ''}
                                </div>
                              </td>
                            </tr>

                            {/* Origin Lock-up, PhilHealth No Row */}
                            <tr>
                              <td colSpan="2" style={{ 
                                border: '1px solid #000', 
                                padding: '8px 10px'
                              }}>
                                <div>
                                  <strong>Origin lock-up:</strong> {selectedRecord?.origin_lock_up || ''}
                                </div>
                              </td>
                              <td colSpan="6" style={{ 
                                border: '1px solid #000', 
                                padding: '8px 10px'
                              }}>
                                <div>
                                  <strong>PhilHealth No:</strong> {selectedRecord?.philhealth_no || ''}
                                </div>
                              </td>
                            </tr>

                            {/* Case, Case No Row */}
                            <tr>
                              <td colSpan="4" style={{ 
                                border: '1px solid #000', 
                                padding: '8px 10px'
                              }}>
                                <div>
                                  <strong>Case:</strong> {selectedRecord?.case_ || ''}
                                </div>
                              </td>
                              <td colSpan="4" style={{ 
                                border: '1px solid #000', 
                                padding: '8px 10px'
                              }}>
                                <div>
                                  <strong>Case No:</strong> {selectedRecord?.case_no || ''}
                                </div>
                              </td>
                            </tr>

                            {/* Name of contact person, Relationship Row */}
                            <tr>
                              <td colSpan="4" style={{ 
                                border: '1px solid #000', 
                                padding: '8px 10px'
                              }}>
                                <div>
                                  <strong>Name of contact person:</strong> {selectedRecord?.name_of_contact_person || ''}
                                </div>
                              </td>
                              <td colSpan="4" style={{ 
                                border: '1px solid #000', 
                                padding: '8px 10px'
                              }}>
                                <div>
                                  <strong>Relationship:</strong> {selectedRecord?.relationship || ''}
                                </div>
                              </td>
                            </tr>

                            {/* Address of contact person, Phone/Email Row */}
                            <tr>
                              <td colSpan="4" style={{ 
                                border: '1px solid #000', 
                                padding: '8px 10px'
                              }}>
                                <div>
                                  <strong>Address of contact person:</strong> {selectedRecord?.add_of_contact_person || ''}
                                </div>
                              </td>
                              <td colSpan="4" style={{ 
                                border: '1px solid #000', 
                                padding: '8px 10px'
                              }}>
                                <div>
                                  <strong>Phone / Email:</strong> {selectedRecord?.phone_email || ''}
                                </div>
                              </td>
                            </tr>

                            {/* Section Header: A. INITIAL HEALTH ASSESSMENT */}
                            <tr>
                              <td colSpan="8" style={{ 
                                border: '1px solid #000', 
                                padding: '10px 12px',
                                background: '#fff',
                                borderTop: '3px solid #000'
                              }}>
                                <div style={{ fontSize: '13px', fontWeight: 'bold' }}>
                                  A. INITIAL HEALTH ASSESSMENT
                                </div>
                              </td>
                            </tr>

                            {/* Present Health Condition & PAST MEDICAL HISTORY Row */}
                            <tr>
                              <td colSpan="7" style={{ 
                                border: '1px solid #000', 
                                padding: '8px 10px',
                                verticalAlign: 'top'
                              }}>
                                <div style={{ marginBottom: '10px' }}>
                                  <strong>Present Health Condition:</strong> {(selectedRecord?.hr_present_health_conditions || []).map(c => c.present_health_condition).join(', ') || ''}
                                </div>
                                <div>
                                  <strong>PAST MEDICAL HISTORY</strong> <span style={{ fontSize: '10px', fontStyle: 'italic' }}>(Please enumerate/document as applicable):</span>
                                </div>
                              </td>
                              <td style={{ 
                                border: '1px solid #000', 
                                padding: '8px 10px',
                                verticalAlign: 'top'
                              }}>
                                <div>
                                  <strong>Blood type:</strong> {selectedRecord?.hr_a_medical_history?.blood_type || ''}
                                </div>
                              </td>
                            </tr>

                            {/* Pediatric History Row */}
                            <tr>
                              <td colSpan="7" style={{ 
                                border: '1px solid #000', 
                                padding: '8px 10px',
                                verticalAlign: 'top',
                                minHeight: '45px'
                              }}>
                                <div>
                                  Pediatric History / Significant Childhood Illnesses: {selectedRecord?.hr_a_medical_history?.pediatric_history || ''}
                                </div>
                              </td>
                              <td rowSpan="2" style={{ 
                                border: '1px solid #000', 
                                padding: '8px 10px',
                                verticalAlign: 'top'
                              }}>
                                <div>
                                  <strong>Allergies:</strong> {selectedRecord?.hr_a_medical_history?.allergies || ''}
                                </div>
                              </td>
                            </tr>

                            {/* Major Adult Illnesses Row */}
                            <tr>
                              <td colSpan="7" style={{ 
                                border: '1px solid #000', 
                                padding: '8px 10px',
                                verticalAlign: 'top',
                                minHeight: '45px'
                              }}>
                                <div>
                                  Major Adult Illnesses: {selectedRecord?.hr_a_medical_history?.major_adult_illnesses || ''}
                                </div>
                              </td>
                            </tr>

                            {/* Major Surgeries Row */}
                            <tr>
                              <td colSpan="7" style={{ 
                                border: '1px solid #000', 
                                padding: '8px 10px',
                                verticalAlign: 'top',
                                minHeight: '45px'
                              }}>
                                <div>
                                  Major Surgeries / Previous Hospitalization: {selectedRecord?.hr_a_medical_history?.major_surgeries || ''}
                                </div>
                              </td>
                              <td rowSpan="6" style={{ 
                                border: '1px solid #000', 
                                padding: '8px 10px',
                                verticalAlign: 'top'
                              }}>
                                <div style={{ marginBottom: '8px' }}>
                                  <strong>Family History</strong> <span style={{ fontSize: '10px', fontStyle: 'italic' }}>(up to 1st degree):</span>
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <input type="checkbox" checked={selectedRecord?.hr_a_medical_history?.fm_hypertension === 1} disabled readOnly style={{ width: '14px', height: '14px' }} />
                                    <span style={{ fontSize: '11px' }}>Hypertension</span>
                                  </label>
                                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <input type="checkbox" checked={selectedRecord?.hr_a_medical_history?.fm_stroke === 1} disabled readOnly style={{ width: '14px', height: '14px' }} />
                                    <span style={{ fontSize: '11px' }}>Stroke</span>
                                  </label>
                                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <input type="checkbox" checked={selectedRecord?.hr_a_medical_history?.fm_heartAttack === 1} disabled readOnly style={{ width: '14px', height: '14px' }} />
                                    <span style={{ fontSize: '11px' }}>Heart attack</span>
                                  </label>
                                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <input type="checkbox" checked={selectedRecord?.hr_a_medical_history?.fm_diabetes === 1} disabled readOnly style={{ width: '14px', height: '14px' }} />
                                    <span style={{ fontSize: '11px' }}>Diabetes</span>
                                  </label>
                                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <input type="checkbox" checked={selectedRecord?.hr_a_medical_history?.fm_asthma === 1} disabled readOnly style={{ width: '14px', height: '14px' }} />
                                    <span style={{ fontSize: '11px' }}>Asthma</span>
                                  </label>
                                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <input type="checkbox" checked={selectedRecord?.hr_a_medical_history?.fm_kidneyDisease === 1} disabled readOnly style={{ width: '14px', height: '14px' }} />
                                    <span style={{ fontSize: '11px' }}>Kidney disease</span>
                                  </label>
                                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <input type="checkbox" checked={selectedRecord?.hr_a_medical_history?.fm_cancer === 1} disabled readOnly style={{ width: '14px', height: '14px' }} />
                                    <span style={{ fontSize: '11px' }}>Cancer</span>
                                  </label>
                                </div>
                                <div style={{ marginTop: '8px', fontSize: '11px' }}>
                                  <strong>Others:</strong> {selectedRecord?.hr_a_medical_history?.fm_others || ''}
                                </div>
                              </td>
                            </tr>

                            {/* Serious Physical Injuries Row */}
                            <tr>
                              <td colSpan="7" style={{ 
                                border: '1px solid #000', 
                                padding: '8px 10px',
                                verticalAlign: 'top',
                                minHeight: '45px'
                              }}>
                                <div>
                                  Serious Physical Injuries: {selectedRecord?.hr_a_medical_history?.serious_physical_injuries || ''}
                                </div>
                              </td>
                            </tr>

                            {/* Limitations in Range of Motion Row */}
                            <tr>
                              <td colSpan="7" style={{ 
                                border: '1px solid #000', 
                                padding: '8px 10px',
                                verticalAlign: 'top',
                                minHeight: '45px'
                              }}>
                                <div>
                                  Limitations in Range of Motion & Activities: {selectedRecord?.hr_a_medical_history?.limit_in_range_of_motion || ''}
                                </div>
                              </td>
                            </tr>

                            {/* Medications History Row */}
                            <tr>
                              <td colSpan="7" style={{ 
                                border: '1px solid #000', 
                                padding: '8px 10px',
                                verticalAlign: 'top',
                                minHeight: '45px'
                              }}>
                                <div>
                                  Medications History: {selectedRecord?.hr_a_medical_history?.medications_history || ''}
                                </div>
                              </td>
                            </tr>

                            {/* History of Transfusions Row */}
                            <tr>
                              <td colSpan="7" style={{ 
                                border: '1px solid #000', 
                                padding: '8px 10px',
                                verticalAlign: 'top',
                                minHeight: '45px'
                              }}>
                                <div>
                                  History of Transfusions / BT reactions: {selectedRecord?.hr_a_medical_history?.history_of_transfusions || ''}
                                </div>
                              </td>
                            </tr>

                            {/* Mental & Emotional Problems Row */}
                            <tr>
                              <td colSpan="7" style={{ 
                                border: '1px solid #000', 
                                padding: '8px 10px',
                                verticalAlign: 'top',
                                minHeight: '45px'
                              }}>
                                <div>
                                  Mental & Emotional Problems: {selectedRecord?.hr_a_medical_history?.mental_emotional_problems || ''}
                                </div>
                              </td>
                            </tr>

                            {/* Past psychiatric history Row */}
                            <tr>
                              <td colSpan="8" style={{ 
                                borderLeft: '1px solid #000',
                                borderRight: '1px solid #000',
                                padding: '4px 10px'
                              }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                                  <strong>Past psychiatric history:</strong>
                                  <span>Consultation with Psychiatrist</span>
                                  <label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                    <input type="checkbox" checked={selectedRecord?.hr_a_psychiatric_history?.consulted_psychiatrist === 1 || selectedRecord?.hr_a_psychiatric_history?.consulted_psychiatrist === '1' || selectedRecord?.hr_a_psychiatric_history?.consulted_psychiatrist === 'Yes'} disabled readOnly style={{ width: '14px', height: '14px' }} />
                                    <span>Yes</span>
                                  </label>
                                  <label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                    <input type="checkbox" checked={selectedRecord?.hr_a_psychiatric_history?.consulted_psychiatrist === 0 || selectedRecord?.hr_a_psychiatric_history?.consulted_psychiatrist === '0' || selectedRecord?.hr_a_psychiatric_history?.consulted_psychiatrist === 'No'} disabled readOnly style={{ width: '14px', height: '14px' }} />
                                    <span>No</span>
                                  </label>
                                  <span style={{ marginLeft: '20px' }}>If yes, when? {selectedRecord?.hr_a_psychiatric_history?.consultation_date ? formatDate(selectedRecord.hr_a_psychiatric_history.consultation_date) : '_______________'}</span>
                                </div>
                              </td>
                            </tr>

                            {/* Confinement in psychiatric facility Row */}
                            <tr>
                              <td colSpan="8" style={{ 
                                borderLeft: '1px solid #000',
                                borderRight: '1px solid #000',
                                padding: '4px 10px'
                              }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                                  <strong>Confinement in a psychiatric facility:</strong>
                                  <label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                    <input type="checkbox" checked={selectedRecord?.hr_a_psychiatric_history?.confined === 1 || selectedRecord?.hr_a_psychiatric_history?.confined === '1' || selectedRecord?.hr_a_psychiatric_history?.confined === 'Yes'} disabled readOnly style={{ width: '14px', height: '14px' }} />
                                    <span>Yes</span>
                                  </label>
                                  <label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                    <input type="checkbox" checked={selectedRecord?.hr_a_psychiatric_history?.confined === 0 || selectedRecord?.hr_a_psychiatric_history?.confined === '0' || selectedRecord?.hr_a_psychiatric_history?.confined === 'No'} disabled readOnly style={{ width: '14px', height: '14px' }} />
                                    <span>No</span>
                                  </label>
                                  <span style={{ marginLeft: '20px' }}>If yes, when? {selectedRecord?.hr_a_psychiatric_history?.confinement_date ? formatDate(selectedRecord.hr_a_psychiatric_history.confinement_date) : '___________'}</span>
                                  <span style={{ marginLeft: '10px' }}>Where? {selectedRecord?.hr_a_psychiatric_history?.confinement_place || '_______________'}</span>
                                </div>
                              </td>
                            </tr>

                            {/* Diagnosis & Medication Row */}
                            <tr>
                              <td colSpan="8" style={{ 
                                borderLeft: '1px solid #000',
                                borderRight: '1px solid #000',
                                padding: '4px 10px'
                              }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                                  <strong>Diagnosis:</strong>
                                  <span>{selectedRecord?.hr_a_psychiatric_history?.diagnosis || '_______________________________________________'}</span>
                                  <strong style={{ marginLeft: '30px' }}>Medication taken:</strong>
                                  <span>{selectedRecord?.hr_a_psychiatric_history?.medication || '_______________________________________________'}</span>
                                </div>
                              </td>
                            </tr>

                            {/* ARV treatment Row */}
                            <tr>
                              <td colSpan="8" style={{ 
                                borderLeft: '1px solid #000',
                                borderRight: '1px solid #000',
                                padding: '4px 10px'
                              }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                                  <strong>ARV treatment:</strong>
                                  <label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                    <input type="checkbox" checked={selectedRecord?.hr_a_psychiatric_history?.arv_treatment === 1 || selectedRecord?.hr_a_psychiatric_history?.arv_treatment === '1' || selectedRecord?.hr_a_psychiatric_history?.arv_treatment === 'Yes'} disabled readOnly style={{ width: '14px', height: '14px' }} />
                                    <span>Yes</span>
                                  </label>
                                  <label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                    <input type="checkbox" checked={selectedRecord?.hr_a_psychiatric_history?.arv_treatment === 0 || selectedRecord?.hr_a_psychiatric_history?.arv_treatment === '0' || selectedRecord?.hr_a_psychiatric_history?.arv_treatment === 'No'} disabled readOnly style={{ width: '14px', height: '14px' }} />
                                    <span>No</span>
                                  </label>
                                  <span style={{ marginLeft: '20px' }}>If yes, from (MM/DD/YY) {selectedRecord?.hr_a_psychiatric_history?.arv_start ? formatDate(selectedRecord.hr_a_psychiatric_history.arv_start) : '____/____/____'}</span>
                                  <strong style={{ marginLeft: '15px' }}>Place:</strong>
                                  <span>{selectedRecord?.hr_a_psychiatric_history?.arv_place || '_______________________________________________'}</span>
                                </div>
                              </td>
                            </tr>

                            {/* OB/GYNE history Row */}
                            <tr>
                              <td colSpan="8" style={{ 
                                borderLeft: '1px solid #000',
                                borderRight: '1px solid #000',
                                padding: '4px 10px'
                              }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                                  <strong>OB/GYNE history:</strong>
                                  <label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                    <input type="checkbox" checked={selectedRecord?.hr_a_psychiatric_history?.obgyne === 1 || selectedRecord?.hr_a_psychiatric_history?.obgyne === '1' || selectedRecord?.hr_a_psychiatric_history?.obgyne === 'Yes'} disabled readOnly style={{ width: '14px', height: '14px' }} />
                                    <span>Yes</span>
                                  </label>
                                  <label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                    <input type="checkbox" checked={selectedRecord?.hr_a_psychiatric_history?.obgyne === 0 || selectedRecord?.hr_a_psychiatric_history?.obgyne === '0' || selectedRecord?.hr_a_psychiatric_history?.obgyne === 'No'} disabled readOnly style={{ width: '14px', height: '14px' }} />
                                    <span>No</span>
                                  </label>
                                  <span style={{ marginLeft: '20px' }}>LMP (MM/DD/YY)</span>
                                  <span style={{ borderBottom: '1px solid #000', minWidth: '150px', textAlign: 'center' }}>{selectedRecord?.hr_a_psychiatric_history?.lmp ? formatDate(selectedRecord.hr_a_psychiatric_history.lmp) : '_______________'}</span>
                                  <span style={{ marginLeft: '10px' }}>G</span>
                                  <span style={{ borderBottom: '1px solid #000', minWidth: '60px', textAlign: 'center' }}>{selectedRecord?.hr_a_psychiatric_history?.g_gravida || '______'}</span>
                                  <span style={{ marginLeft: '5px' }}>P</span>
                                  <span style={{ borderBottom: '1px solid #000', minWidth: '60px', textAlign: 'center' }}>{selectedRecord?.hr_a_psychiatric_history?.p_para || '______'}</span>
                                </div>
                              </td>
                            </tr>

                            {/* Vaccines Row */}
                            <tr>
                              <td colSpan="8" style={{ 
                                borderLeft: '1px solid #000',
                                borderRight: '1px solid #000',
                                padding: '4px 10px'
                              }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                  <strong>Vaccines:</strong>
                                  <span style={{ fontSize: '10px', fontStyle: 'italic' }}>(if known)</span>
                                  <span>{selectedRecord?.hr_a_psychiatric_history?.vaccines || '_________________________________________________________________'}</span>
                                </div>
                              </td>
                            </tr>

                            {/* ALCOHOL HISTORY Header */}
                            <tr>
                              <td colSpan="8" style={{ 
                                border: '1px solid #000',
                                borderBottom: 'none',
                                padding: '6px 10px'
                              }}>
                                <div style={{ textDecoration: 'underline' }}>
                                  <strong>ALCOHOL HISTORY</strong>
                                </div>
                              </td>
                            </tr>

                            {/* Alcohol drinker Row */}
                            <tr>
                              <td colSpan="8" style={{ 
                                borderLeft: '1px solid #000',
                                borderRight: '1px solid #000',
                                padding: '4px 10px'
                              }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                                  <strong>Alcohol drinker:</strong>
                                  <label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                    <input type="checkbox" checked={selectedRecord?.hr_a_drugs_history?.alcohol_drinker === 1 || selectedRecord?.hr_a_drugs_history?.alcohol_drinker === '1' || selectedRecord?.hr_a_drugs_history?.alcohol_drinker === 'Yes'} disabled readOnly style={{ width: '14px', height: '14px' }} />
                                    <span>Yes</span>
                                  </label>
                                  <label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                    <input type="checkbox" checked={selectedRecord?.hr_a_drugs_history?.alcohol_drinker === 0 || selectedRecord?.hr_a_drugs_history?.alcohol_drinker === '0' || selectedRecord?.hr_a_drugs_history?.alcohol_drinker === 'No'} disabled readOnly style={{ width: '14px', height: '14px' }} />
                                    <span>No</span>
                                  </label>
                                  <span style={{ marginLeft: '20px' }}>Age at onset:</span>
                                  <span style={{ borderBottom: '1px solid #000', minWidth: '60px', textAlign: 'center' }}>{selectedRecord?.hr_a_drugs_history?.alcohol_age_at_onset || '______'}</span>
                                  <span style={{ marginLeft: '20px' }}>Frequency of use per week:</span>
                                  <span style={{ borderBottom: '1px solid #000', minWidth: '60px', textAlign: 'center' }}>{selectedRecord?.hr_a_drugs_history?.use_per_week || '______'}</span>
                                  <span style={{ marginLeft: '20px' }}>Type of drinks containing alcohol:</span>
                                  <span style={{ borderBottom: '1px solid #000', minWidth: '100px', textAlign: 'center' }}>{selectedRecord?.hr_a_drugs_history?.type_of_drinks || '________'}</span>
                                </div>
                              </td>
                            </tr>

                            {/* Approximate volume Row */}
                            <tr>
                              <td colSpan="8" style={{ 
                                borderLeft: '1px solid #000',
                                borderRight: '1px solid #000',
                                borderBottom: '1px solid #000',
                                padding: '4px 10px'
                              }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                  <strong>Approximate volume of alcohol consumed:</strong>
                                  <span style={{ borderBottom: '1px solid #000', minWidth: '150px', textAlign: 'center' }}>{selectedRecord?.hr_a_drugs_history?.volume_of_alcohol || '_______________'}</span>
                                  <span style={{ fontSize: '10px', fontStyle: 'italic' }}>(bottle, glass, shots, etc.)</span>
                                </div>
                              </td>
                            </tr>

                            {/* SMOKING Header */}
                            <tr>
                              <td colSpan="8" style={{ 
                                border: '1px solid #000',
                                borderBottom: 'none',
                                padding: '6px 10px',
                                marginTop: '5px'
                              }}>
                                <div style={{ textDecoration: 'underline' }}>
                                  <strong>SMOKING (Tobacco/Cigarette/Vape)</strong>
                                </div>
                              </td>
                            </tr>

                            {/* Smoking checkboxes Row */}
                            <tr>
                              <td colSpan="8" style={{ 
                                borderLeft: '1px solid #000',
                                borderRight: '1px solid #000',
                                padding: '4px 10px'
                              }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                                  <label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                    <input type="checkbox" checked={selectedRecord?.hr_a_drugs_history?.smoking === 'Never smoked'} disabled readOnly style={{ width: '14px', height: '14px' }} />
                                    <span>Never smoked</span>
                                  </label>
                                  <label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                    <input type="checkbox" checked={selectedRecord?.hr_a_drugs_history?.smoking === 'Current smoker'} disabled readOnly style={{ width: '14px', height: '14px' }} />
                                    <span>Current smoker</span>
                                  </label>
                                  <label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                    <input type="checkbox" checked={selectedRecord?.hr_a_drugs_history?.smoking === 'Passive smoker'} disabled readOnly style={{ width: '14px', height: '14px' }} />
                                    <span>Passive smoker</span>
                                  </label>
                                  <label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                    <input type="checkbox" checked={selectedRecord?.hr_a_drugs_history?.smoking === 'Stopped > a year'} disabled readOnly style={{ width: '14px', height: '14px' }} />
                                    <span>Stopped &gt; a year</span>
                                  </label>
                                  <label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                    <input type="checkbox" checked={selectedRecord?.hr_a_drugs_history?.smoking === 'Stopped < a year'} disabled readOnly style={{ width: '14px', height: '14px' }} />
                                    <span>Stopped &lt; a year</span>
                                  </label>
                                </div>
                              </td>
                            </tr>

                            {/* Smoking details Row */}
                            <tr>
                              <td colSpan="8" style={{ 
                                borderLeft: '1px solid #000',
                                borderRight: '1px solid #000',
                                borderBottom: '1px solid #000',
                                padding: '4px 10px'
                              }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                                  <strong>Age at onset:</strong>
                                  <span style={{ borderBottom: '1px solid #000', minWidth: '80px', textAlign: 'center' }}>{selectedRecord?.hr_a_drugs_history?.smoking_age_at_onset || '________'}</span>
                                  <span style={{ marginLeft: '30px' }}>Number of cigarette sticks consumed in a day:</span>
                                  <span style={{ borderBottom: '1px solid #000', minWidth: '100px', textAlign: 'center' }}>{selectedRecord?.hr_a_drugs_history?.cigarette_a_day || '__________'}</span>
                                </div>
                              </td>
                            </tr>

                            {/* DRUGS Header */}
                            <tr>
                              <td colSpan="8" style={{ 
                                border: '1px solid #000',
                                borderBottom: 'none',
                                padding: '6px 10px',
                                marginTop: '5px'
                              }}>
                                <div style={{ textDecoration: 'underline' }}>
                                  <strong>DRUGS</strong>
                                </div>
                              </td>
                            </tr>

                            {/* Drugs Row 1 */}
                            <tr>
                              <td colSpan="8" style={{ 
                                borderLeft: '1px solid #000',
                                borderRight: '1px solid #000',
                                padding: '4px 10px'
                              }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                                  <strong>Use of illicit drugs:</strong>
                                  <label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                    <input type="checkbox" checked={selectedRecord?.hr_a_drugs_history?.use_drugs === 1 || selectedRecord?.hr_a_drugs_history?.use_drugs === '1' || selectedRecord?.hr_a_drugs_history?.use_drugs === 'Yes'} disabled readOnly style={{ width: '14px', height: '14px' }} />
                                    <span>Yes</span>
                                  </label>
                                  <label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                    <input type="checkbox" checked={selectedRecord?.hr_a_drugs_history?.use_drugs === 0 || selectedRecord?.hr_a_drugs_history?.use_drugs === '0' || selectedRecord?.hr_a_drugs_history?.use_drugs === 'No'} disabled readOnly style={{ width: '14px', height: '14px' }} />
                                    <span>No</span>
                                  </label>
                                  <span style={{ marginLeft: '20px' }}>Age of onset:</span>
                                  <span style={{ borderBottom: '1px solid #000', minWidth: '80px', textAlign: 'center' }}>{selectedRecord?.hr_a_drugs_history?.drugs_age_at_onset || '________'}</span>
                                  <span style={{ marginLeft: '30px' }}>Injecting drugs for non-medical use:</span>
                                  <label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                    <input type="checkbox" checked={selectedRecord?.hr_a_drugs_history?.injecting_drugs === 1 || selectedRecord?.hr_a_drugs_history?.injecting_drugs === '1' || selectedRecord?.hr_a_drugs_history?.injecting_drugs === 'Yes'} disabled readOnly style={{ width: '14px', height: '14px' }} />
                                    <span>Yes</span>
                                  </label>
                                  <label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                    <input type="checkbox" checked={selectedRecord?.hr_a_drugs_history?.injecting_drugs === 0 || selectedRecord?.hr_a_drugs_history?.injecting_drugs === '0' || selectedRecord?.hr_a_drugs_history?.injecting_drugs === 'No'} disabled readOnly style={{ width: '14px', height: '14px' }} />
                                    <span>No</span>
                                  </label>
                                </div>
                              </td>
                            </tr>

                            {/* Drugs Row 2 */}
                            <tr>
                              <td colSpan="8" style={{ 
                                borderLeft: '1px solid #000',
                                borderRight: '1px solid #000',
                                padding: '4px 10px'
                              }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                                  <strong>Uses more than one drug at a time:</strong>
                                  <label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                    <input type="checkbox" checked={selectedRecord?.hr_a_drugs_history?.use_more_than_one_drugs === 1 || selectedRecord?.hr_a_drugs_history?.use_more_than_one_drugs === '1' || selectedRecord?.hr_a_drugs_history?.use_more_than_one_drugs === 'Yes'} disabled readOnly style={{ width: '14px', height: '14px' }} />
                                    <span>Yes</span>
                                  </label>
                                  <label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                    <input type="checkbox" checked={selectedRecord?.hr_a_drugs_history?.use_more_than_one_drugs === 0 || selectedRecord?.hr_a_drugs_history?.use_more_than_one_drugs === '0' || selectedRecord?.hr_a_drugs_history?.use_more_than_one_drugs === 'No'} disabled readOnly style={{ width: '14px', height: '14px' }} />
                                    <span>No</span>
                                  </label>
                                </div>
                              </td>
                            </tr>

                            {/* Drugs Table */}
                            <tr>
                              <td colSpan="8" style={{ 
                                borderLeft: '1px solid #000',
                                borderRight: '1px solid #000',
                                padding: '0'
                              }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                  <tbody>
                                    {/* Primary use */}
                                    <tr>
                                      <td style={{ 
                                        borderTop: '1px solid #000',
                                        borderRight: '1px solid #000',
                                        borderBottom: '1px solid #000',
                                        padding: '6px 10px',
                                        width: '12%'
                                      }}>
                                        <strong>Primary use</strong>
                                      </td>
                                      <td style={{ 
                                        borderTop: '1px solid #000',
                                        borderRight: '1px solid #000',
                                        borderBottom: '1px solid #000',
                                        padding: '6px 10px',
                                        width: '25%'
                                      }}>{selectedRecord?.hr_a_drugs_history?.primary_use || ''}</td>
                                      <td style={{ 
                                        borderTop: '1px solid #000',
                                        borderRight: '1px solid #000',
                                        borderBottom: '1px solid #000',
                                        padding: '6px 10px',
                                        width: '12%'
                                      }}>
                                        <strong>Frequency</strong>
                                      </td>
                                      <td style={{ 
                                        borderTop: '1px solid #000',
                                        borderRight: '1px solid #000',
                                        borderBottom: '1px solid #000',
                                        padding: '6px 10px',
                                        width: '14%'
                                      }}>{selectedRecord?.hr_a_drugs_history?.pri_frequency || ''}</td>
                                      <td style={{ 
                                        borderTop: '1px solid #000',
                                        borderRight: '1px solid #000',
                                        borderBottom: '1px solid #000',
                                        padding: '6px 10px',
                                        width: '12%'
                                      }}>
                                        <strong>Last intake</strong>
                                      </td>
                                      <td style={{ 
                                        borderTop: '1px solid #000',
                                        borderBottom: '1px solid #000',
                                        padding: '6px 10px',
                                        width: '25%'
                                      }}>{selectedRecord?.hr_a_drugs_history?.pri_last_intake || ''}</td>
                                    </tr>

                                    {/* Secondary use */}
                                    <tr>
                                      <td style={{ 
                                        borderRight: '1px solid #000',
                                        borderBottom: '1px solid #000',
                                        padding: '6px 10px'
                                      }}>
                                        <strong>Secondary use</strong>
                                      </td>
                                      <td style={{ 
                                        borderRight: '1px solid #000',
                                        borderBottom: '1px solid #000',
                                        padding: '6px 10px'
                                      }}>{selectedRecord?.hr_a_drugs_history?.secondary_use || ''}</td>
                                      <td style={{ 
                                        borderRight: '1px solid #000',
                                        borderBottom: '1px solid #000',
                                        padding: '6px 10px'
                                      }}>
                                        <strong>Frequency</strong>
                                      </td>
                                      <td style={{ 
                                        borderRight: '1px solid #000',
                                        borderBottom: '1px solid #000',
                                        padding: '6px 10px'
                                      }}>{selectedRecord?.hr_a_drugs_history?.sec_frequency || ''}</td>
                                      <td style={{ 
                                        borderRight: '1px solid #000',
                                        borderBottom: '1px solid #000',
                                        padding: '6px 10px'
                                      }}>
                                        <strong>Last intake</strong>
                                      </td>
                                      <td style={{ 
                                        borderBottom: '1px solid #000',
                                        padding: '6px 10px'
                                      }}>{selectedRecord?.hr_a_drugs_history?.sec_last_intake || ''}</td>
                                    </tr>

                                    {/* Other */}
                                    <tr>
                                      <td style={{ 
                                        borderRight: '1px solid #000',
                                        padding: '6px 10px'
                                      }}>
                                        <strong>Other</strong>
                                      </td>
                                      <td style={{ 
                                        borderRight: '1px solid #000',
                                        padding: '6px 10px'
                                      }}>{selectedRecord?.hr_a_drugs_history?.other_use || ''}</td>
                                      <td style={{ 
                                        borderRight: '1px solid #000',
                                        padding: '6px 10px'
                                      }}>
                                        <strong>Frequency</strong>
                                      </td>
                                      <td style={{ 
                                        borderRight: '1px solid #000',
                                        padding: '6px 10px'
                                      }}>{selectedRecord?.hr_a_drugs_history?.oth_frequency || ''}</td>
                                      <td style={{ 
                                        borderRight: '1px solid #000',
                                        padding: '6px 10px'
                                      }}>
                                        <strong>Last intake</strong>
                                      </td>
                                      <td style={{ 
                                        padding: '6px 10px'
                                      }}>{selectedRecord?.hr_a_drugs_history?.oth_last_intake || ''}</td>
                                    </tr>
                                  </tbody>
                                </table>
                              </td>
                            </tr>
                          </tbody>
                        </table>

                      </div>

                      {/* Page 4 Navigation */}
                      <div className={modalStyles.jbrPageNavFooter}>
                        <div className={modalStyles.jbrPageNavFooterRight}>
                          <button 
                            className={modalStyles.jbrNextBtn}
                            onClick={() => setCurrentPage(5)}
                          >
                            Next
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </button>
                        </div>
                      </div>
                    </>
                  )}

                  {/* Page 5 Content - Back Form Image */}
                  {currentPage === 5 && (
                    <>
                      <style>
                        {`
                          #back-form-container input[type="checkbox"] {
                            width: 16px;
                            height: 16px;
                            cursor: default;
                            appearance: none;
                            -webkit-appearance: none;
                            border: 2px solid #000;
                            border-radius: 2px;
                            position: relative;
                            background-color: #fff;
                          }
                          #back-form-container input[type="checkbox"]:checked {
                            background-color: #000;
                            border-color: #000;
                          }
                          #back-form-container input[type="checkbox"]:checked::after {
                            content: '';
                            position: absolute;
                            width: 4px;
                            height: 9px;
                            border: solid #fff;
                            border-width: 0 2px 2px 0;
                            top: 45%;
                            left: 50%;
                            transform: translate(-50%, -50%) rotate(45deg);
                          }
                        `}
                      </style>
                      <div id="back-form-container" style={{ 
                        maxWidth: '1100px', 
                        margin: '0 auto 1.5rem auto', 
                        background: '#ffffff', 
                        padding: '20px', 
                        fontFamily: 'Arial, sans-serif',
                        color: '#000000',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        border: '1px solid #ddd'
                      }}>
                        {/* Form No. */}
                        <div style={{ textAlign: 'right', marginBottom: '10px' }}>
                          <strong style={{ fontSize: '14px' }}>Form No. _________</strong>
                          </div>

                          {/* B. PHYSICAL EXAMINATION ON ARRIVAL */}
                          <table style={{ 
                            width: '100%', 
                            borderCollapse: 'collapse',
                            border: '1px solid #000',
                            fontSize: '11px',
                            marginTop: '10px'
                          }}>
                            <tbody>
                              {/* Header Row */}
                              <tr>
                                <td colSpan="8" style={{ 
                                  border: '1px solid #000',
                                  padding: '8px 10px',
                                  backgroundColor: '#fff'
                                }}>
                                  <strong style={{ fontSize: '13px' }}>B. PHYSICAL EXAMINATION ON ARRIVAL</strong>
                                </td>
                              </tr>

                              {/* Vital Signs Row */}
                              <tr>
                                <td colSpan="8" style={{ 
                                  border: '1px solid #000',
                                  padding: '10px'
                                }}>
                                  <div style={{ display: 'flex', gap: '15px', alignItems: 'center', flexWrap: 'wrap' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                      <strong>T:</strong>
                                      <span style={{ borderBottom: '1px solid #000', minWidth: '50px', display: 'inline-block', textAlign: 'center' }}>{selectedRecord?.hr_b_physical_examination?.temp || ''}</span>
                                      <span>Â°C</span>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                      <strong>BP:</strong>
                                      <span style={{ borderBottom: '1px solid #000', minWidth: '70px', display: 'inline-block', textAlign: 'center' }}>{selectedRecord?.hr_b_physical_examination?.bp || ''}</span>
                                      <span>mm/Hg</span>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                      <strong>HR:</strong>
                                      <span style={{ borderBottom: '1px solid #000', minWidth: '60px', display: 'inline-block', textAlign: 'center' }}>{selectedRecord?.hr_b_physical_examination?.hr || ''}</span>
                                      <span>bpm</span>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                      <strong>RR:</strong>
                                      <span style={{ borderBottom: '1px solid #000', minWidth: '50px', display: 'inline-block', textAlign: 'center' }}>{selectedRecord?.hr_b_physical_examination?.rr || ''}</span>
                                      <span>cpm</span>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                      <strong>Height:</strong>
                                      <span style={{ borderBottom: '1px solid #000', minWidth: '50px', display: 'inline-block', textAlign: 'center' }}>{selectedRecord?.hr_b_physical_examination?.height || ''}</span>
                                      <span>m</span>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                      <strong>Weight:</strong>
                                      <span style={{ borderBottom: '1px solid #000', minWidth: '50px', display: 'inline-block', textAlign: 'center' }}>{selectedRecord?.hr_b_physical_examination?.weight || ''}</span>
                                      <span>kg</span>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                      <strong>BMI:</strong>
                                      <span style={{ borderBottom: '1px solid #000', minWidth: '60px', display: 'inline-block', textAlign: 'center' }}>{selectedRecord?.hr_b_physical_examination?.bmi || ''}</span>
                                      <span>kg/mÂ²</span>
                                    </div>
                                  </div>
                                </td>
                              </tr>

                              {/* Main Content Row with 2 columns */}
                              <tr>
                                {/* Left Column - Examination Items */}
                                <td colSpan="5" style={{ 
                                  border: '1px solid #000',
                                  padding: '10px',
                                  verticalAlign: 'top',
                                  width: '67%'
                                }}>
                                  {/* Yes/No Header */}
                                  <div style={{ display: 'flex', marginBottom: '10px' }}>
                                    <div style={{ flex: 1 }}></div>
                                    <div style={{ display: 'flex', gap: '10px', minWidth: '80px', justifyContent: 'center' }}>
                                      <div style={{ width: '16px', textAlign: 'center', fontSize: '11px' }}>
                                        <strong>Yes</strong>
                                      </div>
                                      <div style={{ width: '16px', textAlign: 'center', fontSize: '11px' }}>
                                        <strong>No</strong>
                                      </div>
                                    </div>
                                  </div>

                                  {/* Examination items list */}
                                  <div>
                                    {/* Item 1 */}
                                    <div style={{ display: 'flex', marginBottom: '8px' }}>
                                      <div style={{ flex: 1 }}>
                                        <strong>1. General appearance</strong> <em style={{ fontSize: '10px' }}>(upright with coordinated movement, no signs of distress)</em>
                                      </div>
                                      <div style={{ display: 'flex', gap: '10px', minWidth: '80px', justifyContent: 'center' }}>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                                          <input type="checkbox" checked={selectedRecord?.hr_b_physical_examination?.general_appearance === 'Yes' || selectedRecord?.hr_b_physical_examination?.general_appearance === 1 || selectedRecord?.hr_b_physical_examination?.general_appearance === '1'} disabled readOnly style={{ width: '16px', height: '16px' }} />
                                        </label>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                                          <input type="checkbox" checked={selectedRecord?.hr_b_physical_examination?.general_appearance === 'No' || selectedRecord?.hr_b_physical_examination?.general_appearance === 0 || selectedRecord?.hr_b_physical_examination?.general_appearance === '0'} disabled readOnly style={{ width: '16px', height: '16px' }} />
                                        </label>
                                      </div>
                                    </div>

                                    {/* Item 2 */}
                                    <div style={{ display: 'flex', marginBottom: '8px' }}>
                                      <div style={{ flex: 1 }}>
                                        <strong>2. Head, eyes, ears, nose and throat</strong> <em style={{ fontSize: '10px' }}>(Normocephalic, pink palpebral conjunctiva, white and clear sclera, PERLA, no nasal/aural discharges, no tonsillo-pharyngeal congestion)</em>
                                      </div>
                                      <div style={{ display: 'flex', gap: '10px', minWidth: '80px', justifyContent: 'center' }}>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                                          <input type="checkbox" checked={selectedRecord?.hr_b_physical_examination?.head === 'Yes' || selectedRecord?.hr_b_physical_examination?.head === 1 || selectedRecord?.hr_b_physical_examination?.head === '1'} disabled readOnly style={{ width: '16px', height: '16px' }} />
                                        </label>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                                          <input type="checkbox" checked={selectedRecord?.hr_b_physical_examination?.head === 'No' || selectedRecord?.hr_b_physical_examination?.head === 0 || selectedRecord?.hr_b_physical_examination?.head === '0'} disabled readOnly style={{ width: '16px', height: '16px' }} />
                                        </label>
                                      </div>
                                    </div>

                                    {/* Item 3 */}
                                    <div style={{ display: 'flex', marginBottom: '8px' }}>
                                      <div style={{ flex: 1 }}>
                                        <strong>3. Neck</strong> <em style={{ fontSize: '10px' }}>(no lymph nodes palpable)</em>
                                      </div>
                                      <div style={{ display: 'flex', gap: '10px', minWidth: '80px', justifyContent: 'center' }}>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                                          <input type="checkbox" checked={selectedRecord?.hr_b_physical_examination?.neck === 'Yes' || selectedRecord?.hr_b_physical_examination?.neck === 1 || selectedRecord?.hr_b_physical_examination?.neck === '1'} disabled readOnly style={{ width: '16px', height: '16px' }} />
                                        </label>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                                          <input type="checkbox" checked={selectedRecord?.hr_b_physical_examination?.neck === 'No' || selectedRecord?.hr_b_physical_examination?.neck === 0 || selectedRecord?.hr_b_physical_examination?.neck === '0'} disabled readOnly style={{ width: '16px', height: '16px' }} />
                                        </label>
                                      </div>
                                    </div>

                                    {/* Item 4 */}
                                    <div style={{ display: 'flex', marginBottom: '8px' }}>
                                      <div style={{ flex: 1 }}>
                                        <strong>4. Chest/lungs</strong> <em style={{ fontSize: '10px' }}>(symmetrical chest and lung expansion, clear both sounds)</em>
                                      </div>
                                      <div style={{ display: 'flex', gap: '10px', minWidth: '80px', justifyContent: 'center' }}>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                                          <input type="checkbox" checked={selectedRecord?.hr_b_physical_examination?.chest === 'Yes' || selectedRecord?.hr_b_physical_examination?.chest === 1 || selectedRecord?.hr_b_physical_examination?.chest === '1'} disabled readOnly style={{ width: '16px', height: '16px' }} />
                                        </label>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                                          <input type="checkbox" checked={selectedRecord?.hr_b_physical_examination?.chest === 'No' || selectedRecord?.hr_b_physical_examination?.chest === 0 || selectedRecord?.hr_b_physical_examination?.chest === '0'} disabled readOnly style={{ width: '16px', height: '16px' }} />
                                        </label>
                                      </div>
                                    </div>

                                    {/* Item 5 */}
                                    <div style={{ display: 'flex', marginBottom: '8px' }}>
                                      <div style={{ flex: 1 }}>
                                        <strong>5. Heart</strong> <em style={{ fontSize: '10px' }}>(regular rhythm, no abnormal heart sounds)</em>
                                      </div>
                                      <div style={{ display: 'flex', gap: '10px', minWidth: '80px', justifyContent: 'center' }}>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                                          <input type="checkbox" checked={selectedRecord?.hr_b_physical_examination?.heart === 'Yes' || selectedRecord?.hr_b_physical_examination?.heart === 1 || selectedRecord?.hr_b_physical_examination?.heart === '1'} disabled readOnly style={{ width: '16px', height: '16px' }} />
                                        </label>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                                          <input type="checkbox" checked={selectedRecord?.hr_b_physical_examination?.heart === 'No' || selectedRecord?.hr_b_physical_examination?.heart === 0 || selectedRecord?.hr_b_physical_examination?.heart === '0'} disabled readOnly style={{ width: '16px', height: '16px' }} />
                                        </label>
                                      </div>
                                    </div>

                                    {/* Item 6 */}
                                    <div style={{ display: 'flex', marginBottom: '8px' }}>
                                      <div style={{ flex: 1 }}>
                                        <strong>6. Abdomen</strong> <em style={{ fontSize: '10px' }}>(flat, soft, no masses, normal bowel sounds)</em>
                                      </div>
                                      <div style={{ display: 'flex', gap: '10px', minWidth: '80px', justifyContent: 'center' }}>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                                          <input type="checkbox" checked={selectedRecord?.hr_b_physical_examination?.abdomen === 'Yes' || selectedRecord?.hr_b_physical_examination?.abdomen === 1 || selectedRecord?.hr_b_physical_examination?.abdomen === '1'} disabled readOnly style={{ width: '16px', height: '16px' }} />
                                        </label>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                                          <input type="checkbox" checked={selectedRecord?.hr_b_physical_examination?.abdomen === 'No' || selectedRecord?.hr_b_physical_examination?.abdomen === 0 || selectedRecord?.hr_b_physical_examination?.abdomen === '0'} disabled readOnly style={{ width: '16px', height: '16px' }} />
                                        </label>
                                      </div>
                                    </div>

                                    {/* Item 7 */}
                                    <div style={{ display: 'flex', marginBottom: '8px' }}>
                                      <div style={{ flex: 1 }}>
                                        <strong>7. Genito-urinary</strong> <em style={{ fontSize: '10px' }}>(no lesions, no urethral discharges, no palpable hernia)</em>
                                      </div>
                                      <div style={{ display: 'flex', gap: '10px', minWidth: '80px', justifyContent: 'center' }}>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                                          <input type="checkbox" checked={selectedRecord?.hr_b_physical_examination?.genito_urinary === 'Yes' || selectedRecord?.hr_b_physical_examination?.genito_urinary === 1 || selectedRecord?.hr_b_physical_examination?.genito_urinary === '1'} disabled readOnly style={{ width: '16px', height: '16px' }} />
                                        </label>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                                          <input type="checkbox" checked={selectedRecord?.hr_b_physical_examination?.genito_urinary === 'No' || selectedRecord?.hr_b_physical_examination?.genito_urinary === 0 || selectedRecord?.hr_b_physical_examination?.genito_urinary === '0'} disabled readOnly style={{ width: '16px', height: '16px' }} />
                                        </label>
                                      </div>
                                    </div>

                                    {/* Item 8 */}
                                    <div style={{ display: 'flex', marginBottom: '8px' }}>
                                      <div style={{ flex: 1 }}>
                                        <strong>8. Musculo-skeletal</strong> <em style={{ fontSize: '10px' }}>(no gross deformities, no limitation of movement)</em>
                                      </div>
                                      <div style={{ display: 'flex', gap: '10px', minWidth: '80px', justifyContent: 'center' }}>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                                          <input type="checkbox" checked={selectedRecord?.hr_b_physical_examination?.musculo_skeletal === 'Yes' || selectedRecord?.hr_b_physical_examination?.musculo_skeletal === 1 || selectedRecord?.hr_b_physical_examination?.musculo_skeletal === '1'} disabled readOnly style={{ width: '16px', height: '16px' }} />
                                        </label>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                                          <input type="checkbox" checked={selectedRecord?.hr_b_physical_examination?.musculo_skeletal === 'No' || selectedRecord?.hr_b_physical_examination?.musculo_skeletal === 0 || selectedRecord?.hr_b_physical_examination?.musculo_skeletal === '0'} disabled readOnly style={{ width: '16px', height: '16px' }} />
                                        </label>
                                      </div>
                                    </div>

                                    {/* Item 9 */}
                                    <div style={{ display: 'flex', marginBottom: '8px' }}>
                                      <div style={{ flex: 1 }}>
                                        <strong>9. Extremities</strong> <em style={{ fontSize: '10px' }}>(no swelling/deformity, no weakness, good capillary refill, strong pulses, normal flexes)</em>
                                      </div>
                                      <div style={{ display: 'flex', gap: '10px', minWidth: '80px', justifyContent: 'center' }}>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                                          <input type="checkbox" checked={selectedRecord?.hr_b_physical_examination?.extremities === 'Yes' || selectedRecord?.hr_b_physical_examination?.extremities === 1 || selectedRecord?.hr_b_physical_examination?.extremities === '1'} disabled readOnly style={{ width: '16px', height: '16px' }} />
                                        </label>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                                          <input type="checkbox" checked={selectedRecord?.hr_b_physical_examination?.extremities === 'No' || selectedRecord?.hr_b_physical_examination?.extremities === 0 || selectedRecord?.hr_b_physical_examination?.extremities === '0'} disabled readOnly style={{ width: '16px', height: '16px' }} />
                                        </label>
                                      </div>
                                    </div>

                                    {/* Item 10 */}
                                    <div style={{ display: 'flex', marginBottom: '0' }}>
                                      <div style={{ flex: 1 }}>
                                        <strong>10. Other significant findings</strong> <em style={{ fontSize: '10px' }}>(specify)</em>
                                        <span style={{ borderBottom: '1px solid #000', display: 'inline-block', minWidth: '200px' }}>{selectedRecord?.hr_b_physical_examination?.other_findings || ''}</span>
                                      </div>
                                    </div>
                                  </div>
                                </td>

                                {/* Right Column - Detailed findings */}
                                <td colSpan="3" style={{ 
                                  border: '1px solid #000',
                                  padding: '10px',
                                  verticalAlign: 'top',
                                  width: '33%'
                                }}>
                                  <strong style={{ display: 'block', marginBottom: '10px' }}>Detailed findings</strong>
                                  <div style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}>
                                    {selectedRecord?.hr_b_physical_examination?.detailed_findings || ''}
                                  </div>
                                </td>
                              </tr>

                              {/* C. TB ENTRY SCREENING CHECKLIST */}
                              <tr>
                                <td colSpan="8" style={{ 
                                  border: '1px solid #000',
                                  padding: '10px',
                                  backgroundColor: '#fff'
                                }}>
                                  <strong style={{ fontSize: '14px' }}>C. TB ENTRY SCREENING CHECKLIST</strong>
                                </td>
                              </tr>

                              <tr>
                                {/* Left Column - Checklist Items */}
                                <td colSpan="4" style={{ 
                                  border: '1px solid #000',
                                  padding: '10px',
                                  verticalAlign: 'top',
                                  width: '50%'
                                }}>
                                  {/* Yes/No Header */}
                                  <div style={{ display: 'flex', marginBottom: '10px' }}>
                                    <div style={{ flex: 1 }}></div>
                                    <div style={{ display: 'flex', gap: '10px', minWidth: '80px', justifyContent: 'center' }}>
                                      <div style={{ width: '16px', textAlign: 'center', fontSize: '11px' }}>
                                        <strong>Yes</strong>
                                      </div>
                                      <div style={{ width: '16px', textAlign: 'center', fontSize: '11px' }}>
                                        <strong>No</strong>
                                      </div>
                                    </div>
                                  </div>

                                  {/* Item A */}
                                  <div style={{ display: 'flex', marginBottom: '8px' }}>
                                    <div style={{ flex: 1 }}>
                                      <strong>A.</strong> Unexplained cough, any duration
                                    </div>
                                    <div style={{ display: 'flex', gap: '10px', minWidth: '80px', justifyContent: 'center' }}>
                                      <label style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                                        <input type="checkbox" checked={selectedRecord?.hr_c_tb_screening?.unexplained_cough === 'Yes' || selectedRecord?.hr_c_tb_screening?.unexplained_cough === 1 || selectedRecord?.hr_c_tb_screening?.unexplained_cough === '1'} disabled readOnly style={{ width: '16px', height: '16px' }} />
                                      </label>
                                      <label style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                                        <input type="checkbox" checked={selectedRecord?.hr_c_tb_screening?.unexplained_cough === 'No' || selectedRecord?.hr_c_tb_screening?.unexplained_cough === 0 || selectedRecord?.hr_c_tb_screening?.unexplained_cough === '0'} disabled readOnly style={{ width: '16px', height: '16px' }} />
                                      </label>
                                    </div>
                                  </div>

                                  {/* Item B */}
                                  <div style={{ display: 'flex', marginBottom: '8px' }}>
                                    <div style={{ flex: 1 }}>
                                      <strong>B.</strong> BMI &lt; 18.5
                                    </div>
                                    <div style={{ display: 'flex', gap: '10px', minWidth: '80px', justifyContent: 'center' }}>
                                      <label style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                                        <input type="checkbox" checked={selectedRecord?.hr_c_tb_screening?.bmi_low === 'Yes' || selectedRecord?.hr_c_tb_screening?.bmi_low === 1 || selectedRecord?.hr_c_tb_screening?.bmi_low === '1'} disabled readOnly style={{ width: '16px', height: '16px' }} />
                                      </label>
                                      <label style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                                        <input type="checkbox" checked={selectedRecord?.hr_c_tb_screening?.bmi_low === 'No' || selectedRecord?.hr_c_tb_screening?.bmi_low === 0 || selectedRecord?.hr_c_tb_screening?.bmi_low === '0'} disabled readOnly style={{ width: '16px', height: '16px' }} />
                                      </label>
                                    </div>
                                  </div>

                                  {/* Known medical conditions */}
                                  <div style={{ display: 'flex', marginBottom: '8px' }}>
                                    <div style={{ flex: 1 }}>
                                      <div style={{ marginBottom: '4px' }}>Known medical conditions</div>
                                      <div style={{ fontSize: '10px', fontStyle: 'italic', paddingLeft: '10px' }}>
                                        (Clinical High-risk Groups: HIV/AIDS, diabetes, end-stage renal disease, cancer, connective tissue diseases, autoimmune diseases, silicosis, gastrectomy, solid organ transplantation, prolonged systemic steroids)
                                      </div>
                                    </div>
                                    <div style={{ display: 'flex', gap: '10px', minWidth: '80px', justifyContent: 'center' }}>
                                      <label style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                                        <input type="checkbox" checked={selectedRecord?.hr_c_tb_screening?.known_medical_condition === 'Yes' || selectedRecord?.hr_c_tb_screening?.known_medical_condition === 1 || selectedRecord?.hr_c_tb_screening?.known_medical_condition === '1'} disabled readOnly style={{ width: '16px', height: '16px' }} />
                                      </label>
                                      <label style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                                        <input type="checkbox" checked={selectedRecord?.hr_c_tb_screening?.known_medical_condition === 'No' || selectedRecord?.hr_c_tb_screening?.known_medical_condition === 0 || selectedRecord?.hr_c_tb_screening?.known_medical_condition === '0'} disabled readOnly style={{ width: '16px', height: '16px' }} />
                                      </label>
                                    </div>
                                  </div>

                                  {/* Item C */}
                                  <div style={{ display: 'flex', marginBottom: '8px' }}>
                                    <div style={{ flex: 1 }}>
                                      <strong>C.</strong> Blood-streaked sputum
                                    </div>
                                    <div style={{ display: 'flex', gap: '10px', minWidth: '80px', justifyContent: 'center' }}>
                                      <label style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                                        <input type="checkbox" checked={selectedRecord?.hr_c_tb_screening?.blood_streaked_sputum === 'Yes' || selectedRecord?.hr_c_tb_screening?.blood_streaked_sputum === 1 || selectedRecord?.hr_c_tb_screening?.blood_streaked_sputum === '1'} disabled readOnly style={{ width: '16px', height: '16px' }} />
                                      </label>
                                      <label style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                                        <input type="checkbox" checked={selectedRecord?.hr_c_tb_screening?.blood_streaked_sputum === 'No' || selectedRecord?.hr_c_tb_screening?.blood_streaked_sputum === 0 || selectedRecord?.hr_c_tb_screening?.blood_streaked_sputum === '0'} disabled readOnly style={{ width: '16px', height: '16px' }} />
                                      </label>
                                    </div>
                                  </div>

                                  {/* Weight loss or BMI < 18.5 */}
                                  <div style={{ display: 'flex', marginBottom: '8px' }}>
                                    <div style={{ flex: 1 }}>
                                      Weight loss or BMI &lt; 18.5
                                    </div>
                                    <div style={{ display: 'flex', gap: '10px', minWidth: '80px', justifyContent: 'center' }}>
                                      <label style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                                        <input type="checkbox" checked={selectedRecord?.hr_c_tb_screening?.weight_loss === 'Yes' || selectedRecord?.hr_c_tb_screening?.weight_loss === 1 || selectedRecord?.hr_c_tb_screening?.weight_loss === '1'} disabled readOnly style={{ width: '16px', height: '16px' }} />
                                      </label>
                                      <label style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                                        <input type="checkbox" checked={selectedRecord?.hr_c_tb_screening?.weight_loss === 'No' || selectedRecord?.hr_c_tb_screening?.weight_loss === 0 || selectedRecord?.hr_c_tb_screening?.weight_loss === '0'} disabled readOnly style={{ width: '16px', height: '16px' }} />
                                      </label>
                                    </div>
                                  </div>

                                  {/* Persistent fever */}
                                  <div style={{ display: 'flex', marginBottom: '8px' }}>
                                    <div style={{ flex: 1 }}>
                                      Persistent fever
                                    </div>
                                    <div style={{ display: 'flex', gap: '10px', minWidth: '80px', justifyContent: 'center' }}>
                                      <label style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                                        <input type="checkbox" checked={selectedRecord?.hr_c_tb_screening?.persistent_fever === 'Yes' || selectedRecord?.hr_c_tb_screening?.persistent_fever === 1 || selectedRecord?.hr_c_tb_screening?.persistent_fever === '1'} disabled readOnly style={{ width: '16px', height: '16px' }} />
                                      </label>
                                      <label style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                                        <input type="checkbox" checked={selectedRecord?.hr_c_tb_screening?.persistent_fever === 'No' || selectedRecord?.hr_c_tb_screening?.persistent_fever === 0 || selectedRecord?.hr_c_tb_screening?.persistent_fever === '0'} disabled readOnly style={{ width: '16px', height: '16px' }} />
                                      </label>
                                    </div>
                                  </div>

                                  {/* Item D */}
                                  <div style={{ display: 'flex', marginBottom: '8px' }}>
                                    <div style={{ flex: 1 }}>
                                      <strong>D.</strong> Chest x-ray suggestive of TB
                                    </div>
                                    <div style={{ display: 'flex', gap: '10px', minWidth: '80px', justifyContent: 'center' }}>
                                      <label style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                                        <input type="checkbox" checked={selectedRecord?.hr_c_tb_screening?.xray_suggestion === 'Yes' || selectedRecord?.hr_c_tb_screening?.xray_suggestion === 1 || selectedRecord?.hr_c_tb_screening?.xray_suggestion === '1'} disabled readOnly style={{ width: '16px', height: '16px' }} />
                                      </label>
                                      <label style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                                        <input type="checkbox" checked={selectedRecord?.hr_c_tb_screening?.xray_suggestion === 'No' || selectedRecord?.hr_c_tb_screening?.xray_suggestion === 0 || selectedRecord?.hr_c_tb_screening?.xray_suggestion === '0'} disabled readOnly style={{ width: '16px', height: '16px' }} />
                                      </label>
                                    </div>
                                  </div>

                                  {/* Item E */}
                                  <div style={{ display: 'flex', marginBottom: '8px' }}>
                                    <div style={{ flex: 1 }}>
                                      <strong>E.</strong> History of previous TB treatment
                                    </div>
                                    <div style={{ display: 'flex', gap: '10px', minWidth: '80px', justifyContent: 'center' }}>
                                      <label style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                                        <input type="checkbox" checked={selectedRecord?.hr_c_tb_screening?.tb_treatment_history === 'Yes' || selectedRecord?.hr_c_tb_screening?.tb_treatment_history === 1 || selectedRecord?.hr_c_tb_screening?.tb_treatment_history === '1'} disabled readOnly style={{ width: '16px', height: '16px' }} />
                                      </label>
                                      <label style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                                        <input type="checkbox" checked={selectedRecord?.hr_c_tb_screening?.tb_treatment_history === 'No' || selectedRecord?.hr_c_tb_screening?.tb_treatment_history === 0 || selectedRecord?.hr_c_tb_screening?.tb_treatment_history === '0'} disabled readOnly style={{ width: '16px', height: '16px' }} />
                                      </label>
                                    </div>
                                  </div>

                                  {/* If outcome known, specify */}
                                  <div style={{ marginBottom: '8px', paddingLeft: '10px' }}>
                                    If outcome known, specify:
                                    <div style={{ borderBottom: '1px solid #000', display: 'block', minWidth: '100%', marginTop: '5px' }}>{selectedRecord?.hr_c_tb_screening?.tb_tratment_specify || ''}</div>
                                  </div>

                                  {/* Item F */}
                                  <div style={{ display: 'flex', marginBottom: '8px' }}>
                                    <div style={{ flex: 1 }}>
                                      <strong>F.</strong> Exposure to TB case
                                    </div>
                                    <div style={{ display: 'flex', gap: '10px', minWidth: '80px', justifyContent: 'center' }}>
                                      <label style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                                        <input type="checkbox" checked={selectedRecord?.hr_c_tb_screening?.exposure_to_tb === 'Yes' || selectedRecord?.hr_c_tb_screening?.exposure_to_tb === 1 || selectedRecord?.hr_c_tb_screening?.exposure_to_tb === '1'} disabled readOnly style={{ width: '16px', height: '16px' }} />
                                      </label>
                                      <label style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                                        <input type="checkbox" checked={selectedRecord?.hr_c_tb_screening?.exposure_to_tb === 'No' || selectedRecord?.hr_c_tb_screening?.exposure_to_tb === 0 || selectedRecord?.hr_c_tb_screening?.exposure_to_tb === '0'} disabled readOnly style={{ width: '16px', height: '16px' }} />
                                      </label>
                                    </div>
                                  </div>

                                  {/* If known, specify */}
                                  <div style={{ marginBottom: '8px', paddingLeft: '10px' }}>
                                    If known, specify:
                                    <div style={{ borderBottom: '1px solid #000', display: 'block', minWidth: '100%', marginTop: '5px' }}>{selectedRecord?.hr_c_tb_screening?.exposure_specify || ''}</div>
                                  </div>

                                  {/* Complete name and signature */}
                                  <div style={{ marginTop: '15px' }}>
                                    Complete name and signature of health staff:
                                    <div style={{ borderBottom: '1px solid #000', display: 'block', minWidth: '100%', marginTop: '5px' }}>
                                      {(() => {
                                        const nurseData = selectedRecord?.nurse_created || selectedRecord?.nurseCreated;
                                        if (nurseData) {
                                          const firstName = nurseData.first_name || '';
                                          const middleName = nurseData.middle_name || '';
                                          const lastName = nurseData.last_name || '';
                                          return `${firstName} ${middleName} ${lastName}`.trim();
                                        }
                                        return '';
                                      })()}
                                    </div>
                                  </div>
                                </td>

                                {/* Right Column - Remarks and Final Result */}
                                <td colSpan="4" style={{ 
                                  border: '1px solid #000',
                                  padding: '10px',
                                  verticalAlign: 'top',
                                  width: '50%'
                                }}>
                                  {/* Remarks Section */}
                                  <div style={{ marginBottom: '15px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '5px' }}>
                                      <strong style={{ width: '80px', flexShrink: 0 }}>Remarks:</strong>
                                      <input type="checkbox" checked={selectedRecord?.hr_c_tb_screening?.remarks?.includes('Presumptive TB')} disabled readOnly style={{ width: '16px', height: '16px', marginRight: '8px' }} />
                                      <span>Presumptive TB</span>
                                    </div>
                                      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '5px', marginLeft: '80px' }}>
                                        <input type="checkbox" checked={selectedRecord?.hr_c_tb_screening?.remarks?.includes('Presumptive DR-TB')} disabled readOnly style={{ width: '16px', height: '16px', marginRight: '8px' }} />
                                        <span>Presumptive DR-TB</span>
                                      </div>
                                      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '5px', marginLeft: '80px' }}>
                                        <input type="checkbox" checked={selectedRecord?.hr_c_tb_screening?.remarks?.includes('Not TB')} disabled readOnly style={{ width: '16px', height: '16px', marginRight: '8px' }} />
                                        <span>Not TB</span>
                                      </div>
                                      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '5px', marginLeft: '80px' }}>
                                        <input type="checkbox" checked={selectedRecord?.hr_c_tb_screening?.remarks?.includes('On-going TB treatment')} disabled readOnly style={{ width: '16px', height: '16px', marginRight: '8px' }} />
                                        <span>On-going TB treatment <em style={{ fontSize: '10px' }}>(Ask for NTP Referral Form)</em></span>
                                      </div>
                                      <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '5px', marginLeft: '80px' }}>
                                        <input type="checkbox" checked={selectedRecord?.hr_c_tb_screening?.remarks?.includes('Other follow-up')} disabled readOnly style={{ width: '16px', height: '16px', marginRight: '8px', marginTop: '2px' }} />
                                        <div style={{ flex: 1 }}>
                                          Other follow-up, specify:
                                          <div style={{ borderBottom: '1px solid #000', display: 'block', minWidth: '100%', marginTop: '3px' }}>{(selectedRecord?.hr_c_tb_screening?.remarks?.includes('Other follow-up') && selectedRecord?.hr_c_tb_screening?.remarks?.split(':')[1]?.trim()) || ''}</div>
                                        </div>
                                      </div>
                                  </div>

                                  {/* Final Result Section */}
                                  <div style={{ marginBottom: '15px' }}>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center' }}>
                                      <strong style={{ marginRight: '10px' }}>Final Result:</strong>
                                      <div style={{ display: 'flex', alignItems: 'center' }}>
                                        <input type="checkbox" checked={selectedRecord?.hr_c_tb_screening?.final_result?.includes('New Case')} disabled readOnly style={{ width: '16px', height: '16px', marginRight: '5px' }} />
                                        <span>New Case</span>
                                      </div>
                                      <div style={{ display: 'flex', alignItems: 'center' }}>
                                        <input type="checkbox" checked={selectedRecord?.hr_c_tb_screening?.final_result?.includes('Retreatment')} disabled readOnly style={{ width: '16px', height: '16px', marginRight: '5px' }} />
                                        <span>Retreatment</span>
                                      </div>
                                      <div style={{ display: 'flex', alignItems: 'center' }}>
                                        <input type="checkbox" checked={selectedRecord?.hr_c_tb_screening?.final_result?.includes('Negative')} disabled readOnly style={{ width: '16px', height: '16px', marginRight: '5px' }} />
                                        <span>Negative</span>
                                      </div>
                                    </div>
                                    <div style={{ marginTop: '8px', display: 'flex', gap: '10px', marginLeft: '105px' }}>
                                      <div style={{ display: 'flex', alignItems: 'center' }}>
                                        <input type="checkbox" checked={selectedRecord?.hr_c_tb_screening?.final_result?.includes('BC')} disabled readOnly style={{ width: '16px', height: '16px', marginRight: '5px' }} />
                                        <span>BC</span>
                                      </div>
                                      <div style={{ display: 'flex', alignItems: 'center' }}>
                                        <input type="checkbox" checked={selectedRecord?.hr_c_tb_screening?.final_result?.includes('CD')} disabled readOnly style={{ width: '16px', height: '16px', marginRight: '5px' }} />
                                        <span>CD</span>
                                      </div>
                                    </div>
                                  </div>

                                  {/* Presumptive TB Info Box */}
                                  <div style={{ 
                                    border: '2px solid #000',
                                    borderRadius: '15px',
                                    padding: '15px',
                                    marginTop: '10px'
                                  }}>
                                    <div style={{ marginBottom: '10px' }}>
                                      <strong>Presumptive TB if:</strong>
                                      <div style={{ marginTop: '5px', fontSize: '11px', paddingLeft: '10px' }}>
                                        <div>Unexplained cough of any duration (A) with/without other signs/symptoms (C) or risk factors (B).</div>
                                        <div>X-ray suggestive of TB (D)</div>
                                        <div>If with any other signs/symptoms (C) or risk factors (B), refer to physician.</div>
                                        <div style={{ paddingLeft: '15px', marginTop: '3px' }}>In absence of physician, you may consider presumptive TB.</div>
                                      </div>
                                    </div>
                                    <div>
                                      <strong>Presumptive DR-TB if:</strong>
                                      <div style={{ marginTop: '5px', fontSize: '11px', paddingLeft: '10px' }}>
                                        <div>Presumptive TB with previous treatment (E)</div>
                                        <div>Presumptive TB with exposure to known active DR-TB case (F)</div>
                                      </div>
                                    </div>
                                  </div>
                                </td>
                              </tr>

                              {/* Body Diagrams and Conclusion Section */}
                              <tr>
                                {/* Left Column - Body Diagrams */}
                                <td colSpan="4" style={{ 
                                  border: '1px solid #000',
                                  padding: '10px',
                                  verticalAlign: 'top',
                                  width: '50%'
                                }}>
                                  {/* Body Diagrams with Markings */}
                                  <div style={{ display: 'flex', gap: '15px', marginBottom: '15px' }}>
                                    {/* Front Body Column */}
                                    <div style={{ flex: 1 }}>
                                      <div style={{ 
                                        textAlign: 'center',
                                        fontSize: '11px',
                                        fontWeight: 'bold',
                                        marginBottom: '5px'
                                      }}>Front</div>
                                      <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                                        <div style={{ 
                                          border: '1.5px solid #ccc',
                                          borderRadius: '8px',
                                          position: 'relative',
                                          backgroundColor: '#fff',
                                          display: 'inline-block',
                                          lineHeight: 0,
                                          flexShrink: 0,
                                          boxShadow: '0 2px 6px rgba(0,0,0,0.1)'
                                        }}>
                                          <img 
                                            src="/front_body.png" 
                                            alt="Front View" 
                                            style={{ 
                                              width: '120px',
                                              height: 'auto',
                                              display: 'block',
                                              borderRadius: '7px'
                                            }}
                                          />
                                          {/* Body Markings Overlay - Front */}
                                          {bodyMarkings.front.map((mark, index) => {
                                            const markTypeCount = bodyMarkings.front
                                              .slice(0, index)
                                              .filter(m => m.mark_type === mark.mark_type).length + 1;
                                            
                                            const getMarkColor = (type) => {
                                              const colors = {
                                                tattoo: '#FF0000',
                                                piercing: '#0000FF',
                                                scar: '#800080',
                                                burn: '#FFA500',
                                                bruise: '#4B0082',
                                                wound: '#8B0000'
                                              };
                                              return colors[type] || '#FF0000';
                                            };
                                            
                                            return (
                                              <div
                                                key={index}
                                                style={{
                                                  position: 'absolute',
                                                  left: `${mark.position_x}%`,
                                                  top: `${mark.position_y}%`,
                                                  width: '14px',
                                                  height: '14px',
                                                  borderRadius: '50%',
                                                  backgroundColor: getMarkColor(mark.mark_type),
                                                  border: '2px solid white',
                                                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.5)',
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                  justifyContent: 'center',
                                                  fontSize: '8px',
                                                  fontWeight: 'bold',
                                                  color: 'white',
                                                  transform: 'translate(-50%, -50%)',
                                                  zIndex: 2
                                                }}
                                                title={`${mark.mark_type.charAt(0).toUpperCase() + mark.mark_type.slice(1)} ${markTypeCount}: ${mark.description || 'N/A'}`}
                                              >
                                                {markTypeCount}
                                              </div>
                                            );
                                          })}
                                        </div>
                                        {/* Front Marks List */}
                                        <div style={{ flex: 1, fontSize: '9px', minWidth: 0 }}>
                                          {bodyMarkings.front.length === 0 ? (
                                            <div style={{ textAlign: 'center', color: '#666', fontStyle: 'italic' }}>
                                              No markings
                                            </div>
                                          ) : (
                                            bodyMarkings.front.map((mark, index) => {
                                              const markTypeCount = bodyMarkings.front
                                                .slice(0, index)
                                                .filter(m => m.mark_type === mark.mark_type).length + 1;
                                              
                                              const getMarkColor = (type) => {
                                                const colors = {
                                                  tattoo: '#FF0000',
                                                  piercing: '#0000FF',
                                                  scar: '#800080',
                                                  burn: '#FFA500',
                                                  bruise: '#4B0082',
                                                  wound: '#8B0000'
                                                };
                                                return colors[type] || '#FF0000';
                                              };
                                              
                                              return (
                                                <div 
                                                  key={index}
                                                  style={{
                                                    padding: '4px',
                                                    marginBottom: '3px',
                                                    backgroundColor: '#f5f5f5',
                                                    borderRadius: '3px',
                                                    border: '1px solid #ddd',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '5px'
                                                  }}
                                                >
                                                  <span style={{
                                                    display: 'inline-block',
                                                    width: '8px',
                                                    height: '8px',
                                                    borderRadius: '50%',
                                                    backgroundColor: getMarkColor(mark.mark_type),
                                                    flexShrink: 0
                                                  }}></span>
                                                  <div style={{ flex: 1, overflow: 'hidden' }}>
                                                    <div style={{ fontWeight: 'bold' }}>
                                                      {mark.mark_type.charAt(0).toUpperCase() + mark.mark_type.slice(1)} {markTypeCount}
                                                    </div>
                                                    {mark.description && (
                                                      <div style={{ color: '#555', fontSize: '8px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                                        {mark.description}
                                                      </div>
                                                    )}
                                                  </div>
                                                </div>
                                              );
                                            })
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                    
                                    {/* Back Body Column */}
                                    <div style={{ flex: 1 }}>
                                      <div style={{ 
                                        textAlign: 'center',
                                        fontSize: '11px',
                                        fontWeight: 'bold',
                                        marginBottom: '5px'
                                      }}>Back</div>
                                      <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                                        <div style={{ 
                                          border: '1.5px solid #ccc',
                                          borderRadius: '8px',
                                          position: 'relative',
                                          backgroundColor: '#fff',
                                          display: 'inline-block',
                                          lineHeight: 0,
                                          flexShrink: 0,
                                          boxShadow: '0 2px 6px rgba(0,0,0,0.1)'
                                        }}>
                                          <img 
                                            src="/back_body.png" 
                                            alt="Back View" 
                                            style={{ 
                                              width: '120px',
                                              height: 'auto',
                                              display: 'block',
                                              borderRadius: '7px'
                                            }}
                                          />
                                          {/* Body Markings Overlay - Back */}
                                          {bodyMarkings.back.map((mark, index) => {
                                            const markTypeCount = bodyMarkings.back
                                              .slice(0, index)
                                              .filter(m => m.mark_type === mark.mark_type).length + 1;
                                            
                                            const getMarkColor = (type) => {
                                              const colors = {
                                                tattoo: '#FF0000',
                                                piercing: '#0000FF',
                                                scar: '#800080',
                                                burn: '#FFA500',
                                                bruise: '#4B0082',
                                                wound: '#8B0000'
                                              };
                                              return colors[type] || '#FF0000';
                                            };
                                            
                                            return (
                                              <div
                                                key={index}
                                                style={{
                                                  position: 'absolute',
                                                  left: `${mark.position_x}%`,
                                                  top: `${mark.position_y}%`,
                                                  width: '14px',
                                                  height: '14px',
                                                  borderRadius: '50%',
                                                  backgroundColor: getMarkColor(mark.mark_type),
                                                  border: '2px solid white',
                                                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.5)',
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                  justifyContent: 'center',
                                                  fontSize: '8px',
                                                  fontWeight: 'bold',
                                                  color: 'white',
                                                  transform: 'translate(-50%, -50%)',
                                                  zIndex: 2
                                                }}
                                                title={`${mark.mark_type.charAt(0).toUpperCase() + mark.mark_type.slice(1)} ${markTypeCount}: ${mark.description || 'N/A'}`}
                                              >
                                                {markTypeCount}
                                              </div>
                                            );
                                          })}
                                        </div>
                                        {/* Back Marks List */}
                                        <div style={{ flex: 1, fontSize: '9px', minWidth: 0 }}>
                                          {bodyMarkings.back.length === 0 ? (
                                            <div style={{ textAlign: 'center', color: '#666', fontStyle: 'italic' }}>
                                              No markings
                                            </div>
                                          ) : (
                                            bodyMarkings.back.map((mark, index) => {
                                              const markTypeCount = bodyMarkings.back
                                                .slice(0, index)
                                                .filter(m => m.mark_type === mark.mark_type).length + 1;
                                              
                                              const getMarkColor = (type) => {
                                                const colors = {
                                                  tattoo: '#FF0000',
                                                  piercing: '#0000FF',
                                                  scar: '#800080',
                                                  burn: '#FFA500',
                                                  bruise: '#4B0082',
                                                  wound: '#8B0000'
                                                };
                                                return colors[type] || '#FF0000';
                                              };
                                              
                                              return (
                                                <div 
                                                  key={index}
                                                  style={{
                                                    padding: '4px',
                                                    marginBottom: '3px',
                                                    backgroundColor: '#f5f5f5',
                                                    borderRadius: '3px',
                                                    border: '1px solid #ddd',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '5px'
                                                  }}
                                                >
                                                  <span style={{
                                                    display: 'inline-block',
                                                    width: '8px',
                                                    height: '8px',
                                                    borderRadius: '50%',
                                                    backgroundColor: getMarkColor(mark.mark_type),
                                                    flexShrink: 0
                                                  }}></span>
                                                  <div style={{ flex: 1, overflow: 'hidden' }}>
                                                    <div style={{ fontWeight: 'bold' }}>
                                                      {mark.mark_type.charAt(0).toUpperCase() + mark.mark_type.slice(1)} {markTypeCount}
                                                    </div>
                                                    {mark.description && (
                                                      <div style={{ color: '#555', fontSize: '8px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                                        {mark.description}
                                                      </div>
                                                    )}
                                                  </div>
                                                </div>
                                              );
                                            })
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </div>

                                  {/* Checkboxes below diagrams */}
                                  <div style={{ marginBottom: '10px', paddingLeft: '20px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '5px' }}>
                                      <input type="checkbox" checked={selectedRecord?.hr_c_tb_conclusion?.no_physical_injury === 1 || selectedRecord?.hr_c_tb_conclusion?.no_physical_injury === '1' || selectedRecord?.hr_c_tb_conclusion?.no_physical_injury === 'Yes'} disabled readOnly style={{ width: '16px', height: '16px', marginRight: '8px' }} />
                                      <span>No signs of external physical injury</span>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '5px' }}>
                                      <input type="checkbox" checked={selectedRecord?.hr_c_tb_conclusion?.no_skin_markings === 1 || selectedRecord?.hr_c_tb_conclusion?.no_skin_markings === '1' || selectedRecord?.hr_c_tb_conclusion?.no_skin_markings === 'Yes'} disabled readOnly style={{ width: '16px', height: '16px', marginRight: '8px' }} />
                                      <span>No skin markings or unusual piercings</span>
                                    </div>
                                  </div>

                                  {/* Signature */}
                                  <div style={{ marginTop: '15px', paddingLeft: '20px' }}>
                                    Signature of PDL:
                                    <div style={{ borderBottom: '1px solid #000', display: 'inline-block', minWidth: '200px', marginLeft: '10px' }}></div>
                                  </div>
                                </td>

                                {/* Right Column - Conclusion and Recommendation */}
                                <td colSpan="4" style={{ 
                                  border: '1px solid #000',
                                  padding: '10px',
                                  verticalAlign: 'top',
                                  width: '50%'
                                }}>
                                  {/* Conclusion Section */}
                                  <div style={{ marginBottom: '15px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
                                      <strong style={{ marginRight: '15px' }}>Conclusion:</strong>
                                      <input type="checkbox" checked={selectedRecord?.hr_c_tb_conclusion?.practically_healthy === 1 || selectedRecord?.hr_c_tb_conclusion?.practically_healthy === '1' || selectedRecord?.hr_c_tb_conclusion?.practically_healthy === 'Yes'} disabled readOnly style={{ width: '16px', height: '16px', marginRight: '8px' }} />
                                      <span>Practically healthy</span>
                                    </div>

                                    <div style={{ marginBottom: '10px' }}>
                                      <em>For further examination/evaluation:</em>
                                    </div>

                                    <div style={{ paddingLeft: '20px' }}>
                                      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '5px' }}>
                                        <input type="checkbox" checked={selectedRecord?.hr_c_tb_conclusion?.require_sbirt === 1 || selectedRecord?.hr_c_tb_conclusion?.require_sbirt === '1' || selectedRecord?.hr_c_tb_conclusion?.require_sbirt === 'Yes'} disabled readOnly style={{ width: '16px', height: '16px', marginRight: '8px' }} />
                                        <span>SBIRT</span>
                                      </div>
                                      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '5px' }}>
                                        <input type="checkbox" checked={selectedRecord?.hr_c_tb_conclusion?.require_philpen === 1 || selectedRecord?.hr_c_tb_conclusion?.require_philpen === '1' || selectedRecord?.hr_c_tb_conclusion?.require_philpen === 'Yes'} disabled readOnly style={{ width: '16px', height: '16px', marginRight: '8px' }} />
                                        <span>PhilPEN</span>
                                      </div>
                                      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '5px' }}>
                                        <input type="checkbox" checked={selectedRecord?.hr_c_tb_conclusion?.require_neuro_eval === 1 || selectedRecord?.hr_c_tb_conclusion?.require_neuro_eval === '1' || selectedRecord?.hr_c_tb_conclusion?.require_neuro_eval === 'Yes'} disabled readOnly style={{ width: '16px', height: '16px', marginRight: '8px' }} />
                                        <span>Neuro-Psychiatric Evaluation</span>
                                      </div>
                                      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '5px' }}>
                                        <input type="checkbox" checked={selectedRecord?.hr_c_tb_conclusion?.require_ill_treatment_doc === 1 || selectedRecord?.hr_c_tb_conclusion?.require_ill_treatment_doc === '1' || selectedRecord?.hr_c_tb_conclusion?.require_ill_treatment_doc === 'Yes'} disabled readOnly style={{ width: '16px', height: '16px', marginRight: '8px' }} />
                                        <span>Further documentation of Allegation of Ill-Treatment</span>
                                      </div>
                                    </div>
                                  </div>

                                  {/* Other impressions */}
                                  <div style={{ marginBottom: '15px' }}>
                                    <div style={{ marginBottom: '5px' }}>Other impressions if not practically healthy:</div>
                                    <div style={{ borderBottom: '1px solid #000', marginBottom: '5px', minHeight: '20px', whiteSpace: 'pre-wrap' }}>{selectedRecord?.hr_c_tb_conclusion?.other_impressions || ''}</div>
                                  </div>

                                  {/* Recommendation Section */}
                                  <div>
                                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
                                      <strong style={{ marginRight: '15px' }}>Recommendation:</strong>
                                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '5px' }}>
                                          <input type="checkbox" checked={selectedRecord?.hr_c_tb_conclusion?.recommendation?.includes('dorm')} disabled readOnly style={{ width: '16px', height: '16px', marginRight: '8px' }} />
                                          <span>to be accommodated in general dormitory</span>
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '5px' }}>
                                          <input type="checkbox" checked={selectedRecord?.hr_c_tb_conclusion?.recommendation?.includes('isolated')} disabled readOnly style={{ width: '16px', height: '16px', marginRight: '8px' }} />
                                          <span>to be isolated</span>
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '5px' }}>
                                          <input type="checkbox" checked={selectedRecord?.hr_c_tb_conclusion?.recommendation?.includes('hospitalized')} disabled readOnly style={{ width: '16px', height: '16px', marginRight: '8px' }} />
                                          <span>to be hospitalized</span>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>

                      {/* Page 5 Navigation - Back Button */}
                      <div className={modalStyles.jbrPageNavFooter}>
                        <div className={modalStyles.jbrPageNavFooterLeft}>
                          <button 
                            className={modalStyles.jbrBackBtn}
                            onClick={() => setCurrentPage(4)}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            Back
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Change Password Modal */}
      <ChangePasswordModal
        isOpen={showChangePasswordModal}
        onClose={() => setShowChangePasswordModal(false)}
        userData={userData}
        onSuccess={(message) => {
          setSuccessMessage(message || 'Password changed successfully');
          setShowSuccessModal(true);
        }}
        onError={(message) => {
          setErrorMessage(message);
          setShowErrorModal(true);
        }}
      />

      {/* Success Modal */}
      {showSuccessModal && (
        <div className={pdlSearchStyles['modal-overlay']} onClick={() => setShowSuccessModal(false)}>
          <div className={pdlSearchStyles.successModal} onClick={(e) => e.stopPropagation()}>
            <div className={pdlSearchStyles.successIcon}>
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M22 11.08V12C21.9988 14.1564 21.3005 16.2547 20.0093 17.9818C18.7182 19.7088 16.9033 20.9725 14.8354 21.5839C12.7674 22.1953 10.5573 22.1219 8.53447 21.3746C6.51168 20.6273 4.78465 19.2461 3.61096 17.4371C2.43727 15.628 1.87979 13.4881 2.02168 11.3363C2.16356 9.18455 2.99721 7.13631 4.39828 5.49706C5.79935 3.85781 7.69279 2.71537 9.79619 2.24013C11.8996 1.7649 14.1003 1.98232 16.07 2.85999" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M22 4L12 14.01L9 11.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h3>Success</h3>
            <p>{successMessage}</p>
            <button className={pdlSearchStyles.successBtn} onClick={() => setShowSuccessModal(false)}>
              OK
            </button>
          </div>
        </div>
      )}

      {/* Error Modal */}
      {showErrorModal && (
        <div className={pdlSearchStyles['modal-overlay']} onClick={() => setShowErrorModal(false)}>
          <div className={pdlSearchStyles.errorModal} onClick={(e) => e.stopPropagation()}>
            <div className={pdlSearchStyles.errorIcon}>
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <line x1="12" y1="8" x2="12" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <line x1="12" y1="16" x2="12.01" y2="16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h3>Error</h3>
            <p>{errorMessage}</p>
            <button className={pdlSearchStyles.errorBtn} onClick={() => setShowErrorModal(false)}>
              Close
            </button>
          </div>
        </div>
      )}

      {/* Theme Change Confirmation Modal */}
      {showThemeModal && (
        <div className={pdlSearchStyles['modal-overlay']} onClick={cancelThemeChange}>
          <div className={pdlSearchStyles.errorModal} onClick={(e) => e.stopPropagation()} style={{ borderColor: '#f59e0b' }}>
            <div className={pdlSearchStyles.errorIcon} style={{ background: 'rgba(245, 158, 11, 0.1)', borderColor: 'rgba(245, 158, 11, 0.3)', color: '#f59e0b' }}>
              {pendingTheme ? (
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="currentColor"/>
                </svg>
              ) : (
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 3V4M12 20V21M4 12H3M6.31412 6.31412L5.5 5.5M17.6859 6.31412L18.5 5.5M6.31412 17.69L5.5 18.5M17.6859 17.69L18.5 18.5M21 12H20M16 12C16 14.2091 14.2091 16 12 16C9.79086 16 8 14.2091 8 12C8 9.79086 9.79086 8 12 8C14.2091 8 16 9.79086 16 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
            </div>
            <h3 style={{ color: '#f59e0b' }}>Change Theme</h3>
            <p>Are you sure you want to switch to <strong>{pendingTheme ? 'Dark' : 'Light'} Mode</strong>?</p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
              <button className={pdlSearchStyles.cancelBtn} onClick={cancelThemeChange}>Cancel</button>
              <button className={pdlSearchStyles.errorBtn} onClick={confirmThemeChange} style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' }}>Confirm</button>
            </div>
          </div>
        </div>
      )}

      {/* Logout Confirmation Modal */}
      {showLogoutModal && ReactDOM.createPortal(
        <div className={pdlSearchStyles['modal-overlay']} onClick={cancelLogout}>
          <div className={pdlSearchStyles.errorModal} onClick={(e) => e.stopPropagation()} style={{ borderColor: '#ef4444' }}>
            <div className={pdlSearchStyles.errorIcon} style={{ background: 'rgba(239, 68, 68, 0.1)', borderColor: 'rgba(239, 68, 68, 0.3)', color: '#ef4444' }}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H9" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <polyline points="16,17 21,12 16,7" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <line x1="21" y1="12" x2="9" y2="12" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h3 style={{ color: '#ef4444' }}>Logout</h3>
            <p>Are you sure you want to logout?</p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
              <button className={pdlSearchStyles.cancelBtn} onClick={cancelLogout}>Cancel</button>
              <button className={pdlSearchStyles.errorBtn} onClick={confirmLogout} style={{ background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' }}>Logout</button>
            </div>
          </div>
        </div>,
        document.body
      )}

      </div>
    </div>
  );
}

export default PdlHealthRecordSearch;
